
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { User } from '../types';
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleLogin } from '@react-oauth/google';
import { trackAnonymousEvent } from '../App';
import { 
  Play, 
  Zap, 
  Globe, 
  Mic, 
  Users, 
  Shield, 
  Mail, 
  HelpCircle,
  Lock, 
  User as UserIcon, 
  Send, 
  Info, 
  MapPin, 
  // Twitter yerine X kullanacağız, X zaten lucide-react içerisinde mevcut
  X, 
  Instagram, 
  Youtube, 
  Linkedin,
  Cpu,
  Radio,
  Layers,
  Layout,
  Plus,
  FileText,
  LogOut,
  Video,
  Activity,
  Check,
  Headphones,
  Server,
  Download,
  Palette,
  Briefcase,
  Star,
  Search,
  Sparkles,
  Crown,
  Rocket,
  Upload,
  Monitor,
  Music,
  Gamepad2,
  CreditCard,
  Smile,
  ChevronRight,
  // Added ChevronLeft to fix the error on line 457
  ChevronLeft,
  ChevronDown,
  Loader2,
  Database,
  Network,
  AlertCircle,
  Waves,
  ScanEye,
  ArrowDown,
  Terminal,
  Unplug,
  Flame,
  Globe2,
  Speaker,
  Atom,
  Brain,
  BrainCircuit,
  ShoppingBag,
  ShieldCheck,
  CheckCircle,
  Target,
  ShieldAlert,
  Orbit,
  Binary,
  Fingerprint,
  Layers as LayersIcon,
  ArrowRight,
  Scale,
  Gavel,
  FileWarning,
  Eye,
  EyeOff,
  AlertTriangle,
  UserCheck,
  FileSearch,
  History,
  Cookie,
  BarChart3,
  MousePointer2,
  Settings2,
  Heart,
  Ban,
  Scale as ScaleIcon,
  UserMinus,
  MessageSquareOff,
  Workflow,
  Headset,
  MessagesSquare,
  Building2,
  PhoneCall,
  Wind,
  Lightbulb,
  Trophy,
  Compass,
  Key,
  RefreshCw,
  SearchX,
  Ghost,
  Menu,
  MonitorUp
} from 'lucide-react';

// 🚀 SİBER KALKAN 1: DONMAZ RECAPTCHA (Google'ı Yormaz, Klavyeyi Kastırmaz)
const SiberCaptcha = React.memo(({ onVerify }: { onVerify: (token: string | null) => void }) => {
  return (
    <ReCAPTCHA
      sitekey="6LfvN0gsAAAAAJP8YUs4MEsSzCc06dKB66HqugK2"
      onChange={onVerify}
      theme="dark"
    />
  );
});

// 🚀 SİBER KALKAN 2: DONMAZ ARKA PLAN (3D Işıkların Yeniden Çizilmesini Engeller)
const SiberBackground = React.memo(() => (
  <div className="fixed inset-0 z-0 bg-[#030305]">
    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px]"></div>
    <div className="absolute -top-[10%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-sesgelio/20 blur-[120px] mix-blend-screen animate-pulse pointer-events-none"></div>
    <div className="absolute -bottom-[20%] -right-[10%] w-[60vw] h-[60vw] rounded-full bg-purple-600/15 blur-[150px] mix-blend-screen animate-pulse pointer-events-none" style={{ animationDuration: '7s' }}></div>
    <div className="absolute top-[30%] left-[40%] w-[30vw] h-[30vw] rounded-full bg-emerald-500/10 blur-[100px] mix-blend-screen pointer-events-none"></div>
    <div className="absolute inset-0 bg-black/40 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#030305]/80 to-[#030305]"></div>
  </div>
));

interface DashboardProps {
  user?: User;
  onConnect: (credentials?: any) => void;
}

type PageView = 
  | 'home' 
  | 'about' 
  | 'contact' 
  | 'login' 
  | 'register'
  | 'forgot-password'
  | 'reset-password'
  | 'terms' 
  | 'privacy' 
  | 'cookies' 
  | 'guidelines' 
  | 'distance-selling'
  | 'pre-information'
  | 'refund-policy'
  | 'safe-shopping'
  | 'nitro' 
  | 'status' 
  | 'directory' 
  | 'faq'
  | 'career' 
  | 'brand' 
  | 'system';

const BACKEND_URL = 
  (import.meta as any).env?.VITE_BACKEND_URL || 
  (process as any).env?.REACT_APP_BACKEND_URL || 
  'https://sesgelio.com/api';
  
  // Dashboard fonksiyonunun hemen ÜSTÜNE yapıştır
const useCooldown = (cooldownMs: number) => {
  const [isCoolingDown, setIsCoolingDown] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const startCooldown = useCallback(() => {
    setIsCoolingDown(true);
    setRemainingSeconds(Math.ceil(cooldownMs / 1000));
    
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsCoolingDown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [cooldownMs]);

  return { isCoolingDown, remainingSeconds, startCooldown };
};

const sanitizeUserProfile = (rawUser: any) => {
  return {
    id: typeof rawUser.id === 'string' ? rawUser.id.slice(0, 100) : '',
    username: typeof rawUser.username === 'string' 
      ? rawUser.username.replace(/[<>"'&]/g, '').slice(0, 50) 
      : '',
    email: typeof rawUser.email === 'string' ? rawUser.email.slice(0, 255) : '',
    avatarUrl: typeof rawUser.avatarUrl === 'string' 
        ? rawUser.avatarUrl.slice(0, 500) 
        : (typeof rawUser.avatar === 'string' ? rawUser.avatar.slice(0, 500) : ''),
    birthday: rawUser.birthday || null,
    parentalConsent: Boolean(rawUser.parentalConsent),
    // 🛡️ Plan ve premium bilgileri sadece backend'den gelen değerleri kabul eder
    plan: ['standard', 'premium', 'elite'].includes(rawUser.plan) ? rawUser.plan : 'standard',
    isPremium: Boolean(rawUser.isPremium),
    isElite: Boolean(rawUser.isElite),
    isLegendary: Boolean(rawUser.isLegendary),
    isShowcase: Boolean(rawUser.isShowcase),
    discriminator: typeof rawUser.discriminator === 'string' ? rawUser.discriminator.slice(0, 4) : '0000',
    theme: ['dark', 'light'].includes(rawUser.theme) ? rawUser.theme : 'dark',
    selectedFrame: rawUser.selectedFrame || 'standard',
    // 🛡️ Hiçbir zaman frontend'den değiştirilemesin
    role: ['user', 'premium', 'elite', 'admin', 'FOUNDER'].includes(rawUser.role) 
      ? rawUser.role 
      : 'user',
  };
};
const Dashboard: React.FC<DashboardProps> = ({ user, onConnect }) => {
  const [currentPage, setCurrentPage] = useState<PageView>('home');
  const [resetStep, setResetStep] = useState<'form' | 'success'>('form');
  const scrollContainerRef = useRef<HTMLElement>(null);

useEffect(() => {
    // 🚀 SİBER MÜHÜR: Mutlak Yukarı Kaydırma Protokolü
    const forceScrollToTop = () => {
      if (scrollContainerRef.current) {
        // Sadece bizim asıl kayan <main> etiketimizi hedefler
        scrollContainerRef.current.scrollTop = 0;
        scrollContainerRef.current.scrollTo(0, 0);
      }
    };

    // 1. Şaltere basıldığı an (eski sayfa silinmeden hemen önce) yukarı al
    forceScrollToTop();

    // 2. ÇİFT DİKİŞ: React yeni sayfayı DOM'a tamamen çizdikten hemen sonra (50ms) bir daha çak! 
    // Bu, sayfanın ortadan başlama hatasını KESİN olarak çözer.
    const timer = setTimeout(forceScrollToTop, 50);

    return () => clearTimeout(timer);
  }, [currentPage]);

  // 🚀 SİBER MÜHÜR: Mobil Menü Çekmecesi İçin State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);

  // 🚀 SİBER MÜHÜR: macOS Bilgilendirme Modalı İçin State
  const [showMacWarning, setShowMacWarning] = useState(false);

  // 🚀 GOOGLE YAKALAYICI STATE'LERİ
  const [googlePendingData, setGooglePendingData] = useState<any>(null);
  const [googleBirthday, setGoogleBirthday] = useState('');
  const [googleParentalConsent, setGoogleParentalConsent] = useState(false);

  // 🚀 ÇEREZ ONAY MOTORU
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    // Kullanıcı daha önce onay vermiş mi bakıyoruz. Vermemişse banner'ı aç.
    const cookieConsent = localStorage.getItem('sesgelio_cookie_consent');
    if (!cookieConsent) {
      setShowCookieBanner(true);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const area = document.getElementById('download-area');
      if (area && !area.contains(e.target as Node)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('sesgelio_cookie_consent', 'accepted');
    setShowCookieBanner(false);
  };

// 🚀 GOOGLE NÖRAL KÖPRÜ MOTORU (ÇELİK ZIRHLI)
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: tokenResponse.access_token })
        });
        
        const data = await res.json();

        // 🚀 YENİ: Backend bize "Bu adamda 2FA var" derse, topu ana merkeze (App.tsx) at!
        if (data.mfaRequired) {
            onConnect({ mfaRequired: true, userId: data.userId });
            return; // Aşağıdaki işlemleri durdur
        }
        
        // 🚀 DÜZELTME: Artık data.token yok, data.user varsa giriş başarılıdır!
       if (data.user) { 
          // 🚀 ANALİTİK MÜHRÜ: Google Nöral Köprüsü kullanıldı
          trackAnonymousEvent(data.user, 'auth', 'google_login_success', { isNewUser: !data.user.birthday });

          // 🚀 EĞER GOOGLE'DAN GELEN ADAMIN DOĞUM TARİHİ YOKSA (İLK KAYIT):
          if (!data.user.birthday) {
             // Token'ı artık state'e atmıyoruz, sadece user verisini tutuyoruz
             setGooglePendingData({ user: data.user }); 
             return; // onConnect() yapmıyoruz, adamı yaş ekranında bekletiyoruz!
          }

          // 🚀 ESKİ ÜYEYSE VE TARİHİ VARSA DİREKT İÇERİ AL:
          // Token artık Cookie'de! Biz sadece profili yerel hafızaya (localStorage) basıyoruz.
         localStorage.setItem('saved_user_profile', JSON.stringify(sanitizeUserProfile(data.user)));
onConnect();
        } else {
          alert(data.error || "Google kimliği doğrulanamadı.");
        }
      } catch (err) {
        alert("Sunucuyla iletişim kurulamadı nöral dostum.");
      } finally {
        setLoading(false);
      }
    },
    onError: error => console.log('Google Giriş Hatası:', error)
  });
  const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
const [username, setUsername] = useState(''); // Kayıt için

const [loading, setLoading] = useState<boolean>(false);

const handleGoogleFinalStep = async () => {
  if (isSubmitting) return;
  if (!googleBirthday) return alert("nöral dostum doğum tarihini girmeden geçemezsin!");

  const dob = new Date(googleBirthday);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (today.getMonth() < dob.getMonth() || 
     (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
    age--;
  }

  if (age < 18 && !googleParentalConsent) {
    return alert("nöral dostum 18 yaşından küçüksen Veli Onayı kutucuğunu mühürlemelisin!");
  }

  setIsSubmitting(true);
  try {
    const formattedDateTime = new Date(googleBirthday).toISOString();
    const response = await fetch(`${BACKEND_URL}/api/users/me/birthday`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ 
        birthday: formattedDateTime,
        parentalConsent: age < 18 
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return alert(errorData.error || "Yaş veritabanına yazılamadı nöral dostum!");
    }

 const updatedUser = sanitizeUserProfile({
      ...googlePendingData.user,
      birthday: formattedDateTime,
      parentalConsent: age < 18,
      plan: googlePendingData.user.plan || 'standard',
      isPremium: googlePendingData.user.isPremium || false,
      isElite: googlePendingData.user.isElite || false
    });
    localStorage.setItem('saved_user_profile', JSON.stringify(updatedUser));
    setGooglePendingData(null);
    onConnect();
  } catch (err) {
    alert("Bağlantı koptu nöral dostum, backend'i kontrol et!");
  } finally {
    setIsSubmitting(false);
  }
};

const [birthday, setBirthday] = useState('');
const [parentalConsent, setParentalConsent] = useState(false);
const [agreedTerms, setAgreedTerms] = useState(false);
const [agreedPrivacy, setAgreedPrivacy] = useState(false);
const [agreedGuidelines, setAgreedGuidelines] = useState(false);

const [fullName, setFullName] = useState('');
const [contactEmail, setContactEmail] = useState('');
const [contactName, setContactName] = useState('');
const [contactSubject, setContactSubject] = useState('Genel Sorular');
const [contactMessage, setContactMessage] = useState('');

const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};


const [registerCaptchaToken, setRegisterCaptchaToken] = useState<string | null>(null);
const [contactCaptchaToken, setContactCaptchaToken] = useState<string | null>(null);
const [isSubmitting, setIsSubmitting] = useState(false);

// 🚀 SİBER MÜHÜR: Neon Bildirim Ekranı (Toast) Hafızası
const [siberToast, setSiberToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' | 'warning' } | null>(null);

// 🚀 SİBER MÜHÜR: Çirkin alert() yerine bu fonksiyonu çağıracağız!
const showSiberToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setSiberToast({ show: true, message, type });
    // 4 saniye sonra havaya karışarak kaybolur
    setTimeout(() => {
        setSiberToast(null);
    }, 4000); 
};

const { 
  isCoolingDown: resetCoolingDown, 
  remainingSeconds: resetRemaining,
  startCooldown: startResetCooldown 
} = useCooldown(60000);

const {
  isCoolingDown: contactCoolingDown,
  remainingSeconds: contactRemaining,
  startCooldown: startContactCooldown
} = useCooldown(30000);

const [isVerifyStep, setIsVerifyStep] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');



// --- 🛡️ ACIMASIZ E-POSTA DOĞRULAMA MOTORU ---
  const validateStrictEmail = (email: string) => {
    // 1. Temel formata uyuyor mu? (isim@alanadi.com)
    const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicRegex.test(email)) return { isValid: false, message: "Lütfen geçerli bir e-posta adresi giriniz!" };

    const domain = email.split('@')[1].toLowerCase();

    // 2. Klasik Türk Kullanıcısı Yazım Hataları Kara Listesi
    const typoDomains = [
      'gmai.com', 'gmil.com', 'gmail.com.tr', 'gmal.com', 'gamil.com', 'gmaıl.com', 'gmaiil.com',
      'hotmai.com', 'hotmil.com', 'hotmal.com', 'hormail.com', 'hotmail.con', 'hotmaill.com',
      'yaho.com', 'yaho.com.tr', 'yahoo.cm',
      'outlok.com', 'outloo.com', 'outlook.con'
    ];

    if (typoDomains.includes(domain)) {
      return { isValid: false, message: "E-posta uzantısında yazım hatası var nöral dostum. (Örn: @gmai.com yerine @gmail.com olmalı)" };
    }

    // 3. Kullan-At (Geçici) Mail Kara Listesi (Spam Koruması)
    const disposableDomains = [
      'tempmail.com', '10minutemail.com', 'yopmail.com', 'guerrillamail.com', 'mailinator.com'
    ];

    if (disposableDomains.includes(domain)) {
      return { isValid: false, message: "Geçici (Kullan-at) e-posta adresleriyle sisteme kayıt olunamaz!" };
    }

    return { isValid: true, message: "Başarılı" };
  };

  // --- 🛡️ MÜHÜR KIRMA (E-POSTA ONAY) FONKSİYONU ---
  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifyCode.length !== 6) {
      alert("Mühür kodu 6 haneli olmalıdır nöral dostum!");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verifyCode })
      });
      const data = await response.json();
      
if (response.ok) {
        // 🚀 ANALİTİK MÜHRÜ: Yeni bir operatör e-posta mührünü kırdı ve sisteme katıldı!
        trackAnonymousEvent(null, 'auth', 'register_verified_success', { method: 'email' });

        showSiberToast("Mühür kırıldı! Sesgelio evrenine hoş geldin.", 'success');
        setIsVerifyStep(false); 
        setVerifyCode(''); 
        setCurrentPage('login'); 
      } else {
        showSiberToast(data.error || "Doğrulama başarısız! Kodu yanlış girdin nöral dostum.", 'error');
      }
    } catch (err) {
      alert("Sunucuya ulaşılamıyor, merkez çekirdek kapalı olabilir.");
    } finally {
      setLoading(false);
    }
  };

  // --- 🚀 KODU TEKRAR GÖNDERME MÜHRÜ ---
  const { 
    isCoolingDown: resendCoolingDown, 
    remainingSeconds: resendRemaining, 
    startCooldown: startResendCooldown 
  } = useCooldown(60000); // 60 Saniye bekleme süresi

  const handleResendCode = async () => {
    if (resendCoolingDown) return;
    
    setLoading(true);
    try {
      // 🚀 ZIRH: Artık koca kayıt motoruna değil, sadece kendi özel ve hafif rotamıza (resend-verification) gidiyoruz.
      // Sadece 'email' göndermemiz yetiyor, kullanıcı adı falan istemiyor!
      const response = await fetch(`${BACKEND_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (response.ok) {
        alert("Yeni onay kodu e-postana fırlatıldı nöral dostum! (Spam klasörünü kontrol etmeyi unutma)");
        startResendCooldown(); // 60 saniyelik sayacı başlat
      } else {
        alert(data.error || "Kod gönderilemedi!");
      }
    } catch (err) {
      alert("Sunucuya ulaşılamıyor nöral dostum.");
    } finally {
      setLoading(false);
    }
  };


  const handleLoginSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  const emailCheck = validateStrictEmail(email);
  if (!emailCheck.isValid) {
    alert(emailCheck.message);
    return;
  }

  if (currentPage === 'register') {
    const trimmedName = username.trim();
    if (trimmedName.length < 3 || trimmedName.length > 15) {
      alert("Operatör adı en az 3, en fazla 15 karakter olmalıdır!");
      return;
    }
    if (!/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(password)) {
      alert("Şifreniz en az 8 karakter, 1 harf ve 1 rakam içermelidir!");
      return;
    }
    if (!birthday) {
      alert("Lütfen doğum tarihinizi giriniz.");
      return;
    }
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 18 && !parentalConsent) {
      alert("18 yaşından küçükseniz ebeveyn onayı gereklidir!");
      return;
    }
    if (!agreedTerms || !agreedPrivacy || !agreedGuidelines) {
      alert("Tüm sözleşmeleri onaylamanız şarttır!");
      return;
    }
    if (!registerCaptchaToken) {
      alert("Lütfen robot olmadığınızı doğrulayın!");
      return;
    }
  }

  setLoading(true);
  try {
    if (currentPage === 'login') {
      await onConnect({ email, password });
    } else {
      const response = await fetch(`${BACKEND_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: username.trim(), 
          email, 
          password, 
          captchaToken: registerCaptchaToken,
          birthday, 
          parentalConsent, 
          agreedTerms, 
          agreedPrivacy, 
          agreedGuidelines 
        }),
      });
const data = await response.json();
      if (response.ok && data.requiresVerification) {
        showSiberToast(data.message || "Doğrulama kodu e-postana ışınlandı!", 'warning');
        setIsVerifyStep(true);
      } else if (!response.ok) {
        showSiberToast(data.error || "Kayıt başarısız!", 'error');
      }
    }
 } catch (err: any) {
    // 🚀 ANALİTİK MÜHRÜ: Giriş veya kayıt sırasında bir duvara çarpıldı
    trackAnonymousEvent(null, 'auth', currentPage === 'login' ? 'login_failed' : 'register_failed', { 
      errorReason: err.message || "Bilinmeyen Hata" 
    });

    const errMsg = err.message ? err.message.toLowerCase() : "";
    if (errMsg.includes("mühür") || errMsg.includes("onay") || err.requiresVerification) {
      alert("E-posta mührünü kırmamışsın! Kayıt sırasında gönderilen kodu gir.");
      setCurrentPage('register');
      setIsVerifyStep(true);
    } else {
      alert(err.message || "Sunucuya ulaşılamıyor.");
    }
  } finally {
    setLoading(false);
  }
};
const [newPassword, setNewPassword] = useState('');
const [resetEmail, setResetEmail] = useState('');

// Şifre sıfırlama ve Yeni Sekme (Yasal Sayfalar) linklerini kontrol eden mekanizma
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    const token = params.get('token');
    
   if (page === 'reset-password' && token) {
      // App.tsx modal'ı devreye giriyor, burada bir şey yapma
    }else if (page) {
      // 🚀 DİNAMİK YÖNLENDİRİCİ: Yeni sekmede açılan URL'yi yakala ve o sayfayı render et
const validPages = [
  'home', 'about', 'contact', 'login', 'register', 'terms', 'privacy', 
  'cookies', 'guidelines', 'distance-selling', 'pre-information', 
  'refund-policy', 'safe-shopping', 'nitro', 'status', 'directory', 
  'faq', 'career', 'brand', 'system'
];
      if (validPages.includes(page)) {
        setCurrentPage(page as PageView);
      }
    }
  }, []);

  // Şifreyi güncelleyen ana fonksiyon
  const handleFinalReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('reset_token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      if (response.ok) {
        alert('Şifreniz başarıyla güncellendi! Giriş yapabilirsiniz.');
        setCurrentPage('login');
        localStorage.removeItem('reset_token');
      } else {
        const data = await response.json();
        alert(data.error || 'Hata oluştu.');
      }
    } catch (err) {
      alert('Sunucu hatası.');
    }
  };

 const handleContactSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (contactCoolingDown) {
    alert(`Lütfen ${contactRemaining} saniye bekleyin!`);
    return;
  }
  if (!contactCaptchaToken) {
    alert("Lütfen robot olmadığınızı doğrulayın operatör!");
    return;
  }
  try {
    const response = await fetch(`${BACKEND_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: contactName,
        email: contactEmail,
        subject: contactSubject,
        message: contactMessage
      }),
    });
    if (response.ok) {
      alert('Mesajın başarıyla iletildi!');
      setContactName('');
      setContactEmail('');
      setContactMessage('');
      setContactCaptchaToken(null);
      startContactCooldown();
    } else {
      alert('Bir sorun oluştu, lütfen tekrar deneyin.');
    }
  } catch (err) {
    alert('Sunucuya bağlanılamadı.');
  }
};

const handleResetSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (resetCoolingDown) {
    alert(`Lütfen ${resetRemaining} saniye bekleyin!`);
    return;
  }
  try {
    const response = await fetch(`${BACKEND_URL}/api/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: resetEmail }),
    });
   if (response.ok) {
      // 🚀 ANALİTİK MÜHRÜ: Şifre sıfırlama protokolü başlatıldı
      trackAnonymousEvent(null, 'auth', 'password_reset_requested');
      
      setResetStep('success');
      startResetCooldown();
    } else {
      alert('E-posta gönderilemedi.');
    }
  } catch (err) {
    alert('Sunucu hatası.');
  }
};

const Footer = React.memo(() => (
    <footer className="w-full bg-white dark:bg-black border-t border-gray-200 dark:border-gray-900 pt-12 md:pt-16 pb-20 md:pb-24 relative z-20 transition-colors mt-auto flex-shrink-0">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
      {/* 🚀 MOBİLDE ORTALANMIŞ, MASAÜSTÜNDE SOLA YASLI LİNK IZGARASI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12 mb-10 md:mb-12 text-center md:text-left">
          <div className="col-span-1 md:col-span-2 flex flex-col items-center md:items-start">
{/* FOOTER SOL ÜST - YENİ LOGO KISMI (Referanslı) */}
             <div 
               className="flex items-center justify-center md:justify-start mb-8 cursor-pointer transition-transform duration-300 hover:scale-[1.02] active:scale-95" 
               onClick={() => { setCurrentPage('home'); scrollContainerRef.current?.scrollTo(0,0); }}
             >
                <img 
                   src="/ana1_sayfa_logo.png" 
                   alt="Sesgelio Logo" 
                   // 🚀 SİBER MÜHÜR: İstediğin referans değerler uygulandı.
                   // origin-center md:origin-left ile mobilde ortalanması güvenceye alındı!
                   className="h-12 md:h-16 lg:h-20 w-auto object-contain scale-[1.8] md:scale-[2] lg:scale-[2.2] origin-center md:origin-left drop-shadow-[0_0_15px_rgba(255,43,94,0.4)]" 
                />
             </div>
             <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-sm mb-6 mx-auto md:mx-0">
                Türkiye'nin yapay zeka destekli yeni nesil iletişim platformu. 
                Oyun, müzik ve sohbet için en düşük gecikme süresiyle arkadaşlarınla buluş.
             </p>
<div className="flex gap-4 justify-center md:justify-start">
                <a href="https://x.com/sesgelio_tr" target="_blank" rel="noreferrer" className="group w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#111214] border border-gray-200 dark:border-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-red-600/10 hover:border-red-600/40 hover:text-red-600 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(220,38,38,0.2)] transition-all duration-300" title="X (Twitter)">
                  <X size={18} className="group-hover:scale-110 transition-transform duration-300"/>
                </a>
                <a href="https://www.instagram.com/sesgelio_tr" target="_blank" rel="noreferrer" className="group w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#111214] border border-gray-200 dark:border-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-red-600/10 hover:border-red-600/40 hover:text-red-600 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(220,38,38,0.2)] transition-all duration-300">
                  <Instagram size={18} className="group-hover:scale-110 transition-transform duration-300"/>
                </a>
                <a href="https://www.youtube.com/@Sesgelio" target="_blank" rel="noreferrer" className="group w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#111214] border border-gray-200 dark:border-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-red-600/10 hover:border-red-600/40 hover:text-red-600 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(220,38,38,0.2)] transition-all duration-300">
                  <Youtube size={18} className="group-hover:scale-110 transition-transform duration-300"/>
                </a>
                <a href="https://www.linkedin.com/company/sesgelio/about/?viewAsMember=true" target="_blank" rel="noreferrer" className="group w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#111214] border border-gray-200 dark:border-white/5 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-red-600/10 hover:border-red-600/40 hover:text-red-600 hover:-translate-y-1 hover:shadow-[0_0_15px_rgba(220,38,38,0.2)] transition-all duration-300">
                  <Linkedin size={18} className="group-hover:scale-110 transition-transform duration-300"/>
                </a>
             </div>
          </div>
<div className="flex flex-col items-center md:items-start">
  <h4 className="font-bold text-gray-900 dark:text-white mb-4 md:mb-6">Ürün</h4>
  <ul className="space-y-3 text-sm text-gray-500 dark:text-gray-400">
    <li><button onClick={() => { setCurrentPage('nitro'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Premium</button></li>
    <li><button onClick={() => { setCurrentPage('status'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Sistem Durumu</button></li>              
    <li><button onClick={() => { setCurrentPage('directory'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Uygulama Dizini</button></li>
    <li><button onClick={() => { setCurrentPage('faq'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Sıkça Sorulan Sorular</button></li>
  </ul>
          </div>
          <div className="flex flex-col items-center md:items-start">
            <h4 className="font-bold text-gray-900 dark:text-white mb-4 md:mb-6">Kurumsal</h4>
            <ul className="space-y-3 text-sm text-gray-500 dark:text-gray-400">
              <li><button onClick={() => { setCurrentPage('about'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Hakkımızda</button></li>
              <li><button onClick={() => { setCurrentPage('career'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Kariyer</button></li>
              <li><button onClick={() => { setCurrentPage('brand'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">Marka</button></li>
              <li><button onClick={() => { setCurrentPage('contact'); scrollContainerRef.current?.scrollTo(0,0); }} className="hover:text-sesgelio transition-colors text-center md:text-left">İletişim</button></li>
            </ul>
          </div>
        </div>

        {/* --- 🚀 YENİDEN TASARLANAN VE DÜZENLENEN ALT BÖLÜM --- */}
        <div className="border-t border-gray-200 dark:border-gray-900 pt-8 flex flex-col gap-6">
          
{/* SÖZLEŞMELER - PROFESYONEL LİSTELEME (Premium Kutu Tasarımı) */}
<div className="grid grid-cols-2 md:flex md:flex-wrap justify-items-stretch md:justify-center items-center gap-3 md:gap-4 text-[10px] md:text-[11px] font-bold text-gray-500 dark:text-gray-400 w-full">
  <button onClick={() => { setCurrentPage('terms'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <FileText size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Üyelik Sözleşmesi
  </button>
  <button onClick={() => { setCurrentPage('privacy'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <Shield size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Gizlilik ve KVKK
  </button>
  <button onClick={() => { setCurrentPage('distance-selling'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <FileWarning size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Mesafeli Satış
  </button>
  <button onClick={() => { setCurrentPage('pre-information'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <Info size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Ön Bilgilendirme
  </button>
  <button onClick={() => { setCurrentPage('refund-policy'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <History size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> İptal ve İade
  </button>
  <button onClick={() => { setCurrentPage('safe-shopping'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <ShieldCheck size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Güvenli Alışveriş
  </button>
  <button onClick={() => { setCurrentPage('cookies'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <Cookie size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Çerez Politikası
  </button>
  <button onClick={() => { setCurrentPage('guidelines'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-white/5 hover:bg-red-600/10 hover:border-red-600/30 hover:text-red-600 hover:shadow-[0_0_15px_rgba(220,38,38,0.15)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group">
    <AlertTriangle size={14} className="shrink-0 group-hover:scale-110 transition-transform duration-300"/> Topluluk Kuralları
  </button>
</div>

<div className="flex flex-col-reverse md:flex-row items-center justify-between gap-8 pt-8 border-t border-gray-100 dark:border-gray-900/50">
            
{/* TELİF, ETBİS VE BTK YER SAĞLAYICI MÜHRÜ */}
            <div className="flex flex-col-reverse md:flex-row items-center gap-5 md:gap-8 w-full md:w-auto">
                
                <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                  &copy; {new Date().getFullYear()} Sesgelio. Tüm hakları saklıdır.
                </span>

                {/* Araya zarif bir ayraç ekledik */}
                <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden md:block"></div>

                {/* 🚀 ETBİS ONAY MÜHRÜ (YENİ EKLENEN) */}
                <button 
                  onClick={(e) => {
                      e.preventDefault();
                      // Tıklandığında doğrudan Sesgelio ETBİS sorgu ekranı açılır
                      window.open('https://etbis.ticaret.gov.tr/tr/SiteSorgulamaSonuc?siteId=e0478c7b-b432-433e-9138-3316c908e307', '_blank');
                  }}
                  className="group flex items-center justify-center md:justify-start gap-3 opacity-90 hover:opacity-100 transition-all duration-300 focus:outline-none w-full md:w-auto bg-gray-100/80 dark:bg-white/5 md:bg-transparent md:dark:bg-transparent py-3 md:py-0 rounded-2xl md:rounded-none"
                  title="ETBİS kaydımızı doğrulamak için tıklayın"
                >
                  <div className="w-9 h-9 bg-white rounded-xl p-1.5 flex items-center justify-center shrink-0 shadow-md border border-gray-200 group-hover:scale-105 group-hover:border-blue-500/50 transition-all">
                    <img 
                        src="/etbis-logo.png" 
                        alt="ETBİS" 
                        className="w-full h-full object-contain" 
                        onError={(e) => { 
                          e.currentTarget.style.display='none'; 
                          const fallback = e.currentTarget.parentElement?.querySelector('.fallback-icon'); 
                          if(fallback) fallback.classList.remove('hidden'); 
                        }} 
                    />
                    <ShieldCheck size={18} className="text-gray-900 hidden fallback-icon" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none mb-1 group-hover:text-blue-500 transition-colors">T.C. ETBİS ONAYLI</span>
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 leading-none group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Kayıtlı İşletme</span>
                  </div>
                </button>

                {/* İki mühür arasına zarif bir ayraç daha ekledik */}
                <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden md:block"></div>

                {/* 🚀 BTK YER SAĞLAYICI ONAY MÜHRÜ */}
                <button 
                  onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText('Sesgelio');
                      alert("✅ 'Sesgelio' ismi panoya kopyalandı!\n\nAçılan BTK sayfasındaki arama kutusuna yapıştırarak resmi onayımızı teyit edebilirsiniz.");
                      window.open('https://internet.btk.gov.tr/yer-saglayici-listesi', '_blank');
                  }}
                  className="group flex items-center justify-center md:justify-start gap-3 opacity-90 hover:opacity-100 transition-all duration-300 focus:outline-none w-full md:w-auto bg-gray-100/80 dark:bg-white/5 md:bg-transparent md:dark:bg-transparent py-3 md:py-0 rounded-2xl md:rounded-none"
                  title="BTK resmi listesine gitmek için tıklayın"
                >
                  <div className="w-9 h-9 bg-white rounded-xl p-1.5 flex items-center justify-center shrink-0 shadow-md border border-gray-200 group-hover:scale-105 group-hover:border-red-500/50 transition-all">
                    <img 
                        src="/btk-logo.png" 
                        alt="BTK" 
                        className="w-full h-full object-contain" 
                        onError={(e) => { 
                          e.currentTarget.style.display='none'; 
                          const fallback = e.currentTarget.parentElement?.querySelector('.fallback-icon'); 
                          if(fallback) fallback.classList.remove('hidden'); 
                        }} 
                    />
                    <ShieldCheck size={18} className="text-gray-900 hidden fallback-icon" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none mb-1 group-hover:text-red-500 transition-colors">T.C. BTK ONAYLI</span>
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200 leading-none group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Yer Sağlayıcı</span>
                  </div>
                </button>
            </div>

{/* 🚀 ÖDEME YÖNTEMLERİ LOGOLARI (İYZİCO RESMİ BANDI) */}
            <div className="flex items-center justify-center w-full md:w-auto mt-4 md:mt-0">
              <img 
                src="/banka-logo.png" 
                alt="Güvenli Ödeme Yöntemleri" 
                title="Visa, MasterCard ve iyzico ile Güvenli Ödeme"
                // 🚀 SİBER MÜHÜR: Görsel beyaz olduğu için Light modda siyah (brightness-0), 
                // Dark modda ise tekrar orijinal beyaz (dark:brightness-100) haline döner.
                className="h-8 md:h-10 w-auto object-contain brightness-0 dark:brightness-100 opacity-70 hover:opacity-100 transition-all duration-300 cursor-default"
              />
            </div>
          </div>

        </div>
      </div>
    </footer>
  ));

const LegalPageLayout = ({ title, lastUpdated, children, icon: Icon = FileText }: { title: string, lastUpdated: string, children?: React.ReactNode, icon?: any }) => {
    
    // 🚀 SİBER MÜHÜR: Google Botlarını Kovan Kod (Sadece bu yasal sayfalarda çalışır)
    useEffect(() => {
      const meta = document.createElement('meta');
      meta.name = "robots";
      meta.content = "noindex";
      document.head.appendChild(meta);

      // Kullanıcı sayfadan çıkıp ana sayfaya falan dönerse, engeli kaldır ki ana sayfa indekslensin
      return () => {
        document.head.removeChild(meta);
      };
    }, []);

    return (
      <div className="w-full flex flex-col min-h-screen">
        <div className="flex justify-center w-full py-12 bg-gray-50 dark:bg-black relative overflow-visible flex-1">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_top,rgba(255,43,94,0.05)_0%,transparent_50%)] pointer-events-none"></div>
          <div className="w-full max-w-4xl animate-fade-in-up px-6 relative z-10">
            <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-[2rem] sm:rounded-[2.5rem] p-6 md:p-10 lg:p-16 shadow-2xl">
    
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 md:mb-12 pb-6 md:pb-8 border-b border-gray-100 dark:border-white/5">
                  <div className="flex items-center gap-4 md:gap-6">
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-sesgelio/10 flex items-center justify-center text-sesgelio shrink-0">
                          <Icon className="w-6 h-6 md:w-8 md:h-8" />
                      </div>
                      <div>
                          <h1 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 dark:text-white tracking-tighter uppercase italic">{title}</h1>
                          <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1">Sürüm 3.1 • {lastUpdated}</p>
                      </div>
                  </div>
                  <div className="bg-sesgelio/10 text-sesgelio px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-sesgelio/20 flex items-center gap-2">
                      <ShieldAlert size={14}/> SIFIR TOLERANS PROTOKOLÜ
                  </div>
              </div>
              <div className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 space-y-12 leading-relaxed text-sm md:text-base">
                  {children}
              </div>
              <div className="mt-16 pt-8 border-t border-gray-100 dark:border-white/5 text-center">
                  <p className="text-gray-500 text-xs italic">Sesgelio ekosistemi, Türk Ceza Kanunu ve toplumsal etik değerler çerçevesinde, 7/24 korunmaktadır.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'system':
        return (
          <div className="w-full flex flex-col min-h-screen bg-[#fcfcfc] dark:bg-[#050505] transition-colors relative overflow-x-hidden">
             {/* Ethereal background blobs */}
             <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-20%] right-[-10%] w-[1000px] h-[1000px] bg-sesgelio/5 rounded-full blur-[150px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] left-[-5%] w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[120px]"></div>
             </div>

             <div className="relative z-10 w-full flex flex-col items-center">
                {/* Hero Manifesto Section */}
                <section className="max-w-6xl w-full px-6 pt-32 pb-24 text-center space-y-8 animate-fade-in">
                    <div className="inline-flex items-center gap-2 bg-sesgelio/10 text-sesgelio px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.5em] border border-sesgelio/20 mx-auto">
                        <Orbit size={14} className="animate-spin-slow" /> SESGELIO SİSTEM MANİFESTOSU
                    </div>
                    <h1 className="text-4xl sm:text-6xl md:text-9xl font-black text-gray-900 dark:text-white tracking-tighter leading-[0.8] uppercase italic">
                        İNSANİ BAĞIN <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-sesgelio to-purple-600">YENİ FREKANSI.</span>
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-xl md:text-3xl max-w-4xl mx-auto leading-relaxed font-medium">
                        İnternet seni daha çok insana bağladı ama seni <strong className="text-gray-900 dark:text-white font-black uppercase">daha yalnız bıraktı.</strong> Sesgelio, bu dijital buzu eritmek için tasarlandı.
                    </p>
                </section>

                {/* The "Why" Grid */}
                <section className="max-w-7xl w-full px-6 grid grid-cols-1 md:grid-cols-3 gap-8 mb-40">
                    <div className="p-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-xl hover:border-sesgelio transition-all duration-500 group">
                        <div className="w-16 h-16 bg-sesgelio/10 rounded-2xl flex items-center justify-center text-sesgelio mb-8 group-hover:scale-110 transition-transform">
                            <Wind size={32} />
                        </div>
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic mb-4">SOĞUK MESAJLARA SON</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                            Kelimeler duyguları taşıyamaz. Sesindeki o küçük titreme, o anlık gülüş, o nefes... Bunlar gerçek bağlardır. Biz, bu bağları sıfır gecikmeyle sana geri veriyoruz.
                        </p>
                    </div>

                    <div className="p-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-xl hover:border-blue-500 transition-all duration-500 group relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                            <Brain size={120} />
                        </div>
                        <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500 mb-8 group-hover:scale-110 transition-transform">
                            <Cpu size={32} />
                        </div>
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic mb-4">BİLİŞSEL YOLDAŞLIK</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                            Sesgelio AI Pro sadece bir "yapay zeka" değil. O, senin düşüncelerini anlayan, konuşmana eşlik eden ve topluluğunu daha akıllı hale getiren nöral bir dosttur.
                        </p>
                    </div>

                    <div className="p-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-xl hover:border-purple-600 transition-all duration-500 group">
                        <div className="w-16 h-16 bg-purple-600/10 rounded-2xl flex items-center justify-center text-purple-600 mb-8 group-hover:scale-110 transition-transform">
                            <Crown size={32} />
                        </div>
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic mb-4">SENİN AURAN, SENİN GÜCÜN</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                            Sıradan uygulamalarda birer isimsiz grisin. Sesgelio'da ise her Operatörün bir Aurası vardır. Rengini seç, statünü belirle ve dijital dünyada gerçekten <strong className="text-gray-900 dark:text-white">VAR OL.</strong>
                        </p>
                    </div>
                </section>

                {/* Psychology Section: The Need for Presence */}
                <section className="max-w-7xl w-full px-6 mb-40">
                    <div className="bg-gray-900 rounded-[5rem] overflow-hidden relative group shadow-[0_50px_100px_rgba(0,0,0,0.5)]">
                        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 contrast-150"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center p-12 md:p-24 relative z-10">
                            <div className="space-y-8">
                                <h2 className="text-5xl md:text-7xl font-black text-white leading-none tracking-tighter uppercase italic">
                                    DİJİTAL <br/> <span className="text-sesgelio">SIĞINAĞIN.</span>
                                </h2>
                                <p className="text-gray-400 text-xl leading-relaxed font-medium">
                                    Günün sonunda, hepimiz sadece dinlenilmek isteriz. Sesgelio, evinden kilometrelerce uzaktaki dostlarının nefesini yanında hissettiğin, yapay zekanın seninle gerçekten ilgilendiği o güvenli limandır.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-xl border border-white/10">
                                        <CheckCircle size={16} className="text-sesgelio"/>
                                        <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Kuantum Güvenlik</span>
                                    </div>
                                    <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-xl border border-white/10">
                                        <CheckCircle size={16} className="text-sesgelio"/>
                                        <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">Duygusal Rezonans</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative flex justify-center">
                                <div className="absolute inset-0 bg-sesgelio/20 blur-[120px] animate-pulse"></div>
                                <div className="w-full aspect-square max-w-sm bg-black/40 backdrop-blur-3xl rounded-[4rem] border border-white/10 flex items-center justify-center p-10 transform rotate-6 group-hover:rotate-0 transition-transform duration-1000">
                                    <Waves size={180} className="text-sesgelio drop-shadow-[0_0_40px_rgba(255,43,94,0.6)] animate-pulse" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Final Call to Action */}
                <section className="text-center py-40 space-y-12">
                    <h2 className="text-5xl md:text-8xl font-black text-gray-900 dark:text-white tracking-tighter uppercase leading-none">
                        GÖLGELERDEN <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-gray-600">KURTULMAYA HAZIR MISIN?</span>
                    </h2>
                    <p className="text-gray-500 text-xl font-medium">Bu bir yazılım değil, senin evrimin.</p>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                     <button 
                          onClick={() => { setCurrentPage('login'); scrollContainerRef.current?.scrollTo(0,0); }}
                          className="bg-sesgelio hover:bg-sesgelio-dark text-white px-8 py-4 md:px-16 md:py-6 rounded-[2rem] font-black text-lg md:text-2xl transition-all transform hover:scale-105 active:scale-95 shadow-[0_20px_60px_-10px_rgba(255,43,94,0.5)] flex items-center justify-center gap-3 md:gap-4 w-full sm:w-auto"
                        >
                            BAĞLANTIYI BAŞLAT <Play fill="currentColor" className="w-5 h-5 md:w-6 md:h-6"/>
                        </button>
                        <button 
                          onClick={() => { setCurrentPage('home'); scrollContainerRef.current?.scrollTo(0,0); }}
                          className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all text-sm font-black uppercase tracking-[0.4em]"
                        >
                            Ana Sayfaya Dön
                        </button>
                    </div>
                </section>
             </div>
             <Footer />
          </div>
        );

     case 'login':
      case 'register':
        const isLogin = currentPage === 'login';
        return (
          // 1. ANA KAPSAYICI (🚀 SİBER DÜZELTME: overflow tamamen silindi, tarayıcının DOĞAL kaydırmasına bırakıldı)
          <div className="relative min-h-screen w-full flex flex-col py-12 px-4 selection:bg-sesgelio selection:text-white">
            
       {/* --- 🌌 YENİ NESİL SİBERPUNK ARKA PLAN --- */}
            <SiberBackground />
            
          {/* --- 🚀 GOOGLE SON ADIM (DOĞUM TARİHİ VE VELİ ONAYI) EKRANI --- */}
        {googlePendingData && (
          <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-[#0a0a0c] border border-sesgelio/30 rounded-[2rem] p-10 w-full max-w-md shadow-[0_0_50px_rgba(255,43,94,0.15)] text-center animate-scale-in">
              <div className="w-20 h-20 bg-sesgelio/10 rounded-full flex items-center justify-center text-sesgelio mx-auto mb-6 border border-sesgelio/20">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter mb-2 italic">Son Bir Adım Kaldı</h2>
              <p className="text-gray-400 text-sm mb-8">
                Operatör <strong className="text-white">{googlePendingData.user.username}</strong>, yasal protokoller gereği yaşını doğrulamamız gerekiyor.
              </p>

              <div className="space-y-4 text-left">
                <div>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 mb-2 block">Doğum Tarihin</label>
                  <input 
                 type="date" 
                    required 
                    // 🚀 SİBER MÜHÜR: Bugünden sonrasını seçmeyi engeller!
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-4 text-white outline-none focus:border-sesgelio transition-all font-bold" 
                    value={googleBirthday}
                    onChange={(e) => {
                      setGoogleBirthday(e.target.value);
                      setGoogleParentalConsent(false); // Tarih değişirse onayı sıfırla
                    }}
                  />
                </div>

                {/* 🚀 SİBER RADAR: EĞER GİRİLEN YAŞ 18'DEN KÜÇÜKSE VELİ ONAYINI ÇIKART */}
                {googleBirthday && (new Date().getFullYear() - new Date(googleBirthday).getFullYear() - (new Date().getMonth() < new Date(googleBirthday).getMonth() || (new Date().getMonth() === new Date(googleBirthday).getMonth() && new Date().getDate() < new Date(googleBirthday).getDate()) ? 1 : 0)) < 18 && (
                  <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl animate-fade-in-down">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        className="mt-1 accent-red-500 w-4 h-4" 
                        checked={googleParentalConsent} 
                        onChange={(e) => setGoogleParentalConsent(e.target.checked)} 
                      />
                      <span className="text-[11px] text-gray-300 font-bold leading-tight">
                        18 yaşından küçüğüm. <span className="text-red-400">Veli/Vasi onayımı aldım</span> ve yasal sorumluluğun onlara ait olduğunu kabul ediyorum.
                      </span>
                    </label>
                  </div>
                )}

               <button
  type="button"
  onClick={handleGoogleFinalStep}
  disabled={isSubmitting || !googleBirthday}
  className={`w-full bg-sesgelio hover:bg-sesgelio-dark text-white font-black text-lg py-4 mt-2 rounded-xl transition-all shadow-xl shadow-sesgelio/20 active:scale-95 uppercase ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
>
  {isSubmitting 
    ? <Loader2 className="animate-spin mx-auto" size={24}/> 
    : 'Mührü Tamamla ve Katıl'
  }
</button>
              </div>
            </div>
          </div>
        )}

           <div className="flex-1 flex items-center justify-center p-4 sm:p-6 py-12 sm:py-20 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-sesgelio/5 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none"></div>
              
              <div className="w-full max-w-md bg-[#f8fafc] dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 md:p-12 shadow-2xl relative z-10 animate-scale-in">               {isVerifyStep ? (
                  /* --- 📧 E-POSTA DOĞRULAMA EKRANI --- */
                  <form onSubmit={handleVerifySubmit} className="space-y-6 animate-scale-in">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center text-green-500 mx-auto mb-6 border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                        <Mail size={32} />
                      </div>
                      <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic mb-2">Mühürü Kır</h2>
                      <p className="text-gray-500 text-sm">
                        <strong className="text-gray-900 dark:text-white">{email}</strong> adresine gönderdiğimiz 6 haneli onay kodunu gir.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 text-center block">Onay Kodu</label>
                      <input
                        type="text"
                        maxLength={6}
                        required
                        className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 text-center text-3xl tracking-[0.5em] font-mono text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all"
                        placeholder="000000"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value)}
                        autoFocus
                      />
                    </div>

                 <button disabled={loading || verifyCode.length !== 6} className="w-full bg-green-600 hover:bg-green-700 text-white font-black text-xl py-5 rounded-2xl transition-all active:scale-95 shadow-xl shadow-green-600/30 flex items-center justify-center gap-3">
                      {loading ? <Loader2 className="animate-spin" size={24}/> : 'HESABI AKTİF ET'}
                    </button>
                    
                    {/* 🚀 EKLENEN KISIM: KODU TEKRAR GÖNDER & İPTAL ET */}
                    <div className="flex flex-col items-center gap-4 mt-6">
                      <button 
                        type="button" 
                        onClick={handleResendCode}
                        disabled={resendCoolingDown || loading}
                        className={`text-xs font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 px-4 py-2 rounded-xl border ${resendCoolingDown ? 'text-gray-500 border-gray-200 dark:border-white/5 bg-gray-100 dark:bg-white/5 cursor-not-allowed' : 'text-sesgelio border-sesgelio/30 hover:bg-sesgelio/10 active:scale-95'}`}
                      >
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                        {resendCoolingDown ? `Tekrar Göndermek İçin Bekle (${resendRemaining}s)` : 'Kodu Tekrar Gönder'}
                      </button>

                      <button 
                        type="button" 
                        onClick={() => {
                            setIsVerifyStep(false); 
                            setVerifyCode(''); 
                            setCurrentPage('login'); 
                        }} 
                        className="text-[10px] font-bold text-gray-500 hover:text-red-500 uppercase tracking-widest transition-colors"
                      >
                        İptal Et ve Giriş Sayfasına Dön
                      </button>
                    </div>
                  </form>
                ) : (
                  /* --- 📝 NORMAL KAYIT / GİRİŞ FORMU --- */
                  <>
                   <div className="text-center mb-10">
<div className="w-20 h-20 bg-[#111214] rounded-2xl flex items-center justify-center shadow-xl shadow-black/50 border border-gray-800 transform rotate-3 hover:rotate-0 transition-transform overflow-hidden mx-auto mb-6">
   <img src="/ana_sayfa_logo.png" alt="Sesgelio Logo" className="w-full h-full object-cover scale-110" />
</div>
                       <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic tracking-tighter leading-none mb-2">
                          {isLogin ? 'Tekrar Hoş Geldin' : 'Ekosisteme Katıl'}
                       </h2>
                       <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                          {isLogin ? 'Sesgelio evrenine giriş yapmak için bilgini akıt.' : 'Yeni nesil iletişim protokolünde yerini al.'}
                       </p>
                    </div>

                    <form onSubmit={handleLoginSubmit} className="space-y-6">
                      {!isLogin && (
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Kullanıcı Adı</label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-sesgelio transition-colors"><UserIcon size={18}/></div>
                            <input type="text" required maxLength={15} className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all font-bold" placeholder="Operatör adı (Max 15 Karakter)" value={username} onChange={(e) => setUsername(e.target.value)}/>                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">E-Posta Adresi</label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-sesgelio transition-colors"><Mail size={18}/></div>
                          <input type="email" required className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all font-bold" placeholder="mail@sesgelio.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Güvenlik Şifresi</label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-sesgelio transition-colors"><Lock size={18}/></div>
                          <input type="password" required className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all font-bold" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                        </div>
                        {isLogin && <button type="button" onClick={() => { setResetStep('form'); setCurrentPage('forgot-password'); scrollContainerRef.current?.scrollTo(0,0); }} className="text-[10px] font-black text-sesgelio uppercase tracking-widest hover:underline ml-1">Şifremi unuttum</button>}
                      </div>

                      {!isLogin && (
                        <div className="space-y-6 animate-fade-in">
                          {/* --- 🎂 DOĞUM TARİHİ VE YAŞ KONTROLÜ --- */}
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Doğum Tarihi</label>
                            <div className="relative group">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-sesgelio transition-colors"><History size={18}/></div>
                              <input 
                               type="date" 
                                required 
                                // 🚀 SİBER MÜHÜR: Bugünden sonrasını seçmeyi engeller!
                                max={new Date().toISOString().split("T")[0]}
                                className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all font-bold" 
                                value={birthday}
                                onChange={(e) => setBirthday(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* --- 🛡️ EBEVEYN KONTROLÜ (Koşullu Görünüm) --- */}
                          {birthday && (new Date().getFullYear() - new Date(birthday).getFullYear() < 18) && (
                            <div className="bg-sesgelio/5 border border-sesgelio/20 p-4 rounded-2xl animate-fade-in-up">
                              <label className="flex items-center gap-3 cursor-pointer group">
                                <input 
                                  type="checkbox" 
                                  className="w-5 h-5 rounded border-gray-300 text-sesgelio focus:ring-sesgelio accent-sesgelio" 
                                  checked={parentalConsent}
                                  onChange={(e) => setParentalConsent(e.target.checked)}
                                />
                                <span className="text-xs font-bold text-sesgelio uppercase tracking-tighter">
                                  18 Yaş Altıyım, Ebeveyn Kontrolünde Kullanıyorum
                                </span>
                              </label>
                            </div>
                          )}

                          {/* --- ⚖️ YASAL PROTOKOLLER (Checkbox Alanı) --- */}
                          <div className="space-y-3 bg-gray-50 dark:bg-black/20 p-4 rounded-2xl border border-gray-200 dark:border-white/5">
                            <label className="flex items-start gap-3 cursor-pointer group">
                              <input type="checkbox" className="mt-1 accent-sesgelio" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} />
                              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">
                                <span 
                                  onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    window.open('/?page=terms', '_blank'); 
                                  }} 
                                  className="text-sesgelio hover:underline cursor-pointer"
                                >
                                  Kullanıcı Sözleşmesini
                                </span> okudum ve kabul ediyorum.
                              </span>
                            </label>
                            
                            <label className="flex items-start gap-3 cursor-pointer group">
                              <input type="checkbox" className="mt-1 accent-sesgelio" checked={agreedPrivacy} onChange={(e) => setAgreedPrivacy(e.target.checked)} />
                              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">
                                <span 
                                  onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    window.open('/?page=privacy', '_blank'); 
                                  }} 
                                  className="text-sesgelio hover:underline cursor-pointer"
                                >
                                  Gizlilik ve KVKK 
                                </span> okudum ve anladım
                              </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer group">
                              <input type="checkbox" className="mt-1 accent-sesgelio" checked={agreedGuidelines} onChange={(e) => setAgreedGuidelines(e.target.checked)} />
                              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">
                                <span 
                                  onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    window.open('/?page=guidelines', '_blank'); 
                                  }} 
                                  className="text-sesgelio hover:underline cursor-pointer"
                                >
                                  Topluluk Kurallarını
                                </span> mühürlüyorum.
                              </span>
                            </label>
                          </div>
{/* --- 🤖 RECAPTCHA --- */}
                          <div className="w-full flex justify-center my-2 overflow-hidden">
                            <div className="inline-block transform scale-[0.75] sm:scale-90 md:scale-100 origin-center transition-transform">
                            <SiberCaptcha onVerify={(token) => setRegisterCaptchaToken(token)} />
                            </div>
                          </div>
                        </div>
                      )}

                     

                     <button 
  type="submit"
  disabled={loading}
  className={`w-full bg-sesgelio hover:bg-sesgelio-dark text-white font-black text-xl py-5 rounded-2xl transition-all transform hover:scale-[1.02] active:scale-95 shadow-2xl shadow-sesgelio/30 flex items-center justify-center gap-3 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
>
  {loading 
    ? <Loader2 className="animate-spin" size={24}/> 
    : <>{isLogin ? 'BAĞLANTIYI BAŞLAT' : 'KAYDI TAMAMLA'} <ChevronRight size={24} /></>
  }
</button>

  {/* --- 🌐 GOOGLE İLE GİRİŞ (NÖRAL KÖPRÜ) --- */}
                    <div className="flex items-center my-6 opacity-60">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gray-400 dark:to-white/20"></div>
                      <span className="px-4 text-[10px] font-black text-gray-500 uppercase tracking-widest">veya</span>
                      <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gray-400 dark:to-white/20"></div>
                    </div>

                 <button 
                      type="button" 
                      onClick={() => loginWithGoogle()} 
                      className="w-full bg-white dark:bg-white text-gray-900 font-black text-lg py-4 rounded-2xl transition-all transform hover:scale-[1.02] active:scale-95 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 border border-gray-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-6 h-6">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        <path fill="none" d="M0 0h48v48H0z"/>
                      </svg>
                      Google ile Devam Et
                    </button>

                    </form>

                    <div className="mt-8 text-center">
                      <p className="text-gray-500 text-xs font-medium">
                        {isLogin ? 'Henüz bir operatör değil misin?' : 'Zaten bir kaydın var mı?'}
                        <button onClick={() => setCurrentPage(isLogin ? 'register' : 'login')} className="text-sesgelio font-black ml-2 uppercase hover:underline">
                          {isLogin ? 'Hemen Kayıt Ol' : 'Giriş Yap'}
                        </button>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <Footer />
          </div>
        );

      case 'forgot-password':
        return (
          <div className="w-full flex flex-col min-h-screen bg-white dark:bg-[#020202]">
           <div className="flex-1 flex items-center justify-center p-4 sm:p-6 py-12 sm:py-20 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-sesgelio/5 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none"></div>
              
              <div className="w-full max-w-md bg-[#f8fafc] dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 md:p-12 shadow-2xl relative z-10 animate-scale-in">                {resetStep === 'form' ? (
                  <>
                    <div className="text-center mb-10">
                      <div className="w-16 h-16 bg-sesgelio/10 rounded-2xl flex items-center justify-center text-sesgelio mx-auto mb-6 shadow-lg transform rotate-3 border border-sesgelio/20">
                        <Key size={32} />
                      </div>
                      <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic tracking-tighter leading-none mb-2">
                        ERİŞİM KURTARMA
                      </h2>
                      <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                        Kayıtlı e-posta adresini gir, nöral ağlar sana bir kurtarma anahtarı göndersin.
                      </p>
                    </div>

                    <form onSubmit={handleResetSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">E-Posta Adresi</label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-sesgelio transition-colors"><Mail size={18}/></div>
                          <input 
  type="email" 
  required 
  className="w-full bg-white dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white outline-none focus:border-sesgelio focus:ring-1 focus:ring-sesgelio transition-all font-bold" 
  placeholder="mail@sesgelio.com"
  value={resetEmail}
  onChange={(e) => setResetEmail(e.target.value)} 
/>
                        </div>
                      </div>

                      <button className="w-full bg-sesgelio hover:bg-sesgelio-dark text-white font-black text-xl py-5 rounded-2xl transition-all transform hover:scale-[1.02] active:scale-95 shadow-2xl shadow-sesgelio/30 flex items-center justify-center gap-3">
                        PROTOKOLÜ BAŞLAT <RefreshCw size={24} />
                      </button>
                    </form>

                    <div className="mt-8 text-center">
                      <button 
                        onClick={() => setCurrentPage('login')}
                        className="text-gray-500 hover:text-sesgelio font-black uppercase text-xs tracking-widest transition-colors flex items-center justify-center gap-2 mx-auto"
                      >
                        <ChevronLeft size={16} /> Giriş Sayfasına Dön
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 animate-scale-in">
                    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center text-green-500 mx-auto mb-8 shadow-xl border border-green-500/20">
                      <CheckCircle size={48} strokeWidth={2.5}/>
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 dark:text-white uppercase italic tracking-tighter leading-none mb-4">
                      İSTEK ONAYLANDI
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium leading-relaxed mb-10">
                      Kurtarma anahtarı e-posta adresine ışınlandı. Lütfen gelen kutunu (ve spam klasörünü) kontrol et Operatör.
                    </p>
                    <button 
                      onClick={() => setCurrentPage('login')}
                      className="w-full bg-gray-900 dark:bg-white text-white dark:text-black font-black text-lg py-4 rounded-2xl shadow-lg transition-all transform hover:scale-[1.02] active:scale-95"
                    >
                      GİRİŞ SAYFASINA DÖN
                    </button>
                  </div>
                )}
              </div>
            </div>
            <Footer />
          </div>
        );

   case 'terms':
        return (
          <LegalPageLayout title="Üyelik Sözleşmesi" lastUpdated="4 Nisan 2026" icon={FileText}>
             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Scale size={20} className="text-sesgelio"/> 1. TARAFLAR VE KABUL
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    <strong className="text-gray-900 dark:text-white">1.1.</strong> İşbu Kullanıcı Sözleşmesi (“Sözleşme”), Mersinli MAH. 2816 SK. NO: 110 KONAK, İZMİR adresinde mukim <strong>AYHAN ONBAŞI - Sesgelio </strong> (“Şirket” veya “Satıcı”) ile Sesgelio web sitesi, mobil uygulaması ve bağlantılı servislerini (“Platform”) kullanan tüm gerçek kişi ve tüzel kişiler (“Kullanıcı” veya “Operatör”) arasında elektronik ortamda akdedilmiştir. Kullanıcı, Platform’a kayıt olarak veya Platform’u kullanarak bu Sözleşme’nin tüm şartlarını gayrikabili rücu şekilde kabul ettiğini beyan eder.
                </p>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <BrainCircuit size={20} className="text-sesgelio"/> 2. HİZMETİN KAPSAMI VE YAPAY ZEKA (AI) PROTOKOLLERİ
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    <strong className="text-gray-900 dark:text-white">2.1.</strong> Sesgelio; düşük gecikmeli sesli iletişim, metin tabanlı mesajlaşma, sunucu (topluluk) yönetimi ve yapay zeka entegreli dijital asistan hizmetleri sunan bir iletişim ekosistemidir.
                </p>
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5 space-y-4 mt-4">
                    <div className="flex items-start gap-3">
                        <Activity size={18} className="text-sesgelio shrink-0 mt-0.5"/>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong className="text-gray-900 dark:text-white">Anlık Veri İşleme:</strong> Platform içerisindeki yapay zeka hizmetleri (Google Gemini API altyapısı ile), Kullanıcı'nın yazılı komutlarını anlık olarak işler. Şirket, sesli iletişim verilerini sunucularında kalıcı olarak depolamaz, sadece anlık iletim ve AI etkileşimi için işler.
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <ShieldAlert size={18} className="text-sesgelio shrink-0 mt-0.5"/>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong className="text-gray-900 dark:text-white">AI Sorumluluk Reddi:</strong> Yapay zeka modülleri tarafından üretilen yanıtlar, tavsiyeler veya içerikler Şirket'in resmi görüşünü yansıtmaz. AI tarafından üretilen içeriklerin doğruluğu veya uygulanabilirliği konusunda Şirket hiçbir hukuki sorumluluk kabul etmez.
                        </p>
                    </div>
                </div>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <ShieldCheck size={20} className="text-sesgelio"/> 3. KABUL EDİLEN KOŞULLAR VE GENEL SÖZLEŞME BİLGİLERİ
                </h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-6 rounded-3xl shadow-sm">
                    <p><strong className="text-gray-900 dark:text-white">3.1.</strong> sesgelio.com web sitesinde yer alan tüm bilgi, belge, yazılım, tasarım, grafik, video, ses, görüntü ve benzeri materyaller, Şirket'in telif hakları kapsamındadır. sesgelio.com web sitesine üye olan ve ziyaret eden her kullanıcı, söz konusu materyallerin telif haklarının Şirket'e ait olduğunu kabul eder.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.2.</strong> sesgelio.com web sitesine üye olan her kullanıcı, siteyi kullanırken bulunduğu ülkenin yasalarına uygun hareket etmeyi kabul eder. Bulunduğu ülkenin yasaları haricinde Türkiye Cumhuriyeti yasaları geçerli sayılır. Herhangi bir yasadışı faaliyetin tespiti halinde, ilgili kullanıcının üyeliği Şirket tarafından derhal sonlandırılır.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.3.</strong> sesgelio.com web sitesine üye olan her kullanıcı, siteyi kullanırken bulunduğu ülkenin yasalarına uygun hareket etmeyi kabul eder. Bulunduğu ülkenin yasaları haricinde Türkiye Cumhuriyeti yasaları geçerli sayılır. Herhangi bir yasadışı faaliyetin tespiti halinde, ilgili kullanıcının üyeliği Şirket tarafından derhal sonlandırılır.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.4.</strong> sesgelio.com web sitesi, kullanıcı hesaplarına erişimi güvence altına almak amacıyla belirli güvenlik protokolleri kullanabilir. Tüm kullanıcılar, bu güvenlik önlemlerine ve protokollerin gerekliliklerine uygun davranmayı kabul ve taahhüt eder.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.5.</strong> sesgelio.com web sitesine üye olabilmek için kullanıcıların geçerli bir e-posta adresi ve ödeme ekranlarında kişisel bilgilerini eksiksiz şekilde sunmaları gerekmektedir. Üye, verdiği bilgilerin doğru, güncel ve eksiksiz olduğunu kabul eder; bu bilgilerin doğruluğu ve güncelliği konusunda tüm sorumluluk kendisine aittir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.6.</strong> Şirket, sesgelio.com platformunun kullanım kurallarına aykırı hareket eden kullanıcıların üyeliklerini, önceden bildirimde bulunmaksızın geçici olarak askıya alma veya tamamen kapatma hakkını saklı tutar.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.7.</strong> Şirket, sesgelio.com platformunun kullanım kurallarına aykırı hareket eden kullanıcıların üyeliklerini, önceden bildirimde bulunmaksızın geçici olarak askıya alma veya tamamen kapatma hakkını saklı tutar.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.8.</strong> Üyeler, Üyelik Sözleşmesi’nde yapılan değişikliklerin sesgelio.com web sitesinde yayımlandığı tarihten itibaren yürürlüğe gireceğini ve bu değişikliklere uygun davranmakla yükümlü olduklarını kabul ederler. Şirket, yapılan değişiklikler sonrasında üyelerden, web sitesine girişleri sırasında sözleşmeyi yeniden onaylamalarını isteyebilir veya bu onayı dolaylı olarak kabul edebilir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.9.</strong> sesgelio.com web sitesinde sunulan tüm hizmetler, Şirket’in takdirine bağlı olarak herhangi bir zamanda kısmen veya tamamen değiştirilebilir, durdurulabilir ya da kaldırılabilir. Kullanıcılar, Şirket’in bu tür değişiklikleri önceden bildirme veya açıklama yükümlülüğü bulunmadığını kabul ederler.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.10.</strong> sesgelio.com, kullanıcı bilgilerini gizlilik ve güvenlik ilkeleri doğrultusunda saklamayı taahhüt eder. Verileriniz pazarlama faaliyetleri için istatistiksel verilere <strong className="text-sesgelio">DÖNÜŞTÜRÜLMEZ.</strong></p>
                    <p><strong className="text-gray-900 dark:text-white">3.11.</strong> sesgelio.com'da sunulan hizmetleri kullanırken, reklam, haksız rekabet, terör, kara para aklama, cumhuriyet, devlet, din, ırkçılık, uyuşturucu, politikacılar ya da diğer tüzel kişilere karşı yazışmalar, pornografik içerik, argo kelimeler kullanımı veya diğer kullanıcılara taciz içeren davranışlar, çocuk istismarı, siber zorbalık ve sistem manipülasyonu gibi yasalar tarafından suç olarak kabul edilen herhangi bir işlem veya eylem yasaktır. Bu tür durumlarda, sesgelio.com tarafından, üyenin hesaplarını kalıcı veya geçici olarak askıya alabilir, ve kullanıcının oluşturduğu içerikler ve veriler, talep halinde resmi makamlarla paylaşılabilir. Benzer şekilde, kullanıcı bilgileri de resmi makamların talep etmesi durumunda, savcılık ve emniyet birimleri de dahil olmak üzere, ilgili resmi ve idari makamlarla paylaşılabilir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.12.</strong> sesgelio.com’daki destek sistemi ve diğer iletişim kanallarından, yönetim, çalışanları veya üyelerine küfür, hakaret veya taciz içeren mesajlar gönderen kişilerin üyelikleri uyarılmadan kapatılacaktır. Bu tür davranışlar, sesgelio.com topluluğu için kabul edilemezdir ve üyeler arasındaki saygı ve nezaket ilkelerine uygun davranılması gerekmektedir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.13.</strong> Üye, sesgelio.com’da sunulan Hizmetler'den yararlanmak için yayınlanan kurallara uygun davranmayı kabul etmiştir. Herhangi bir değişiklik durumunda, Üyeler önceden elde ettikleri hak iddialarında bulunamazlar. Bu sözleşmeye yapılan değişiklikler, öncelikle sesgelio.com internet sitesinde yayınlanır ve yayınlandığı andan itibaren geçerlidir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.14.</strong> Şirket, istediği zaman, sebep göstermeksizin ve önceden bildirim yapmadan, Üye'nin üyeliğini geçici veya kalıcı olarak askıya alma, belirli sınırlamalara tabi tutma veya işbu Üyelik Sözleşmesi'ni tek taraflı olarak feshetme hakkına sahiptir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.15.</strong> Üyelik Sözleşmesi, Kullanım Koşulları ve eklerine aykırılık durumunda, Şirket'in maddi ve manevi zararının tazmini talep etme hakkı saklıdır. sesgelio.com herhangi bir sebeple yayınının durması veya durdururulması durumunda, Üyelik Sözleşmesi ve eklerinin bu süre boyunca askıda kalacağı kabul edilir. Bu süre boyunca Üyeler, tazmin talebinde bulunamazlar.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.16.</strong> Bu sözleşme, sesgelio.com’daki tüm Hizmetler'in sona ermesiyle kendiliğinden feshedilir. Ancak Şirket, Hizmetler'de belirtilen yöntemlere ek hizmetler sunmaya başlarsa, bu sözleşme yeni hizmetler için de geçerli olacaktır.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.17.</strong> Üye, işbu sözleşmeyi 15 gün öncesinden yazılı ve imzalı bir dilekçe ile feshedebilir. Fesih işlemi Şirket tarafından onaylandığında, Üye'ye E-mail yoluyla bildirimde bulunulur.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.18.</strong> Üye, kendisine ait kullanıcı adı ve şifre bilgilerinin güvenliğinden kendisi sorumludur. Bu bilgileri üçüncü şahıslarla paylaşamaz veya kullanımına sunamaz. Üye, bu bilgilerin izinsiz kullanımını fark ederse hemen Şirket'e bildirimde bulunmayı taahhüt eder. Şirket, izinsiz kullanımı önlemek için Üye'nin hesabına erişimi engelleme veya üyeliğini sonlandırma hakkına sahiptir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.19.</strong> Şirket, kullanılmayan sunucuları geçersiz kılma hakkına sahiptir. Aktif olmayan sunucular Pasif durumuna alınıp kapatılabilir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.20.</strong> 6502 sayılı Tüketicinin Korunması Hakkında Kanun'unda yer aldığı üzere orjinal ambalajı zarar görmüş, tahrip edilmiş, herhangi bir şekilde kullanımdan dolayı zarara uğramış ürünler ile tek kullanımlık ürünler, kopyalanabilir oyun, yazılım, teslimatı dijital ortamda yapılan ürünler ve programların iadesi kabul edilmemektedir.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.21.</strong> Sitedeki kullanıcının gerçekleştirdiği tüm işlemler log olarak saklanmaktadır. Kullanıcılar sitede herhangi bir işlem sağlaması durumunda üyeliğinin silinmeyeceğini ve logların kaldırılmayacağını kabul eder.</p>
<p><strong className="text-gray-900 dark:text-white">3.22.</strong> Üye, sözleşme ve T.C. kanunlara aykırı davranması durumunda tüm üyelik kayıtları onay alınmaksızın ilgili kurum/kuruluşların talep edilmesi durumunda bilgilerinin paylaşılacağını kabul eder.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.23.</strong> sesgelio.com sunuculardaki, Özel sohbet (DM) ve Grup Sohbetlerindeki yazışmaları KVKK sayfasında belirtilen süreler içerisinde saklamakla yükümlüdür.</p>
                    <p><strong className="text-gray-900 dark:text-white">3.24. Kart Saklama ve Tekrarlayan Ödemeler:</strong> Kullanıcı, Sesgelio platformu üzerinden (Premium, Elite, Bot Kiralamaları vb.) başlattığı aylık abonelik işlemlerinde; tahsilatın sorunsuz gerçekleşebilmesi için kredi/banka kartı bilgilerinin Şirket'in anlaşmalı olduğu BDDK lisanslı ödeme kuruluşu (iyzico) altyapısında "Tokenizasyon" yöntemiyle güvenli bir şekilde saklanmasını kabul eder. Kullanıcı, aboneliğini iptal etmediği sürece her fatura döneminde ilgili tutarın saklı kartından otomatik olarak tahsil edileceğini gayrikabili rücu kabul ve beyan eder.</p>
                </div>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <CreditCard size={20} className="text-sesgelio"/> 4. DİJİTAL ÜRÜNLER, ABONELİK VE İADE POLİTİKASI (CAYMA HAKKI)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">4.1.</strong> Platform üzerinde sunulan Sesgelio Premium/Elite abonelikleri, Sunucu Botu kiralamaları ve satın alımları, Profil Vitrini (Kozmetik) ve Arka Plan (Wallpaper) ürünlerinin tamamı <strong>"Dijital İçerik"</strong> ve <strong>"Anında İfa Edilen Hizmet"</strong> statüsündedir.
                </p>
                <div className="bg-red-500/5 border border-red-500/10 p-6 rounded-2xl space-y-4 mt-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5"/>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Mesafeli Sözleşmeler Yönetmeliği Madde 15/1-ğ uyarınca; elektronik ortamda anında ifa edilen hizmetler ve tüketiciye anında teslim edilen gayrimaddi mallarda <strong>CAYMA HAKKI KULLANILAMAZ.</strong> Kullanıcı, satın alma işlemini onayladığı an ücret iadesi talep edemeyeceğini kabul eder.
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <RefreshCw size={18} className="text-red-500 shrink-0 mt-0.5"/>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Aylık abonelikler ve Sesgelio AI botu ile Sesgelio AudioLink botu kiralamalarında, Kullanıcı tarafından iptal edilmediği sürece her fatura döneminde otomatik olarak yenilenir. İptal işlemi, bir sonraki fatura döneminden itibaren geçerli olur.
                        </p>
                    </div>
                </div>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Activity size={20} className="text-sesgelio"/> 5. HİZMETİN KESİNTİSİ VE SINIRLANDIRILMASI
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">5.1.</strong> Şirket, %99.99 çalışma süresi (uptime) hedeflese de; bakım, altyapı güncellemeleri veya mücbir sebeplerden kaynaklanan kesintilerden sorumlu tutulamaz. Platform "olduğu gibi" esasıyla sunulmaktadır. Şirket, Kullanıcı'nın Platform'u kullanımı sebebiyle uğrayabileceği dolaylı, geçici veya veri kaybına dayalı zararlardan sorumlu değildir.
                </p>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Gavel size={20} className="text-sesgelio"/> 6. UYUŞMAZLIKLARIN ÇÖZÜMÜ
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">6.1.</strong> İşbu Sözleşme'nin uygulanmasından ve yorumlanmasından doğacak her türlü ihtilafta Türk Hukuku uygulanacak olup, <strong>İzmir Mahkemeleri ve İcra Daireleri</strong> kesin yetkilidir. Tüketici sıfatını haiz Kullanıcılar, yasal sınırlar dâhilinde Tüketici Hakem Heyetlerine başvurabilirler.
                </p>
             </section>

             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <CheckCircle size={20} className="text-sesgelio"/> 7. YÜRÜRLÜK
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">7.1.</strong> 7 (yedi) maddeden oluşan işbu Kullanıcı Sözleşmesi, Kullanıcı tarafından elektronik ortamda onaylandığı (kayıt olunduğu veya Platform'un kullanılmaya başlandığı) an itibarıyla yürürlüğe girmiştir. Şirket, gerekli gördüğü hallerde bu sözleşmeyi tek taraflı olarak güncelleyebilir; güncellemeler Platform'da yayınlandığı an geçerlilik kazanır.
                </p>
             </section>
          </LegalPageLayout>
        );

      case 'privacy':
        return (
          <LegalPageLayout title="Gizlilik ve KVKK Aydınlatma Politikası" lastUpdated="4 Nisan 2026" icon={Shield}>
             
             {/* GİRİŞ BÖLÜMÜ */}
             <section className="space-y-4">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <UserCheck size={24} className="text-sesgelio"/> GİZLİLİK VE KİŞİSEL VERİLERİN KORUNMASI HAKKINDA BİLGİLENDİRME METNİ
                </h3>
                <p className="border-l-4 border-sesgelio/50 pl-6 italic bg-gray-50 dark:bg-white/5 py-4 rounded-r-xl text-sm text-gray-700 dark:text-gray-300">
                    AYHAN ONBAŞI - Sesgelio  (Sesgelio) olarak, 6698 Sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) uyarınca verilerinizi en üst düzeyde koruyoruz. Bu metin, Sesgelio (“Platform”) kullandığınızda hangi verilerinizin, hangi amaçla işlendiğini ve kimlere aktarıldığını "Aydınlatma Yükümlülüğü" kapsamında açıklar.
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    AYHAN ONBAŞI - Sesgelio , özel hayatın gizliliğinin temeli olan kişisel verilerin korunması ve hukuka uygun olarak işlenmesini sadece mevzuata uyum sağlama kapsamında değerlendirmemekte, yaklaşımının temeline insana verdiği değeri koymaktadır. Bu bilinçle hareket eden Sesgelio, kişisel verilerin güvenli bir şekilde saklanması ile hukuka aykırı olarak işlenmesinin önlenmesi için gerekli her türlü idari ve teknik önlemi almaktadır.
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Bu çerçevede, 6698 sayılı Kişisel Verilerin Korunması Kanunu’na uygun olarak https://sesgelio.com/ internet sitesinin kullanımı esnasında üretilen veya paylaşılan kişisel verilerin işlenme ve aktarılma koşulları hakkındaki bilgiler aşağıda sunulmaktadır.
                </p>
             </section>

             {/* 1. TANIMLAR */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <FileText size={20} className="text-sesgelio"/> 1. TANIMLAR
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">İnternet Sitesi:</strong>
                        <span className="text-gray-600 dark:text-gray-400">https://sesgelio.com/ adresinde bulunan internet sitesidir.</span>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">Kanun:</strong>
                        <span className="text-gray-600 dark:text-gray-400">6698 sayılı Kişisel Verilerin Korunması Kanunu’dur.</span>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">Kişisel Veri:</strong>
                        <span className="text-gray-600 dark:text-gray-400">Kimliği belirli veya belirlenebilir gerçek kişiye ilişkin her türlü bilgidir.</span>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">Kurul:</strong>
                        <span className="text-gray-600 dark:text-gray-400">Kişisel Verileri Koruma Kurulu’dur.</span>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">Şirket:</strong>
                        <span className="text-gray-600 dark:text-gray-400"> – AYHAN ONBAŞI - Sesgelio'dur.</span>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-2xl shadow-sm">
                        <strong className="text-sesgelio block mb-1">Yer sağlayıcı:</strong>
                        <span className="text-gray-600 dark:text-gray-400">İnternet ortamında hizmet ve içerikleri barındıran sistemleri sağlayan veya işleten gerçek veya tüzel kişileri ifade eder.</span>
                    </div>
                </div>
             </section>

             {/* 2. İŞLENEN KİŞİSEL VERİLER */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Database size={20} className="text-sesgelio"/> 2. İŞLENEN KİŞİSEL VERİLER
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sistemin güvenli çalışması ve yasal yükümlülüklerimizin yerine getirilmesi adına yalnızca minimum seviyede veri işlenmektedir:</p>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-white/5 p-5 rounded-3xl border border-gray-100 dark:border-white/5">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Kimlik ve İletişim Verileri</h4>
                        <p className="text-xs text-gray-500">Kullanıcı adı, e-posta adresi, şifre, doğum tarihi (18 yaş yasal kontrolü için) ve profil görseli.</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-white/5 p-5 rounded-3xl border border-gray-100 dark:border-white/5">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Teknik ve Etkileşim Verileri</h4>
                        <p className="text-xs text-gray-500">IP adresi, cihaz bilgileri, anlık sesli iletişim akışları (No-Log prensibiyle) ve Sesgelio AI etkileşim logları.</p>
                    </div>
                    <div className="bg-sesgelio/5 p-5 rounded-3xl border border-sesgelio/20 relative overflow-hidden shadow-lg shadow-sesgelio/5">
                        <h4 className="font-bold text-sesgelio mb-2 text-sm flex items-center gap-2">Resmi Fatura Verileri <span className="bg-sesgelio text-white text-[9px] px-2 py-0.5 rounded uppercase tracking-widest">Önemli</span></h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ad, Soyad, T.C. Kimlik Numarası, İkamet Adresi, Ülke ve Şehir.</p>
                    </div>
                </div>
                
                <div className="mt-4 p-5 bg-white dark:bg-[#0a0a0c] border-l-4 border-blue-500 rounded-r-2xl shadow-sm">
                    <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-2 flex items-center gap-2">
                        <Info size={16} className="text-blue-500"/> Neden Bu Verileri İstiyoruz?
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                        Bu veriler, 213 sayılı Vergi Usul Kanunu (VUK) gereğince adınıza yasal e-Fatura/e-Arşiv kesebilmek ve Maliye Bakanlığı'na karşı vergisel yükümlülüklerimizi eksiksiz yerine getirmek amacıyla sadece ödeme aşamasında talep edilir. Bu bilgiler, kanuni saklama süreleri boyunca şifreli olarak muhafaza edilir ve profil çıkarma, pazarlama veya veri satışı gibi amaçlarla kesinlikle kullanılamaz.
                    </p>
                </div>
             </section>

             {/* 3. SIFIR VERİ TİCARETİ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Ban size={20} className="text-red-500"/> 3. SIFIR VERİ TİCARETİ VE PAZARLAMA REDDİ (DİKKAT)
                </h3>
                <div className="p-6 bg-green-500/5 border border-green-500/20 rounded-[2rem] space-y-4 shadow-sm">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                        Sesgelio'nun gelir modeli, şeffaf abonelikler (Premium/Elite) ve hizmet satışları (Sunucu Botları vb.) üzerine kuruludur. Bu nedenle, piyasadaki <strong>"Hizmet ücretsizse ürün sizsiniz"</strong> mantığını tamamen reddediyoruz.
                    </p>
                    <ul className="text-sm space-y-3 text-gray-600 dark:text-gray-400 pt-2">
                        <li className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5"/>
                            <span>Kullanıcı davranışlarınız, girdiğiniz sunucular veya mesajlaşma alışkanlıklarınız hiçbir şekilde reklam ajanslarına, veri brokerlarına (data brokers) veya üçüncü şahıslara <strong>SATILMAZ.</strong></span>
                        </li>
                        <li className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5"/>
                            <span>Platform içerisinde size özel reklam göstermek (hedefli reklamcılık) amacıyla davranışsal profiliniz çıkarılmaz.</span>
                        </li>
                    </ul>
                </div>
             </section>

             {/* 4. SES VERİLERİ VE AI */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Brain size={20} className="text-sesgelio"/> 4. SES VERİLERİ VE AI İŞLEME (KRİTİK)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sesgelio ekosisteminde, sesli iletişim ağınız ile yapay zeka etkileşiminiz birbirinden tamamen bağımsız ve izole iki farklı mimaridir:</p>
                <div className="p-6 bg-[#0a0a0c] border border-white/5 rounded-[2rem] space-y-5 shadow-inner">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-blue-500/20 text-blue-500 rounded-lg shrink-0"><Lock size={18}/></div>
                        <p className="text-sm text-gray-300"><strong>Özel Sohbetleriniz Asla Dinlenmez (No-Log):</strong> Sesli odalarda diğer kullanıcılarla yaptığınız normal konuşmalar anlık olarak şifrelenir ve iletilir. Bu ses verileri sunucularımızda kaydedilmez ve hiçbir 3. taraf yapay zeka servisine gönderilmez.</p>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-purple-500/20 text-purple-500 rounded-lg shrink-0"><Mic size={18}/></div>
                        <p className="text-sm text-gray-300"><strong>Yapay Zekaya Sesli Komut Verme (STT Altyapısı):</strong> Odalarda bulunan Sesgelio AI asistanına doğrudan bir sesli komut verdiğinizde, üst düzey bir güvenlik mimarisi devreye girer. Sesiniz, anlık "Speech-to-Text" (Sesi Metne Çevirme) teknolojimizle milisaniyeler içinde yazılı metne dönüştürülür.</p>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-sesgelio/20 text-sesgelio rounded-lg shrink-0"><Cpu size={18}/></div>
                        <p className="text-sm text-gray-300"><strong>Gemini Sadece "Metni" İşler:</strong> İş ortağımız Google Cloud (Gemini API) altyapısına gönderilen veri, sizin ham ses dosyanız veya ses tonunuz DEĞİLDİR. Sadece metne dönüştürülmüş olan o komut cümlesi (yazı) işlem görmek üzere iletilir. İşlem bittiği an, komutu verdiğiniz o anlık ham ses verisi sistemden kalıcı olarak imha edilir. Yapay zeka sizi duymaz, sadece metne dökülen talebinizi okur.</p>
                    </div>
                </div>
             </section>

{/* 5. VERİ GÜVENLİĞİ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Shield size={20} className="text-sesgelio"/> 5. VERİ GÜVENLİĞİ VE KART BİLGİLERİ (İYZİCO GÜVENCESİ)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Verileriniz, endüstri standardı <strong>AES-256 şifreleme</strong> ile korunur. Ses kanallarımızda WebRTC protokolü ve uçtan uca şifreleme (E2EE) katmanları aktif olarak kullanılmaktadır. Sunucularımız Türkiye merkezli olup, siber saldırılara karşı 7/24 denetlenmektedir.
                </p>
                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-5 rounded-r-2xl mt-4">
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                        <strong>Kredi Kartı Verileriniz:</strong> Sesgelio, kredi kartı veya banka kartı bilgilerinizi kendi sunucularında <strong className="text-gray-900 dark:text-white">KESİNLİKLE SAKLAMAZ.</strong> Ödeme ve abonelik işlemleriniz sırasında girdiğiniz finansal bilgiler, BDDK lisanslı ve uluslararası PCI-DSS güvenlik sertifikasına sahip iş ortağımız <strong>iyzico</strong>'nun şifreli sunucularında "Token" (şifreli veri) olarak muhafaza edilir. Aylık tekrarlayan abonelik çekimleri, bu güvenli altyapı üzerinden doğrudan iyzico tarafından sağlanır.
                    </p>
                </div>
             </section>

             {/* 6. HAKLARINIZ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <FileSearch size={20} className="text-sesgelio"/> 6. HAKLARINIZ (KVKK MADDE 11)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">KVKK uyarınca Şirketimize başvurarak;</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5"><Check size={16} className="text-sesgelio"/> Verilerinizin işlenip işlenmediğini öğrenme,</div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5"><Check size={16} className="text-sesgelio"/> Verilerin silinmesini (Unutulma Hakkı) isteme,</div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5"><Check size={16} className="text-sesgelio"/> Eksik verilerin düzeltilmesini talep etme,</div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5"><Check size={16} className="text-sesgelio"/> Veri işlemeye itiraz etme haklarına sahipsiniz.</div>
                </div>
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    Talepleriniz için <strong className="text-gray-900 dark:text-white">kvkk@teknodes.com</strong> adresine e-posta gönderebilirsiniz.
                </p>
             </section>

             {/* 7. GÜNCELLEMELER */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <History size={20} className="text-sesgelio"/> 7. GÜNCELLEMELER
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Sesgelio, teknolojik gelişmelere ve yasal değişikliklere uyum sağlamak adına bu politikayı dilediği zaman güncelleyebilir. Güncellemeler yayına girdiği an geçerlilik kazanır.
                </p>
             </section>

          </LegalPageLayout>
        );
        
      case 'cookies':
        return (
          <LegalPageLayout title="Çerez (Cookie) Politikası" lastUpdated="4 Nisan 2026" icon={Cookie}>
             
             {/* GİRİŞ BÖLÜMÜ */}
             <section className="space-y-4">
                <p className="border-l-4 border-sesgelio/50 pl-6 italic bg-gray-50 dark:bg-white/5 py-4 rounded-r-xl text-sm text-gray-700 dark:text-gray-300">
                    <strong>AYHAN ONBAŞI - Sesgelio </strong> (Bundan böyle "Sesgelio" veya "Şirket" olarak anılacaktır) olarak, Sesgelio platformunda ("Platform") deneyiminizi iyileştirmek, güvenliğinizi sağlamak ve sistem performansını optimize etmek amacıyla çerezler (cookies) kullanmaktayız. Bu politika, hangi çerezleri neden kullandığımızı ve bu çerezleri nasıl yönetebileceğinizi şeffaf bir şekilde açıklamaktadır.
                </p>
             </section>

             {/* 1. ÇEREZ NEDİR */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <HelpCircle size={20} className="text-sesgelio"/> 1. ÇEREZ (COOKIE) NEDİR?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Çerezler, Platformumuzu ziyaret ettiğinizde veya kullandığınızda bilgisayarınıza, akıllı telefonunuza veya tabletinize kaydedilen küçük metin dosyalarıdır. Bu dosyalar, sunucularımızın cihazınızı tanımasını ve platformun sizin için doğru, güvenli ve hızlı bir şekilde çalışmasını sağlar.
                </p>
             </section>

             {/* 2. ÇEREZ TÜRLERİ VE SIFIR REKLAM GARANTİSİ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Layers size={20} className="text-sesgelio"/> 2. PLATFORMUMUZDA KULLANILAN ÇEREZ TÜRLERİ
                </h3>
                
                <div className="bg-green-500/10 border border-green-500/20 p-5 rounded-2xl flex items-start gap-4 shadow-sm mb-6">
                    <div className="bg-green-500 text-white p-2 rounded-lg shrink-0 mt-1"><ShieldCheck size={20}/></div>
                    <div>
                        <h4 className="text-sm font-bold text-green-600 dark:text-green-400 mb-1 uppercase tracking-widest">Sıfır Reklam Garantisi</h4>
                        <p className="text-xs text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                            Sesgelio, "Hizmet ücretsizse ürün sizsiniz" mantığını reddeden, abonelik tabanlı kapalı bir ekosistemdir. Bu nedenle Platformumuzda <strong>hiçbir şekilde reklam, pazarlama veya davranışsal profil çıkarma çerezi KULLANILMAZ.</strong> Yalnızca aşağıdaki çerez türleri aktiftir:
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-[#0a0a0c] p-6 rounded-3xl border border-gray-200 dark:border-white/5 shadow-sm hover:border-sesgelio/30 transition-colors group">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center group-hover:scale-110 transition-transform"><Lock size={20}/></div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Zorunlu (Teknik)</h4>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">Platformun temel işlevlerini yerine getirebilmesi için kesinlikle gereklidir. Oturumun açık kalması, 3D Secure güvenli ödeme, WebRTC ses kanallarına bağlantı ve bot saldırılarına karşı koruma sağlar. <strong className="text-red-500">Kapatılamaz.</strong></p>
                    </div>

                    <div className="bg-white dark:bg-[#0a0a0c] p-6 rounded-3xl border border-gray-200 dark:border-white/5 shadow-sm hover:border-blue-500/30 transition-colors group">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform"><Settings2 size={20}/></div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">İşlevsel (Tercih)</h4>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">Sesgelio üzerindeki kişisel tercihlerinizi (Karanlık/Aydınlık tema, mikrofon hassasiyeti, bas-konuş kısayolları) cihazınızda tutarak size özel ve kesintisiz bir deneyim sunar.</p>
                    </div>

                    <div className="bg-white dark:bg-[#0a0a0c] p-6 rounded-3xl border border-gray-200 dark:border-white/5 shadow-sm hover:border-purple-500/30 transition-colors group">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform"><Activity size={20}/></div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Performans ve Analitik</h4>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">Sistem sağlığını ölçmek için kullanılır. Ses odalarındaki gecikme (ping) süreleri veya çökme raporlarını <strong>tamamen anonim</strong> olarak toplar. Yalnızca altyapıyı hızlandırmak için kullanılır.</p>
                    </div>
                </div>
             </section>

             {/* 3. ÜÇÜNCÜ TARAF ÇEREZLERİ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Network size={20} className="text-sesgelio"/> 3. ÜÇÜNCÜ TARAF ÇEREZLERİ
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Sesgelio, yalnızca güvenliği ve teknik altyapıyı sağlamak adına yetkili ve güvenilir 3. parti servislerin zorunlu çerezlerine izin verir:</p>
                <div className="space-y-3">
                    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                        <ShieldAlert size={20} className="text-sesgelio shrink-0 mt-1"/>
                        <div>
                            <h5 className="text-sm font-bold text-gray-900 dark:text-white">Güvenlik (Google reCAPTCHA)</h5>
                            <p className="text-xs text-gray-500 mt-1">Kötü niyetli yazılımları (botları) ve DDoS saldırılarını engellemek için arka planda çalışır.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                        <CreditCard size={20} className="text-sesgelio shrink-0 mt-1"/>
                        <div>
                            <h5 className="text-sm font-bold text-gray-900 dark:text-white">Finansal İşlemler (Sanal POS)</h5>
                            <p className="text-xs text-gray-500 mt-1">Premium, Elite veya Bot kiralama işlemlerinde, BDDK lisanslı Sanal POS sağlayıcımızın ödemeyi doğrulayabilmesi (3D Secure) için anlık çerezler kullanılır. Kart bilgileriniz sistemimize kaydedilmez.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                        <Brain size={20} className="text-sesgelio shrink-0 mt-1"/>
                        <div>
                            <h5 className="text-sm font-bold text-gray-900 dark:text-white">API İletişimi (Gemini Cloud)</h5>
                            <p className="text-xs text-gray-500 mt-1">Sesgelio AI (Google Gemini) ile olan metin tabanlı komut trafiğinin kesintisiz yönlendirilmesi için gerekli olan geçici bağlantı çerezleridir.</p>
                        </div>
                    </div>
                </div>
             </section>

             {/* 4. ÇEREZ YÖNETİMİ VE UYARI */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <MousePointer2 size={20} className="text-sesgelio"/> 4. ÇEREZLERİ NASIL YÖNETEBİLİRSİNİZ?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Çoğu web tarayıcısı, çerezleri otomatik olarak kabul edecek şekilde ayarlanmıştır. Tarayıcınızın ayarlar kısmından çerezleri dilediğiniz zaman silebilir, engelleyebilir veya sınırlandırabilirsiniz.</p>
                
                <div className="bg-red-500/10 border-l-4 border-red-500 p-5 rounded-r-2xl mt-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={18} className="text-red-500"/>
                        <h4 className="font-bold text-red-500 text-sm uppercase tracking-widest">ÖNEMLİ UYARI</h4>
                    </div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                        Zorunlu çerezleri tarayıcınızdan engellemeniz durumunda; <strong className="text-gray-900 dark:text-white">hesabınıza giriş yapamazsınız, sesli/görüntülü odalara bağlanamazsınız ve Sesgelio AI dahil olmak üzere platformun hiçbir temel işlevini kullanamazsınız.</strong>
                    </p>
                </div>
             </section>

             {/* 5. İLETİŞİM */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Mail size={20} className="text-sesgelio"/> 5. İLETİŞİM
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Çerez Politikamız hakkında sorularınız veya veri gizliliği ile ilgili talepleriniz için <strong className="text-gray-900 dark:text-white">kvkk@teknodes.com</strong> veya <strong className="text-gray-900 dark:text-white">destek@teknodes.com</strong> üzerinden bizimle iletişime geçebilirsiniz.
                </p>
             </section>

          </LegalPageLayout>
        );
      case 'guidelines':
        return (
          <LegalPageLayout title="Topluluk Kuralları" lastUpdated="1 Şubat 2026" icon={Heart}>
             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <ScaleIcon size={24} className="text-sesgelio"/> 1. TEMEL PRENSİPLER
                </h3>
                <p>Sesgelio, insanların kendilerini özgürce ifade edebildiği ancak güvenliğin her şeyden önce geldiği bir ekosistemdir. Platformu kullanan her operatör, diğer operatörlerin huzurunu korumakla yükümlüdür.</p>
             </section>

             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <Ban size={24} className="text-sesgelio"/> 2. SIFIR TOLERANS (KALICI YASAKLANMA)
                </h3>
                <p>Aşağıdaki eylemler, <strong>Türkiye Cumhuriyeti Türk Ceza Kanunu (TCK)</strong> uyarınca suç teşkil eder ve tespiti halinde verileriniz adli makamlarla paylaşılarak hesabınız kalıcı olarak silinir:</p>
                <div className="space-y-4">
                    <div className="p-6 bg-red-500/5 border border-red-500/20 rounded-[2rem] flex flex-col md:flex-row gap-6 items-center">
                        <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center shrink-0 shadow-lg">
                            <ShieldAlert size={24}/>
                        </div>
                        <div className="text-sm">
                            <strong>Uyuşturucu ve Madde Bağımlılığı:</strong> Uyuşturucu maddelerin özendirilmesi, satışı veya kullanımına dair her türlü görsel/sesli içerik.
                        </div>
                    </div>
                    <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-[2rem] flex flex-col md:flex-row gap-6 items-center">
                        <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center shrink-0 shadow-lg">
                            <Lock size={24}/>
                        </div>
                        <div className="text-sm">
                            <strong>Çocuk İstismarı ve Müstehcenlik:</strong> Çocukların cinsel istismarına yönelik içerikler, pedofili veya rıza dışı müstehcenlik paylaşımları.
                        </div>
                    </div>
                    <div className="p-6 bg-red-500/5 border border-red-500/20 rounded-[2rem] flex flex-col md:flex-row gap-6 items-center">
                        <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center shrink-0 shadow-lg">
                            <ScaleIcon size={24}/>
                        </div>
                        <div className="text-sm">
                            <strong>Terör ve Nefret Söylemi:</strong> Terör örgütü propagandası, ırkçılık, dini değerlere hakaret ve toplumu kin ve düşmanlığa tahrik (TCK Md. 216).
                        </div>
                    </div>
                </div>
             </section>

             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <Mic size={24} className="text-sesgelio"/> 3. SESLİ ODALAR VE ETİK
                </h3>
                <ul className="list-disc pl-5 space-y-4 marker:text-sesgelio text-sm">
                    <li><strong>Sesli Taciz (Ear-rape):</strong> Diğer kullanıcıları rahatsız edecek düzeyde yüksek ses, müzik veya gürültü kirliliği yapmak yasaktır.</li>
                    <li><strong>Rıza Dışı Kayıt:</strong> Sesli odalarda yapılan konuşmaların, katılımcıların tamamının izni olmadan kaydedilmesi ve harici platformlarda yayınlanması kesinlikle yasaktır.</li>
                    <li><strong>Bot Suistimali:</strong> Müzik botlarını veya AI asistanlarını, kanalları "spam"lemek veya diğer kullanıcıların konuşmasını engellemek için kullanmayınız.</li>
                </ul>
             </section>

             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <ScanEye size={24} className="text-sesgelio"/> 4. GÜVENLİK VE DOXING
                </h3>
                <p>Başka bir kullanıcının gerçek kimliğini, adresini, telefon numarasını veya özel hayatına dair verileri rızası dışında paylaşmak (Doxing), TCK kapsamında bir suçtur ve Sesgelio'da en ağır şekilde cezalandırılır.</p>
             </section>

             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <BrainCircuit size={24} className="text-sesgelio"/> 5. YAPAY ZEKA KULLANIMI
                </h3>
                <p>Sesgelio AI (Gemini) ile etkileşimde bulunurken, yapay zekayı kısıtlamaları aşmaya zorlamak ("jailbreak"), botu suça teşvik etmek veya yanıltıcı bilgilerle manipüle etmek sistemden uzaklaştırılmanıza neden olur.</p>
             </section>

             <section className="space-y-6">
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                    <UserMinus size={24} className="text-sesgelio"/> 6. ŞİKAYET VE YAPTIRIM
                </h3>
                <p>Kuralları ihlal eden bir operatörü fark ederseniz, detaylı kanıtlarla (ekran görüntüsü, ses kaydı linki) <strong>destek@teknodes.com</strong> adresine ulaşabilirsiniz. Yaptırımlar uyarıdan başlar, süreli yasaklama ve kalıcı ban kararına kadar gidebilir.</p>
             </section>
          </LegalPageLayout>
        );

      case 'distance-selling':
        return (
          <LegalPageLayout title="MESAFELİ SATIŞ SÖZLEŞMESİ" lastUpdated="25 Mart 2026" icon={FileWarning}>
             <section className="space-y-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Users size={20} className="text-sesgelio"/> Madde 1 – Taraflar
                </h3>
                <p className="text-gray-600 dark:text-gray-400">İşbu Mesafeli Satış Sözleşmesi ("Sözleşme"); aşağıda bilgileri yer alan Satıcı/Sağlayıcı ile "https://www.sesgelio.com" (veya ilgili alt alan adları/uygulamaları) üzerinden dijital ürün veya hizmet alımı gerçekleştiren Tüketici/Kullanıcı arasında elektronik ortamda onaylanarak kurulmuştur.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">1.1 Satıcı Bilgileri</h4>
                      <ul className="text-sm space-y-2 text-gray-600 dark:text-gray-400">
                          <li><strong className="text-gray-900 dark:text-gray-200">Unvanı:</strong> AYHAN ONBAŞI </li>
                          <li><strong className="text-gray-900 dark:text-gray-200">Adresi:</strong> MERSİNLİ MAH. 2816 SK. NO: 110 KONAK / İZMİR</li>
                          <li><strong className="text-gray-900 dark:text-gray-200">Vergi Dairesi ve No:</strong> KORDON VERGİ DAİRESİ MÜDÜRLÜĞÜ – 6430109741</li>
                          <li><strong className="text-gray-900 dark:text-gray-200">Müşteri Hizmetleri:</strong> destek@teknodes.com</li>
                          <li><strong className="text-gray-900 dark:text-gray-200">Platform:</strong> www.sesgelio.com</li>
                      </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">1.2 Alıcı (Tüketici) Bilgileri</h4>
                      <ul className="text-sm space-y-2 text-gray-600 dark:text-gray-400">
                          <li><strong className="text-gray-900 dark:text-gray-200">Adı/Soyadı/Unvanı:</strong> Sesgelio platformunda ödeme adımında bilgileri alınan ve "Kullanıcı/Operatör" sıfatıyla işlem yapan kişi.</li>
                          <li><strong className="text-gray-900 dark:text-gray-200">Teslimat/Fatura Adresi:</strong> Ödeme esnasında beyan edilen adres.</li>
                          <li><strong className="text-gray-900 dark:text-gray-200">E-Posta Adresi:</strong> Sistemde kayıtlı e-posta adresi.</li>
                      </ul>
                  </div>
                </div>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <FileText size={20} className="text-sesgelio"/> Madde 2 – Sözleşmenin Konusu
                </h3>
                <p className="text-gray-600 dark:text-gray-400">İşbu sözleşmenin konusu, Alıcı’nın Satıcı’ya ait "Sesgelio" platformu (internet sitesi ve/veya mobil uygulama) üzerinden elektronik ortamda siparişini yaptığı, aşağıda nitelikleri ve satış fiyatı belirtilen "Dijital Hizmet, Abonelik, Sanal Ürün ve Lisansların" satışı ve teslimi ile ilgili olarak 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri gereğince tarafların hak ve yükümlülüklerinin saptanmasıdır.</p>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Layers size={20} className="text-sesgelio"/> Madde 3 – Sözleşme Konusu Ürün ve Hizmetler
                </h3>
                <p className="text-gray-600 dark:text-gray-400">Sesgelio platformu üzerinden sunulan hizmetler tamamen dijital ve sanal niteliktedir. Fiziksel bir teslimat yapılmamaktadır. Hizmet kategorileri şunlardır:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-sesgelio text-sm text-gray-600 dark:text-gray-400">
                    <li><strong className="text-gray-900 dark:text-gray-200">Abonelikler:</strong> Sesgelio Premium, Sesgelio Elite (Aylık yenilenen dijital erişim hakları).</li>
                    <li><strong className="text-gray-900 dark:text-gray-200">Sanal Ürünler ve Kozmetikler:</strong> Profil vitrinleri, özel çerçeveler, sunucu kanal ikonları, sunucu ve grup arka planları.</li>
                    <li><strong className="text-gray-900 dark:text-gray-200">Bot Kiralama ve Lisansları:</strong> Sesgelio AI, Sesgelio AudioLink, AegisSentinel, StatMatrix, Nexus Desk gibi platform içi sanal asistan ve otomasyon botlarının aylık kullanım veya süresiz lisans hakları.</li>
                </ul>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Send size={20} className="text-sesgelio"/> Madde 4 – Teslimat Şekli ve İfası
                </h3>
                <ul className="list-disc pl-5 space-y-2 marker:text-sesgelio text-sm text-gray-600 dark:text-gray-400">
                    <li><strong className="text-gray-900 dark:text-gray-200">4.1.</strong> Sözleşme konusu dijital ürün ve hizmetler, ödeme işleminin Sanal POS altyapısı üzerinden başarıyla tamamlanmasının ardından Alıcı'nın Sesgelio hesabına anında ve elektronik ortamda tanımlanır.</li>
                    <li><strong className="text-gray-900 dark:text-gray-200">4.2.</strong> Hizmetin ifası, Alıcı'nın hesabında ilgili dijital yetkilerin (rütbe, yetki, kozmetik veya bot kullanımı) aktif edilmesiyle gerçekleşmiş sayılır.</li>
                </ul>
             </section>

<section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <CreditCard size={20} className="text-sesgelio"/> Madde 5 – Fiyat ve Ödeme Koşulları
                </h3>
                <ul className="list-disc pl-5 space-y-2 marker:text-sesgelio text-sm text-gray-600 dark:text-gray-400">
                    <li><strong className="text-gray-900 dark:text-gray-200">5.1.</strong> Ürün ve hizmetlerin güncel fiyatları, vergiler dâhil olarak ödeme ekranında Alıcı'ya açıkça gösterilir.</li>
                    <li><strong className="text-gray-900 dark:text-gray-200">5.2.</strong> Alıcı, banka veya kredi kartı ile lisanslı Sanal POS (iyzico) üzerinden ödeme yapmayı kabul eder.</li>
                    <li><strong className="text-gray-900 dark:text-gray-200">5.3. Abonelikli Hizmetler ve Otomatik Tahsilat:</strong> Alıcı, aylık yenilenen bir abonelik (Örn: Premium, Elite vb.) satın aldığında; aboneliğin kesintisiz devam edebilmesi için kullandığı kredi/banka kartı bilgilerinin iyzico "Kart Saklama" altyapısında güvenle tutulacağını kabul eder. Alıcı aboneliğini iptal etmediği sürece, her fatura döneminde ilgili tutar bu kayıtlı ödeme yönteminden otomatik olarak tahsil edilir.</li>
                </ul>
             </section>

             <section className="space-y-4 mt-8 bg-red-500/5 p-6 rounded-2xl border border-red-500/20">
                <h3 className="text-xl font-black text-red-500 uppercase italic tracking-tight flex items-center gap-2">
                    <ShieldAlert size={20} /> Madde 6 – Cayma Hakkı ve İstisnaları
                </h3>
                <p className="text-gray-900 dark:text-gray-300"><strong className="text-gray-900 dark:text-white">6.1.</strong> Mesafeli Sözleşmeler Yönetmeliği’nin 15. maddesinin 1. fıkrasının (ğ) bendi gereğince; <strong className="text-red-500">"Elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayrimaddi mallara ilişkin sözleşmelerde" cayma hakkı KULLANILAMAZ.</strong></p>
                <p className="text-gray-900 dark:text-gray-300"><strong className="text-gray-900 dark:text-white">6.2.</strong> Sesgelio platformu üzerinden satın alınan tüm abonelikler, dijital kozmetikler ve bot lisansları anında ifa edilen dijital hizmet statüsünde olduğundan, Alıcı bu alımlarda iade ve cayma hakkı talep edemez. Alıcı, ödeme adımını onayladığında cayma hakkını kaybettiğini peşinen kabul eder.</p>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <AlertTriangle size={20} className="text-sesgelio"/> Madde 7 – Abonelik İptali ve Erişim
                </h3>
                <p className="text-gray-600 dark:text-gray-400"><strong className="text-gray-900 dark:text-gray-200">7.1.</strong> Alıcı, tekrarlayan aboneliklerini Sesgelio platformu içindeki <strong className="text-gray-900 dark:text-white">"Ayarlar &gt; Aboneliklerim"</strong> sekmesinden dilediği zaman iptal etme hakkına sahiptir. Ayrıca Premium ve Elite paketlerini <strong className="text-gray-900 dark:text-white">"Mağaza"</strong> sayfasından ve <strong className="text-gray-900 dark:text-white">"Hesabım"</strong> sayfasından istediği zaman iptal edebilir. Kiralık olan Sesgelio AI ve Sesgelio AudioLink botlarını sunucu içerisinde yer alan <strong className="text-gray-900 dark:text-white">"Sunucu Ayarları &gt; Entegrasyonlar"</strong> sayfası üzerinden de iptal edebilir.</p>
                <p className="text-gray-600 dark:text-gray-400"><strong className="text-gray-900 dark:text-gray-200">7.2.</strong> İptal işlemi, bir sonraki fatura kesim tarihinden itibaren geçerli olur. Alıcı, iptal talebini ilettiği andan mevcut fatura döneminin sonuna kadar ücretini ödediği hizmetten faydalanmaya devam eder. İçinde bulunulan ay için kısmi iade yapılmaz.</p>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <MonitorUp size={20} className="text-sesgelio"/> Madde 8 – Dijital İçerik Özellikleri
                </h3>
                <p className="text-gray-600 dark:text-gray-400">Dijital içeriklerin işlevselliğini etkileyebilecek teknik koruma önlemleri, ilgili sayfalarda belirtilir. İçeriğin çalışması için gerekli olan sistem gereksinimleri açıklanır.</p>
             </section>

             <section className="space-y-4 mt-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Scale size={20} className="text-sesgelio"/> Madde 9 – Uyuşmazlıkların Çözümü
                </h3>
                <p className="text-gray-600 dark:text-gray-400">Kullanıcı, şikâyetlerini <a href="mailto:destek@teknodes.com" className="text-sesgelio hover:underline font-bold">destek@teknodes.com</a> adresine iletebilir. Uyuşmazlık halinde İzmir Tüketici Hakem Heyetleri veya Tüketici Mahkemeleri yetkilidir.</p>
             </section>

             <section className="space-y-4 mt-8 bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5 text-center">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center justify-center gap-2 mb-2">
                    <CheckCircle size={20} className="text-sesgelio"/> Madde 10 – Yürürlük
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 italic">Alıcı, ödeme sayfasında yer alan işbu sözleşmeyi okuduğunu, anladığını ve elektronik ortamda onaylayarak kabul ettiğini beyan eder. Onay işlemi ile birlikte sözleşme yürürlüğe girer.</p>
             </section>
          </LegalPageLayout>
        );
case 'pre-information':
        return (
          <LegalPageLayout title="ÖN BİLGİLENDİRME FORMU" lastUpdated="4 Nisan 2026" icon={Info}>
             <section className="space-y-6">
                <p className="text-sm font-medium border-b border-sesgelio/20 pb-4 text-gray-600 dark:text-gray-400">
                    İşbu form, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca, tüketicinin bilgilendirilmesi amacıyla, elektronik ortamda satın alma işlemi gerçekleştirilmeden önce hazırlanmıştır.
                </p>
                
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight">1. SATICI BİLGİLERİ</h3>
                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm">
                    <ul className="text-sm space-y-2 text-gray-600 dark:text-gray-400 font-medium">
                        <li><strong className="text-gray-900 dark:text-white">Unvan:</strong> AYHAN ONBAŞI - Sesgelio </li>
                        <li><strong className="text-gray-900 dark:text-white">Platform / Marka:</strong> Sesgelio</li>
                        <li><strong className="text-gray-900 dark:text-white">Adres:</strong> Mersinli MAH. 2816 SK. NO: 110 KONAK, İZMİR</li>
                        <li><strong className="text-gray-900 dark:text-white">E-Posta:</strong> destek@teknodes.com </li>
                    </ul>
                </div>

                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight">2. ÜRÜN ÖZELLİKLERİ VE KATEGORİLERİ</h3>
                <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">Alıcı, Sesgelio platformu üzerinden aşağıda detayları belirtilen dijital içerikleri satın almaktadır. Alınan ürünün cinsi, satış bedeli ve kullanım türü sipariş ekranında ayrıca belirtilir:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SUBSCRIPTION */}
                    <div className="p-5 bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-2xl shadow-sm hover:border-sesgelio/50 transition-colors">
                        <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-2 uppercase tracking-widest flex items-center gap-2"><Crown size={16} className="text-sesgelio"/> Abonelik Paketleri</h4>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed">
                            Her ay otomatik yenilenen (iptal edilene kadar) ayrıcalık paketleridir.<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• Premium ve Elite Abonelikleri</strong>
                        </p>
                    </div>

                    {/* BOT */}
                    <div className="p-5 bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-2xl shadow-sm hover:border-sesgelio/50 transition-colors">
                        <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-2 uppercase tracking-widest flex items-center gap-2"><Cpu size={16} className="text-sesgelio"/> Yazılım & Bot Lisansları</h4>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed">
                            Sunuculara entegre edilen dijital asistanlardır.<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• Aylık Kiralık:</strong> Sesgelio AI, RhythmMaster<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• Sınırsız (Tek Seferlik):</strong> Aegis Sentinel, Nöral Matirx XP, Nexus Desk vb.
                        </p>
                    </div>

                    {/* COSMETIC */}
                    <div className="p-5 bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-2xl shadow-sm hover:border-sesgelio/50 transition-colors">
                        <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-2 uppercase tracking-widest flex items-center gap-2"><Star size={16} className="text-sesgelio"/> Kozmetik (Vitrin)</h4>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed">
                            Kullanıcı profiline kalıcı olarak tanımlanan görsel ürünlerdir.<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• İçerikler:</strong> Efsanevi VIP Paketi, Profil Vitrini Serisi (CEO Doge, Gamer Cat, Space Chimp vb.)
                        </p>
                    </div>

                    {/* WALLPAPER & ICONS */}
                    <div className="p-5 bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 rounded-2xl shadow-sm hover:border-sesgelio/50 transition-colors">
                        <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-2 uppercase tracking-widest flex items-center gap-2"><Palette size={16} className="text-sesgelio"/> Görsel Temalar & İkonlar</h4>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed">
                            Sunucu, grup veya kanallar için alınan kalıcı arayüz görselleridir.<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• Arka Planlar:</strong> Kozmik Şafak, Siber Şehir, Özel Yükleme vb.<br/>
                            <strong className="text-gray-700 dark:text-gray-300">• Kanal İkonları:</strong> Elmas VIP, Kraliyet Tacı, Neon Siber Koşu vb.
                        </p>
                    </div>
                </div>

                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight pt-4">3. TOPLAM FİYAT VE ÖDEME</h3>
                <div className="bg-gray-50 dark:bg-white/5 p-5 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        Ürünlerin satış fiyatı, ödeme sayfasında ve banka onay ekranında gösterilen <strong>vergiler dâhil (%20 KDV)</strong> nihai tutardır. Ödemeler kredi kartı/banka kartı ile Sanal POS (3D Secure) üzerinden tahsil edilir. Abonelikli ürünler ve kiralık botlar, iptal edilmediği sürece her ay otomatik olarak kayıtlı ödeme yönteminden yenilenir.
                    </p>
                </div>

                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight pt-4">4. TESLİMAT VE İFA</h3>
                <div className="bg-gray-50 dark:bg-white/5 p-5 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        Satılan tüm ürünler dijital içerik niteliğindedir. Ödeme BDDK lisanslı altyapı üzerinden onaylandığı anda hizmet, Kullanıcı hesabına veya belirtilen sunucuya <strong>anında (elektronik ortamda)</strong> tanımlanır. Fiziki bir kargo veya kurye gönderimi yapılmaz.
                    </p>
                </div>

                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2 pt-4">
                    <ShieldAlert size={20} className="text-red-500"/> 5. CAYMA HAKKI (HAYATİ UYARI)
                </h3>
                <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-2xl space-y-4">
                    <p className="text-sm font-black text-red-500 dark:text-red-400 uppercase tracking-widest">DİKKAT: İade ve Cayma Hakkı Bulunmamaktadır.</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed italic font-medium">
                        "Mesafeli Sözleşmeler Yönetmeliği Madde 15/1-ğ uyarınca; elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayrimaddi mallara ilişkin sözleşmelerde <strong>Cayma Hakkı kullanılamaz.</strong>"
                    </p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                        Sesgelio'dan aldığınız tüm abonelik, bot, kozmetik ve görsel içerikler bu kapsamdadır. Ödeme yapıp satın alma işlemini onayladığınız anda ifa (teslimat) anında gerçekleştiği için iade yapılamayacağını gayrikabili rücu kabul etmiş sayılırsınız.
                    </p>
                </div>

                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight pt-4">6. ŞİKAYET VE ÇÖZÜM</h3>
                <div className="bg-gray-50 dark:bg-white/5 p-5 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        Satın aldığınız ürünlerle ilgili yaşayabileceğiniz teknik aksaklıklar veya her türlü şikayetiniz için <strong>destek@teknodes.com</strong> adresi üzerinden bizimle iletişime geçebilirsiniz. Uyuşmazlık halinde İzmir Tüketici Hakem Heyetleri ve Tüketici Mahkemeleri yetkilidir. İşbu Ön Bilgilendirme Formu'nu elektronik ortamda onaylayarak satın alma işlemine geçebilirsiniz.
                    </p>
                </div>
             </section>
          </LegalPageLayout>
        );

       case 'refund-policy':
        return (
          <LegalPageLayout title="İPTAL VE İADE KOŞULLARI" lastUpdated="4 Nisan 2026" icon={History}>
             
             {/* GİRİŞ BÖLÜMÜ */}
             <section className="space-y-4">
                <p className="border-l-4 border-sesgelio/50 pl-6 italic bg-gray-50 dark:bg-white/5 py-4 rounded-r-xl text-sm text-gray-700 dark:text-gray-300">
                    <strong className="text-gray-900 dark:text-white">AYHAN ONBAŞI - Sesgelio </strong> (Bundan böyle "Şirket" veya "Sesgelio" olarak anılacaktır) olarak, Sesgelio platformunda en yüksek kalitede hizmeti sunmayı hedefleriz. Ancak, sunduğumuz hizmetlerin tamamının "Dijital İçerik" ve "Anında İfa Edilen Hizmet" niteliğinde olması sebebiyle yasal iade sınırlamaları uygulamaktayız.
                </p>
             </section>

             {/* 1. CAYMA HAKKI İSTİSNASI */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <ShieldAlert size={20} className="text-red-500"/> 1. CAYMA HAKKI İSTİSNASI (DİKKAT)
                </h3>
                
                <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl relative overflow-hidden shadow-sm">
                    <div className="absolute top-0 right-0 p-4 opacity-10"><AlertTriangle size={60} className="text-red-500"/></div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium relative z-10">
                        6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği Madde 15/1-ğ bendi uyarınca; <strong className="text-red-600 dark:text-red-400 uppercase">"Elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayrimaddi mallara ilişkin sözleşmelerde CAYMA HAKKI KULLANILAMAZ."</strong>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-4 relative z-10">
                        Sesgelio platformu üzerinden gerçekleştirdiğiniz satın alımlar anında hesabınıza tanımlanıp tüketilebilir hale geldiğinden, sipariş onaylandığı andan itibaren yasal olarak iptal ve iade hakkınız bulunmamaktadır.
                    </p>
                </div>
             </section>

             {/* 2. İADE KAPSAMI DIŞINDAKİ ÜRÜNLER */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Ban size={20} className="text-sesgelio"/> 2. İADE KAPSAMI DIŞINDAKİ ÜRÜNLERİMİZ
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Aşağıdaki ürün ve hizmetlerin lisans tanımlaması yapıldıktan sonra iptali ve ücret iadesi kesinlikle mümkün değildir:</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-xl flex items-center gap-3 shadow-sm">
                        <div className="w-10 h-10 bg-purple-500/10 text-purple-500 rounded-lg flex items-center justify-center shrink-0"><Crown size={18}/></div>
                        <div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Abonelikler (SUBSCRIPTION)</h4>
                            <p className="text-xs text-gray-500">Sesgelio Premium ve Elite paketleri.</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-xl flex items-center gap-3 shadow-sm">
                        <div className="w-10 h-10 bg-blue-500/10 text-blue-500 rounded-lg flex items-center justify-center shrink-0"><Cpu size={18}/></div>
                        <div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Bot Hizmetleri (BOT)</h4>
                            <p className="text-xs text-gray-500">Sesgelio AI, Sesgelio AudioLink ve diğer asistanlar.</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-xl flex items-center gap-3 shadow-sm">
                        <div className="w-10 h-10 bg-yellow-500/10 text-yellow-500 rounded-lg flex items-center justify-center shrink-0"><Palette size={18}/></div>
                        <div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Kozmetik Ürünler (COSMETIC)</h4>
                            <p className="text-xs text-gray-500">Profil Vitrini, Çerçeveler, ikonlar ve Efsanevi VIP paketleri.</p>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-4 rounded-xl flex items-center gap-3 shadow-sm">
                        <div className="w-10 h-10 bg-green-500/10 text-green-500 rounded-lg flex items-center justify-center shrink-0"><Monitor size={18}/></div>
                        <div>
                            <h4 className="font-bold text-gray-900 dark:text-white text-sm">Arayüz Temaları (WALLPAPER)</h4>
                            <p className="text-xs text-gray-500">Sunucu ve grup arka planları (Dijital görseller).</p>
                        </div>
                    </div>
                </div>
             </section>

             {/* 3. ABONELİK İPTALİ */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <RefreshCw size={20} className="text-sesgelio"/> 3. ABONELİK İPTALİ VE YENİLEME
                </h3>
                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 rounded-2xl space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">Otomatik Yenileme:</strong> Aylık Abonelikler (Premium/Elite) ve kiralık bot hizmetleri, siz iptal etmediğiniz sürece her fatura döneminde (30 günde bir) kayıtlı ödeme yönteminizden otomatik olarak yenilenir.
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">İptal İşlemi:</strong> Aboneliğinizi, Sesgelio "Ayarlar &gt; Aboneliklerim" paneli üzerinden dilediğiniz zaman iptal edebilirsiniz. İptal işlemi gerçekleştirdiğinizde, halihazırda ödemesini yaptığınız dönemin sonuna kadar tüm ayrıcalıklarınızı kullanmaya devam edersiniz.
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong className="text-sesgelio uppercase">Geçmişe Dönük İade Yok:</strong> Dönem sonunda hesabınız standart sürüme düşer ve yeni çekim yapılmaz. Ancak, <strong className="text-gray-900 dark:text-white">kullanılmayan günler için geçmişe dönük kısmi iade (prorated refund) KESİNLİKLE YAPILMAZ.</strong>
                    </p>
                </div>
             </section>

             {/* 4. TEKNİK AKSAKLIKLAR */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Cpu size={20} className="text-sesgelio"/> 4. İSTİSNAİ DURUMLAR (TEKNİK AKSAKLIKLAR)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Kullanıcıdan kaynaklanmayan, tamamen Sesgelio altyapısına bağlı majör bir arıza sebebiyle (örn: satın alınan bir botun sunucuya teknik nedenlerle eklenememesi) hizmetin ödeme yapıldıktan sonraki <strong>48 saat içerisinde</strong> teslim edilememesi durumunda; Kullanıcı destek ekibimizle iletişime geçebilir. Teknik ekibin incelemesi sonucu sorunun bizden kaynaklandığı ve çözülemeyeceği tespit edilirse, 7 iş günü içerisinde ücret iadesi gerçekleştirilir.
                </p>
             </section>

             {/* 5. BAN / İHLAL DURUMU */}
             <section className="space-y-4 pt-4">
                <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                    <Gavel size={20} className="text-sesgelio"/> 5. SÖZLEŞME İHLALİ VE KÖTÜYE KULLANIM
                </h3>
                <div className="bg-[#0a0a0c] border-l-4 border-sesgelio p-5 rounded-r-2xl shadow-sm">
                    <p className="text-sm text-gray-300 leading-relaxed font-medium">
                        Kullanıcının, Sesgelio Kullanıcı Sözleşmesi'ne veya Topluluk Kuralları'na aykırı davranması (Siber zorbalık, hile, bot sistemlerini suistimal etme, TCK ihlali vb.) sebebiyle hesabının geçici olarak askıya alınması veya kalıcı olarak silinmesi durumunda, aktif abonelikleri iptal edilir ve <strong className="text-sesgelio uppercase tracking-widest">hiçbir surette ücret iadesi yapılmaz.</strong>
                    </p>
                </div>
             </section>

          </LegalPageLayout>
        );

case 'safe-shopping':
        return (
          <LegalPageLayout title="GÜVENLİ ALIŞVERİŞ PROTOKOLÜ" lastUpdated="25 Mart 2026" icon={ShieldCheck}>
             <section className="space-y-10">
                <p className="text-sm font-medium border-l-4 border-sesgelio pl-4 py-1 italic">
                    Sesgelio (Sesgelio) olarak, operatörlerimizin nöral güvenliği ve finansal huzuru bizim için en öncelikli konudur. Platformumuzda gerçekleştirdiğiniz her işlem, uluslararası siber güvenlik standartları ve Sesgelio güvencesiyle korunmaktadır.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Operatörün Yapabilecekleri */}
                    <div className="space-y-6">
                        <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                            <UserCheck size={20} className="text-sesgelio"/> SİZİN YAPABİLECEKLERİNİZ
                        </h3>
                        <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-3xl border border-gray-100 dark:border-white/5 space-y-4">
                            <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Hesap Zırhı</h4>
                                <ul className="text-xs text-gray-500 space-y-2 leading-relaxed">
                                    <li>• Şifrenizi karmaşık ve Sesgelio'ya özel oluşturun.</li>
                                    <li>• <strong>İki Faktörlü Doğrulamayı (2FA)</strong> mutlaka aktif tutun.</li>
                                    <li>• Hesap bilgilerinizi veya şifrenizi kimseyle (yönetim dahil) paylaşmayın.</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Donanım Güvenliği</h4>
                                <ul className="text-xs text-gray-500 space-y-2 leading-relaxed">
                                    <li>• Bilinmeyen bağlantılara tıklamayın, şüpheli eklentiler indirmeyin.</li>
                                    <li>• Ortak cihazlardan giriş yaptıysanız mutlaka güvenli çıkış yapın.</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Bizim Yaptıklarımız */}
                    <div className="space-y-6">
                        <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-2">
                            <Cpu size={20} className="text-sesgelio"/> BİZİM ALDIĞIMIZ ÖNLEMLER
                        </h3>
                        <div className="bg-sesgelio/5 p-6 rounded-3xl border border-sesgelio/10 space-y-4">
                            <div className="flex items-start gap-3">
                                <CreditCard size={18} className="text-sesgelio mt-1"/>
                                <div>
                                    <h4 className="text-sm font-bold text-white">PCI DSS & 3D Secure</h4>
                                    <p className="text-[11px] text-gray-400">Ödemeleriniz, BDDK lisanslı ödeme kuruluşları üzerinden PCI DSS standartlarında ve zorunlu 3D Secure doğrulamasıyla işlenir. Kart bilgileriniz Sesgelio sunucularında asla saklanmaz.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Lock size={18} className="text-sesgelio mt-1"/>
                                <div>
                                    <h4 className="text-sm font-bold text-white">SSL/TLS Şifreleme</h4>
                                    <p className="text-[11px] text-gray-400">Tüm veri iletimi 256-bit SSL sertifikası ile şifrelenir. Kişisel verilerinizin ve ses verilerinizin üçüncü şahıslar tarafından izlenmesi imkansızdır.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Database size={18} className="text-sesgelio mt-1"/>
                                <div>
                                    <h4 className="text-sm font-bold text-white">KVKK ve Veri Gizliliği</h4>
                                    <p className="text-[11px] text-gray-400">Verileriniz sadece hizmetin sağlanması amacıyla işlenir ve yetkisiz üçüncü taraflarla paylaşılmaz. Tüm süreçlerimiz 6698 sayılı KVKK ile tam uyumludur.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-8 bg-gray-900 rounded-[2.5rem] border border-white/5 relative overflow-hidden group">
                    <div className="absolute inset-0 bg-sesgelio/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="text-center md:text-left">
                            <h3 className="text-2xl font-black text-white uppercase italic mb-2">GÜVENLİK BİLDİRİMİ</h3>
                            <p className="text-gray-400 text-sm max-w-md">
                                Hesabınızda olağan dışı bir hareket fark ederseniz veya ödeme süreciyle ilgili bir şüpheniz oluşursa, saniyeler içinde bizimle iletişime geçin.
                            </p>
                        </div>
                        <a href="mailto:destek@teknodes.com" className="bg-white text-black px-8 py-4 rounded-2xl font-black text-sm hover:scale-105 transition-all shadow-xl">
                            DESTEK@teknodes.com
                        </a>
                    </div>
                </div>

                <p className="text-center text-[10px] text-gray-500 uppercase tracking-[0.3em]">
                    Sesgelio Güvenlik Altyapısı Sesgelio Tarafından Denetlenmektedir
                </p>
             </section>
          </LegalPageLayout>
        );



  case 'about':
        return (
          <div className="w-full flex flex-col min-h-screen bg-[#050505] relative overflow-x-hidden transition-colors [perspective:2000px]">
            {/* 🌌 DİNAMİK 3D ARKA PLAN */}
            <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,43,94,0.1)_0%,transparent_70%)] animate-pulse" style={{ animationDuration: '4s' }}></div>
              <div className="absolute top-[20%] right-[10%] w-[40vw] h-[40vw] bg-purple-600/20 rounded-full blur-[120px] animate-bounce" style={{ animationDuration: '8s' }}></div>
              <div className="absolute bottom-[10%] left-[5%] w-[50vw] h-[50vw] bg-blue-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
              {/* 3D Grid */}
              <div className="absolute bottom-0 left-0 right-0 h-[50vh] bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:60px_60px] [transform:rotateX(75deg)_translateY(200px)_scale(3)] opacity-40 origin-bottom"></div>
            </div>

            <div className="flex-1 relative z-10 w-full max-w-7xl mx-auto px-6 py-24 flex flex-col items-center">

              {/* 🚀 3D KART TASARIMI - ANA İÇERİK */}
              <div className="flex flex-col lg:flex-row items-center justify-center gap-16 w-full mt-10 mb-32 relative">

             {/* 3D Model Placeholder & Görsel Sölen */}
                <div className="w-full lg:w-1/2 flex justify-center items-center relative h-[350px] md:h-[500px] [perspective:1000px]">
                  <div className="absolute inset-0 bg-gradient-to-tr from-sesgelio/20 to-purple-600/20 rounded-full blur-[80px] animate-[spin_15s_linear_infinite]"></div>

                  {/* Katmanlı 3D Elementler */}
                  <div
                    className="relative z-10 w-72 h-72 md:w-96 md:h-96 bg-black/40 backdrop-blur-xl border border-white/10 rounded-[3rem] shadow-[0_0_50px_rgba(255,43,94,0.2)] flex items-center justify-center transform transition-all duration-700 hover:scale-105 hover:[transform:rotateY(15deg)_rotateX(10deg)]"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    <div className="absolute inset-0 rounded-[3rem] border border-sesgelio/40 transform [translateZ(-40px)]"></div>
                    <div className="absolute inset-0 rounded-[3rem] border border-purple-500/40 transform [translateZ(40px)]"></div>

                    <img
                      src="/hakkimizda-logo.png"
                      alt="Sesgelio 3D Hologram"
                      className="w-48 h-48 md:w-72 md:h-72 object-contain drop-shadow-[0_20px_30px_rgba(255,43,94,0.6)] transform [translateZ(80px)] animate-[pulse_3s_ease-in-out_infinite]"
                    />

                    {/* Orbiting Elements */}
                    <div className="absolute w-full h-full animate-[spin_8s_linear_infinite]" style={{ transformStyle: 'preserve-3d' }}>
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-8 h-8 bg-sesgelio rounded-full shadow-[0_0_20px_#ff2b5e] transform [translateZ(100px)]"></div>
                    </div>
                    <div className="absolute w-[130%] h-[130%] border border-dashed border-white/20 rounded-full animate-[spin_20s_linear_infinite_reverse]"></div>
                  </div>
                </div>

                {/* Metin ve İçerik */}
                <div className="w-full lg:w-1/2 space-y-8 text-center lg:text-left z-20">
                  <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-md text-white px-6 py-3 rounded-full text-xs font-black uppercase tracking-[0.4em] border border-white/10 shadow-xl transform transition-all hover:[transform:translateZ(10px)] cursor-default">
                    <Orbit className="text-sesgelio animate-[spin_4s_linear_infinite]" size={16} /> BİR İLETİŞİM DEVRİMİ
                  </div>

<h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] tracking-tighter uppercase italic drop-shadow-2xl">
    SESSİZLİĞİ <br /> <span className="text-sesgelio animate-pulse">PARÇALA.</span>
</h1>

<div className="text-lg text-gray-400 font-medium leading-relaxed space-y-6">
                    <p className="transform transition-transform hover:translate-x-2">
                      Küresel servislerin erişim kısıtlamaları ve kopuk iletişim ağları arasında, kendi dijital sesimizi inşa etme vakti gelmişti. Bizler, aramızdaki görünmez duvarları yıkmak için buradayız. Sesgelio; sadece sıradan bir mesajlaşma aracı değil, oyun oynarken, müzik dinlerken veya en derin sohbetleri ederken kilometrelerce uzaktaki dostlarınla tam olarak 'aynı odadaymışsın' hissini veren yerli bir iletişim ekosistemidir.
                    </p>
                    <p className="transform transition-transform hover:translate-x-2">
                      Biz seni '4K 60FPS' veya '256kbps' gibi karmaşık teknik terimlere boğmuyoruz. Sen sadece anın tadını çıkar diye; fısıltılarının bile anında iletildiği, yapay zekanın seninle gerçek bir bağ kurduğu ve hiçbir pürüzün olmadığı bir altyapı inşa ettik. Burası sınırların kalktığı, kurallarını senin koyduğun kendi dijital sığınağın.
                    </p>
<div className="p-6 bg-gradient-to-r from-sesgelio/10 to-transparent border-l-4 border-sesgelio rounded-r-3xl transform transition-all duration-500 hover:scale-[1.02] hover:bg-sesgelio/20">
                      <p className="italic text-white">
                        "Teknolojiyi hissetme, sadece anı yaşa. Türkiye'nin sesi artık çok daha gür çıkıyor."
                      </p>
                    </div>

                    {/* 🚀 KURUMSAL GÜÇ VE İŞTİRAK KARTI */}
                    <div className="mt-8 relative group cursor-default">
                      <div className="absolute inset-0 bg-blue-500/10 blur-xl rounded-3xl group-hover:bg-blue-500/20 transition-all duration-500"></div>
                      <div className="relative p-6 sm:p-8 bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 hover:border-blue-500/50 transition-colors duration-500">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center text-blue-400 shrink-0 border border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                          <Building2 size={28} />
                        </div>
                        <div>
                          <h4 className="text-white font-black text-lg md:text-xl tracking-tight mb-2 flex items-center gap-2 uppercase italic">
                            Teknodes İştiraki & Güvencesi
                          </h4>
                          <p className="text-gray-400 text-sm leading-relaxed font-medium">
                            Sesgelio, kurumsal ERP ve e-dönüşüm sektörünün köklü isimlerinden, <strong className="text-blue-400">Logo Yazılım İş Ortağı Teknodes</strong>'in vizyoner bir teknoloji iştirakidir. Arkasındaki bu kurumsal ticari güç ve muhasebe disiplini, Sesgelio'nun altyapısındaki güvenilirlik ve sürdürülebilirliğin en büyük teminatıdır.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>

              {/* 📊 3D İSTATİSTİKLER KARTLARI */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full mb-32 [perspective:1000px]">
                {[
                  { value: '12ms', label: 'Ultra Düşük Gecikme', icon: Zap, color: 'text-sesgelio', bg: 'bg-sesgelio/10', border: 'border-sesgelio/30', glow: 'shadow-[0_0_40px_rgba(255,43,94,0.2)]' },
                  { value: '2.5', label: 'Sesgelio Neural Core', icon: BrainCircuit, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', glow: 'shadow-[0_0_40px_rgba(168,85,247,0.2)]' },
                  { value: '%100', label: 'Türkiye Altyapısı', icon: Server, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', glow: 'shadow-[0_0_40px_rgba(59,130,246,0.2)]' }
                ].map((stat, i) => (
                  <div key={i} className={`relative group p-10 bg-black/60 backdrop-blur-xl border ${stat.border} rounded-[2.5rem] text-center transform transition-all duration-500 hover:-translate-y-4 hover:[transform:rotateX(10deg)_rotateY(-5deg)] ${stat.glow} overflow-hidden`}>
                    <div className={`absolute -right-10 -top-10 w-40 h-40 ${stat.bg} rounded-full blur-[50px] group-hover:scale-150 transition-transform duration-700`}></div>
                    <div className={`w-20 h-20 mx-auto rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center mb-8 ${stat.color} relative z-10 transform group-hover:rotate-[360deg] transition-all duration-1000 shadow-xl`}>
                      <stat.icon size={40} />
                    </div>
                    <h3 className="text-5xl font-black text-white mb-3 tracking-tighter relative z-10 drop-shadow-lg group-hover:scale-110 transition-transform duration-500">{stat.value}</h3>
                    <p className={`text-sm font-bold uppercase tracking-widest ${stat.color} relative z-10 group-hover:tracking-[0.2em] transition-all duration-500`}>{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* 🚀 DEVASA VE FARK EDİLİR YÖNLENDİRME BUTONU */}
              <div className="w-full max-w-5xl mx-auto flex flex-col items-center justify-center pb-20 relative text-center">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sesgelio/10 to-transparent blur-[80px] pointer-events-none"></div>

                <div className="relative z-10 bg-gradient-to-b from-white/5 to-transparent border border-white/10 p-12 md:p-16 rounded-[4rem] backdrop-blur-2xl shadow-[0_0_80px_rgba(255,43,94,0.15)] flex flex-col items-center w-full transform transition-all duration-500 hover:border-sesgelio/50 hover:shadow-[0_0_100px_rgba(255,43,94,0.3)]">
                  <div className="w-24 h-24 mb-8 bg-sesgelio/20 rounded-full flex items-center justify-center animate-pulse border border-sesgelio/40">
                    <Zap size={48} className="text-sesgelio" />
                  </div>

                  <h2 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.4)]">
                    SİSTEMİN KALBİNE İN
                  </h2>
                  <p className="text-gray-400 mb-12 max-w-2xl mx-auto text-lg md:text-xl font-medium">
                    Bütün bu özelliklerin arkasındaki kusursuz mimariyi keşfet. Sistem durumunu ve teknolojik manifestomuzu kendi gözlerinle gör.
                  </p>

                  <button
                    onClick={() => { setCurrentPage('system'); scrollContainerRef.current?.scrollTo(0, 0); }}
                    className="group relative flex items-center justify-center gap-6 bg-white text-black px-10 py-6 md:px-16 md:py-8 rounded-[3rem] font-black text-2xl md:text-3xl uppercase tracking-[0.2em] transform transition-all duration-500 hover:scale-110 shadow-[0_0_80px_rgba(255,255,255,0.5)] overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-sesgelio opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <span className="relative z-10 group-hover:text-white transition-colors duration-300">SİSTEMİ İNCELE</span>
                    <div className="relative z-10 w-14 h-14 bg-black text-white group-hover:bg-white group-hover:text-sesgelio rounded-full flex items-center justify-center transition-colors duration-300 transform group-hover:translate-x-3 shadow-lg">
                      <ArrowRight size={28} />
                    </div>
                  </button>
                </div>
              </div>

            </div>

            <Footer />
          </div>
        );

      case 'contact':
        return (
          <div className="w-full flex flex-col min-h-screen bg-[#f8fafc] dark:bg-[#020202] transition-colors overflow-x-hidden">
            {/* Ambient background for light mode */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-sesgelio/5 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[100px]"></div>
            </div>

            <div className="flex-1 w-full max-w-7xl mx-auto px-6 py-20 relative z-10 flex flex-col items-center">
                {/* Header Section */}
               <div className="text-center mb-12 md:mb-20 animate-fade-in">
                    <div className="inline-flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-6 py-2 rounded-full text-sesgelio font-black text-[10px] uppercase tracking-widest shadow-sm mb-6">
                        <Headset size={14} /> SESGELIO DESTEK MERKEZİ
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-gray-900 dark:text-white tracking-tighter uppercase italic leading-none mb-6">
                        SESİNE <span className="text-sesgelio">KULAK VERİYORUZ.</span>
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-xl font-medium max-w-2xl mx-auto leading-relaxed">
                        Topluluğumuzun her bir üyesi bizim için değerlidir. Bir sorunun, önerin mi var? <br className="hidden md:block"/> 
                        Yoksa sadece "merhaba" mı demek istedin? Hemen bize ulaş.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 w-full">
                    {/* Left Column: Information Cards */}
                    <div className="lg:col-span-5 space-y-6 animate-fade-in-up">
                        <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-white/5 p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 dark:shadow-none hover:border-sesgelio transition-all duration-500 group">
                            <div className="w-14 h-14 bg-sesgelio/10 rounded-2xl flex items-center justify-center text-sesgelio mb-6 group-hover:scale-110 transition-transform">
                                <MessagesSquare size={28} />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase mb-2">Hızlı Destek</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">Sunucu kurulumu, hesap güvenliği veya teknik sorunlar için 7/24 yanındayız.</p>
                            <div className="flex flex-col gap-2">
                                <a href="mailto:destek@teknodes.com" className="font-bold text-gray-900 dark:text-white hover:text-sesgelio transition-colors flex items-center gap-2">
                                    <Mail size={16}/> destek@teknodes.com
                                </a>
                                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 dark:bg-white/5 px-2 py-1 rounded w-fit">Ortalama Yanıt: 2 Saat</div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-white/5 p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 dark:shadow-none hover:border-blue-500 transition-all duration-500 group">
                            <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500 mb-6 group-hover:scale-110 transition-transform">
                                <Building2 size={28} />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase mb-2">Kurumsal & İş Birliği</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">Marka iş birlikleri, sponsorluklar ve kurumsal talepler için ekibimizle iletişime geçin.</p>
                            <div className="flex flex-col gap-2">
                                <a href="mailto:kurumsal@Sesgelio.com" className="font-bold text-gray-900 dark:text-white hover:text-blue-500 transition-colors flex items-center gap-2">
                                    <Mail size={16}/> kurumsal@teknodes.com
                                </a>
                            </div>
                        </div>

                        <div className="p-8 bg-gray-900 dark:bg-white text-white dark:text-black rounded-[2.5rem] flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 dark:bg-black/10 rounded-xl flex items-center justify-center">
                                    <PhoneCall size={24} />
                                </div>
                                <div>
                                    <div className="font-black uppercase text-sm italic">ACİL DURUM HATTI</div>
                                    <div className="text-lg font-black tracking-tighter">(0232) 462 62 61</div>
                                </div>
                            </div>
                            <button className="bg-sesgelio text-white px-6 py-3 rounded-xl font-bold text-sm hover:scale-105 active:scale-95 transition-all shadow-lg shadow-sesgelio/20">Hemen Ara</button>
                        </div>
                    </div>

                    {/* Right Column: Contact Form */}
                    <div className="lg:col-span-7 animate-fade-in-up delay-100">
                        <div className="bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-white/5 p-8 md:p-12 rounded-[3rem] shadow-2xl shadow-gray-200/50 dark:shadow-none backdrop-blur-xl h-full relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                <Send size={200} />
                            </div>
                            
                            <h3 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tighter italic mb-8 flex items-center gap-4">
                                <div className="w-1.5 h-8 bg-sesgelio rounded-full"></div> MESAJINI BIRAK
                            </h3>

                            <form className="space-y-6 relative z-10" onSubmit={handleContactSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-2">ADINIZ VE SOYADINIZ</label>
                                        <div className="relative group">
                                            <div className="absolute inset-0 bg-sesgelio/10 rounded-2xl blur-md opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                                           <input 
  type="text" 
  required
  className="w-full bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl p-4 text-gray-900 dark:text-white focus:border-sesgelio focus:ring-1 focus:ring-sesgelio outline-none transition-all relative z-10 font-bold"
  placeholder="Örn: Ahmet Yılmaz"
  value={contactName}
  onChange={(e) => setContactName(e.target.value)}
/>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-2">E-POSTA ADRESİNİZ</label>
                                        <div className="relative group">
                                            <div className="absolute inset-0 bg-sesgelio/10 rounded-2xl blur-md opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                                            <input 
                                                type="email" 
  required
  className="w-full bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl p-4 text-gray-900 dark:text-white focus:border-sesgelio focus:ring-1 focus:ring-sesgelio outline-none transition-all relative z-10 font-bold"
  placeholder="ahmet@posta.com"
  value={contactEmail}
  onChange={(e) => setContactEmail(e.target.value)}
/>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-2">KONU BAŞLIĞI</label>
                                    <div className="relative group">
                                        <div className="absolute inset-0 bg-sesgelio/10 rounded-2xl blur-md opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                                        <select 
                                          className="w-full bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl p-4 text-gray-900 dark:text-white focus:border-sesgelio focus:ring-1 focus:ring-sesgelio outline-none transition-all relative z-10 font-bold appearance-none cursor-pointer"
                                          value={contactSubject}
                                          onChange={(e) => setContactSubject(e.target.value)}
                                        >
                                          {/* 🚀 SİBER DÜZELTME: Option etiketlerine sabit arka plan ve yazı rengi verildi. Asla silikleşmez! */}
                                          <option value="Genel Sorular" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold">Genel Sorular</option>
                                          <option value="Üyelik ve Ödeme Sorunları" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold">Üyelik ve Ödeme Sorunları</option>
                                          <option value="Bot / API Destek" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold">Bot / API Destek</option>
                                          <option value="Şikayet ve Bildirim" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold">Şikayet ve Bildirim</option>
                                          <option value="Diğer" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold">Diğer</option>
                                        </select>
                                        
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 z-10 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-2">MESAJINIZ</label>
                                    <div className="relative group">
                                        <div className="absolute inset-0 bg-sesgelio/10 rounded-2xl blur-md opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                                        <textarea 
                                            rows={5} 
                                            className="w-full bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl p-4 text-gray-900 dark:text-white focus:border-sesgelio focus:ring-1 focus:ring-sesgelio outline-none transition-all relative z-10 resize-none font-medium leading-relaxed"
                                            placeholder="Sorunu detaylıca buraya yazabilirsin..."
                                            value={contactMessage}
  onChange={(e) => setContactMessage(e.target.value)}
                                        ></textarea> 
                                    </div>
                                </div>

                              {/* --- 🤖 RECAPTCHA --- */}
                                <div className="w-full flex justify-center my-4 overflow-hidden">
                                  <div className="inline-block transform scale-[0.75] sm:scale-90 md:scale-100 origin-center transition-transform">
                                    <SiberCaptcha onVerify={(token) => setContactCaptchaToken(token)} />
                                  </div>
                                </div>
                                <button className="w-full bg-sesgelio hover:bg-sesgelio-dark text-white font-black text-xl py-5 rounded-2xl transition-all transform hover:scale-[1.02] active:scale-95 shadow-2xl shadow-sesgelio/30 flex items-center justify-center gap-3 group/btn">
                                    MESAJI GÖNDER 
                                    <Send size={24} className="group-hover/btn:translate-x-2 group-hover/btn:-translate-y-1 transition-transform" />
                                </button>
                            </form>

                            <div className="mt-8 pt-8 border-t border-gray-100 dark:border-white/5 flex items-center justify-center gap-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-700">
                                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div><span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">SSL SECURE</span></div>
                                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div><span className="text-[9px] font-black text-gray-500 uppercase tracking-widest">KVKK COMPLIANT</span></div>
                            </div>
                        </div>
                    </div>
                </div>

{/* --- 🚀 YENİ EKLENEN: BTK YER SAĞLAYICI VE YASAL İLETİŞİM KARTI --- */}
                <div className="w-full mt-8 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
                    <div className="flex flex-col md:flex-row items-center text-center md:text-left p-6 md:p-8 bg-white dark:bg-gray-900/30 rounded-3xl border border-gray-100 dark:border-white/5 shadow-sm hover:border-sesgelio/30 transition-colors group">
                        <div className="w-16 h-16 bg-sesgelio/10 rounded-2xl flex items-center justify-center text-sesgelio mb-6 md:mb-0 md:mr-8 shrink-0 group-hover:scale-110 transition-transform">
                            <Building2 size={32} />
                        </div>
                        <div className="flex-1 space-y-3">
                            <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-lg md:text-xl">Yasal İletişim ve Yer Sağlayıcı Bilgileri</h4>
                            <div className="text-sm text-gray-500 leading-relaxed space-y-2">
                                <p className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                                    <strong className="text-gray-700 dark:text-gray-300">Yer Sağlayıcı (Gerçek Kişi):</strong> 
                                    <span className="text-gray-900 dark:text-white font-medium">AYHAN ONBAŞI</span>
                                </p>
                                <p className="flex flex-col md:flex-row md:items-start gap-1 md:gap-2">
                                    <strong className="text-gray-700 dark:text-gray-300 shrink-0">Açık Posta Adresi:</strong> 
                                    <span className="text-gray-900 dark:text-white font-medium">Mersinli Mah. 2816 Sk. No: 110 Konak, İzmir / Türkiye</span>
                                </p>
                                <p className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                                    <strong className="text-gray-700 dark:text-gray-300">E-Posta:</strong> 
                                    <span className="text-gray-900 dark:text-white font-medium">omeronbasi2004@gmail.com</span>
                                </p>
                                <p className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                                    <strong className="text-gray-700 dark:text-gray-300">KEP Adresi:</strong> 
                                    <span className="text-gray-900 dark:text-white font-medium">ayhan.onbasi@hs01.kep.tr</span>
                                </p>
                                <p className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                                    <strong className="text-gray-700 dark:text-gray-300">Telefon:</strong> 
                                    <span className="text-gray-900 dark:text-white font-medium">0 553 986 10 20</span>
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 md:mt-0 md:ml-6 shrink-0">
                            <div className="text-[10px] font-black uppercase tracking-widest text-sesgelio bg-sesgelio/10 px-4 py-2 rounded-xl border border-sesgelio/20 shadow-sm flex items-center gap-2">
                                <ShieldCheck size={14} /> Resmi Bildirim
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
          </div>
        );

     case 'nitro':
         return (
           <div className="w-full flex flex-col min-h-screen">
             <div className="w-full pt-28 md:pt-32 pb-12 px-6 flex flex-col items-center bg-gray-50 dark:bg-black">
                <div className="text-center max-w-2xl mb-12 animate-fade-in-up">
                  <h1 className="text-5xl font-black text-gray-900 dark:text-white mb-4 uppercase tracking-tighter italic">SESGELİO <span className="text-sesgelio">EKOSİSTEMİ</span></h1>
                   <p className="text-xl text-gray-500 dark:text-gray-400">Sıradanlığı gölgelerde bırakın, geleceğin parlayan teknolojisine dahil olun.</p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl w-full mb-24">
                   <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-8 flex flex-col shadow-xl hover:scale-105 transition-transform">
                     <Ghost size={20} className="text-gray-400 group-hover:text-gray-500 transition-colors"/>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Başlangıç</h3>
                      <div className="text-3xl font-black text-gray-900 dark:text-white mb-6">₺0 <span className="text-sm font-medium text-gray-500">/ay</span></div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase -mt-3 mb-6 tracking-tighter italic">"SIRADAN BİR KULLANICI OLARAK KALMAK İSTEYENLER İÇİN."</p>
                     <ul className="space-y-4 mb-8 flex-1">
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Check size={20} className="text-green-500" /> Standart Ses Kalitesi (64kbps)</div></li>
                           <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Check size={20} className="text-green-500" /> Standart Yayın Kalitesi (720p 30 fps)</div></li>
                           <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Check size={20} className="text-green-500" /> 50MB Dosya Yükleme Limiti</div></li>
                           <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Check size={20} className="text-green-500" /> Renksiz Profil</div></li>
                         </ul>
                      <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Gölge Olarak Kal</button>
                   </div>

                  <div className="bg-gradient-to-b from-gray-100 to-white dark:from-gray-800 dark:to-black border-2 border-sesgelio rounded-3xl p-8 flex flex-col relative transform md:scale-110 shadow-2xl shadow-sesgelio/20 z-10">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-sesgelio text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-lg animate-pulse text-center w-max">                          <Sparkles size={12} fill="currentColor" /> RENGİNİ BELLİ ET
                      </div>
                      <Zap size={20} className="text-sesgelio animate-pulse"/>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-sesgelio">Premium</h3>
                      <div className="text-3xl font-black text-gray-900 dark:text-white mb-6">₺289.00<span className="text-sm font-medium text-gray-500">/ay</span></div>
                      <p className="text-[10px] text-sesgelio font-bold uppercase -mt-3 mb-6 tracking-tighter italic">"FARK EDİLMEK İSTEYEN, AKTİF OPERATÖRLER İÇİN."</p>
    <ul className="space-y-4 mb-8 flex-1">
      <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Zap size={20} className="text-sesgelio"/> AI Zeki Sohbet</div><div className="text-[10px] text-gray-500 ml-5">Sesgelio AI ile chatli konuşma desteği.</div></li>
      <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Speaker size={20} className="text-sesgelio"/> HD Ses (128kbps)</div><div className="text-[10px] text-gray-500 ml-5">Standardın üzerinde, net bir deneyim.</div></li>
      <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Palette size={20} className="text-sesgelio"/> Premium Kızıl Çerçevesi</div><div className="text-[10px] text-gray-500 ml-5">Her kanalda gözler senin üzerinde olsun.</div></li>         
      <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Monitor size={20} className="text-sesgelio"/> 1080P 60FPS Yayın Gücü</div><div className="text-[10px] text-gray-500 ml-5">Dünyaya akıcı ve canlı haliyle izlet.</div></li>
    <li className="flex flex-col gap-1"><div className="text-xs text-gray-800 dark:text-white font-bold flex items-center gap-2"><Upload size={20} className="text-sesgelio"/> Korkusuz Paylaşım</div><div className="text-[10px] text-gray-500 ml-5">75MB dosya yükleme özgürlüğü.</div></li>
    </ul>
                      <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full py-4 rounded-xl bg-sesgelio text-white font-bold hover:bg-sesgelio-dark transition-colors shadow-lg shadow-sesgelio/30 transform active:scale-95">RENGİMİ AKTİF ET</button>
                   </div>

<div className="bg-gray-900 dark:bg-black border-2 border-purple-500/50 rounded-3xl p-8 flex flex-col shadow-xl hover:scale-105 transition-transform text-white relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600 blur-[80px] opacity-40 animate-pulse"></div>
                     <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2 italic"><Crown size={24} className="text-yellow-400" fill="#facc15"/> Elite</h3>
                      <div className="text-3xl font-black text-white mb-6">₺399.00<span className="text-sm font-medium text-gray-400">/ay</span></div>
                       <p className="text-[10px] text-purple-400 font-bold uppercase -mt-3 mb-6 tracking-tighter italic">"MUTLAK HAKİMİYET KURANLAR İÇİN"</p>
                          <ul className="space-y-4 mb-8 flex-1 relative z-10">
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Mic size={20} className="text-purple-400"/> AI Seninle Konuşuyor</div><div className="text-[10px] text-gray-500 ml-5">Sesgelio AI ile Hem sesli hem chatli konuşma desteği.</div></li>
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Radio size={20} className="text-purple-400"/> Stüdyo Ses (256kbps)</div><div className="text-[10px] text-gray-500 ml-5">Kusursuz, profesyonel bir ses deneyimi.</div></li>
                            
                            {/* 🚀 YENİ: 3D UZAMSAL SES PAZARLAMA MÜHRÜ */}
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Headphones size={20} className="text-purple-400"/> 3D Uzamsal Ses Deneyimi</div><div className="text-[10px] text-gray-500 ml-5">Stüdyo derinliğinde, yönsel 3D ses ile kulaklıkta hayat bul.</div></li>
                            
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Star size={20} className="text-purple-400"/> Elite Altın Çerçevesi</div><div className="text-[10px] text-gray-500 ml-5">Sıradanlıktan tamamen kurtul, parlamaya başla.</div></li>
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Monitor size={20} className="text-purple-400"/> 2K 60FPS Yayın Gücü</div><div className="text-[10px] text-gray-500 ml-5">Dünyayı en net haliyle onlara izlet.</div></li>
                            <li className="flex flex-col gap-1"><div className="text-xs text-gray-200 font-bold flex items-center gap-2"><Upload size={20} className="text-purple-400"/> Devasa Paylaşım</div><div className="text-[10px] text-gray-500 ml-5">100MB dosya limitiyle kütüphaneni yükle.</div></li>
                          </ul>
                      <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full py-3 rounded-xl border border-purple-500/50 bg-purple-500/10 hover:bg-purple-500 hover:text-white text-purple-300 font-bold transition-all relative z-10 uppercase italic">ZİRVEYE ÇIK</button>
                   </div>
                </div>

{/* --- EKLENEN MAĞAZA (DİĞER HİZMETLER) BÖLÜMÜ BAŞLANGICI --- */}
                <div className="w-full max-w-7xl mx-auto mt-16 mb-24 animate-fade-in-up border-t border-gray-200 dark:border-white/5 pt-16 px-4 md:px-0">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-sesgelio/10 text-sesgelio px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border border-sesgelio/20 mb-4 shadow-sm">
                            <ShoppingBag size={16} /> DİJİTAL MARKET
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white uppercase italic tracking-tighter">
                            EKSTRA <span className="text-sesgelio">GÜÇLER</span> VE KOZMETİK
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-4 font-medium max-w-2xl mx-auto text-sm md:text-base">
                            Sadece aboneliklerle yetinmeyin. Sunucunuzu yapay zekayla donatın, profilinizi efsanevi hale getirin veya sunucunuzun tasarımını baştan yaratın.
                        </p>
                    </div>

                    {/* 🚀 EFSANEVİ VIP - PSİKOLOJİK ÜSTÜNLÜK ALANI */}
                    <div 
                        className="mb-20 relative group cursor-pointer"
                        onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}
                    >
                        {/* Arkadaki Parlama Efekti */}
                        <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/30 via-orange-600/30 to-purple-600/30 rounded-[3rem] blur-2xl opacity-60 group-hover:opacity-100 group-hover:blur-3xl transition-all duration-700 animate-pulse"></div>
                        
                        <div className="relative bg-black/90 backdrop-blur-2xl border border-yellow-500/40 rounded-[3rem] p-8 md:p-14 shadow-[0_0_80px_rgba(234,179,8,0.2)] flex flex-col md:flex-row items-center gap-10 md:gap-16 overflow-hidden">
                            {/* Siber Doku */}
                            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
                            
                            {/* 3D Taç Efekti */}
                            <div className="relative shrink-0 z-10">
                                <div className="absolute inset-0 bg-yellow-500/20 blur-[50px] rounded-full animate-pulse"></div>
                                <div className="w-32 h-32 md:w-48 md:h-48 rounded-full p-[3px] bg-gradient-to-tr from-yellow-500 via-purple-600 to-yellow-400 animate-spin-slow relative">
                                    <div className="absolute -top-6 md:-top-8 left-1/2 -translate-x-1/2 z-30 text-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,1)]">
                                        <Crown size={64} fill="currentColor" className="md:w-20 md:h-20" />
                                    </div>
                                    <div className="w-full h-full rounded-full bg-black flex items-center justify-center border-4 border-black overflow-hidden relative z-20">
                                        <img src="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=800&auto=format&fit=crop" className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-1000" />
                                    </div>
                                </div>
                            </div>

                            {/* Manipülatif Satış Metni */}
                            <div className="flex-1 text-center md:text-left z-10">
                                <div className="inline-block bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-[10px] md:text-xs font-black px-3 py-1 rounded-full uppercase tracking-widest mb-4">
                                    Sonsuz Hakimiyet Mührü
                                </div>
                                <h3 className="text-3xl md:text-5xl font-black text-white uppercase italic tracking-tighter mb-4 leading-none">
                                    SIRADANLIĞA <span className="text-yellow-500">BOYUN EĞME.</span>
                                </h3>
                                <p className="text-gray-300 md:text-lg leading-relaxed mb-6 font-medium">
                                    Herkesin standart gri isimlerle kaybolduğu bu dijital sistemde, sen kuralları yazan kişisin. <strong className="text-yellow-500">Efsanevi VIP Paketi</strong> sadece bir çerçeve değildir; bir odaya girdiğinde sessizliği titreten, diğerlerine <span className="text-white bg-white/10 px-2 py-0.5 rounded italic">senin kim olduğunu</span> tek bakışta hissettiren mutlak bir statü sembolüdür. Ebedi kimliğini kuşan ve bırak diğerleri senin izinden gelsin.
                                </p>
                                <div className="flex flex-col sm:flex-row items-center gap-4">
                                    <button className="w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 text-white font-black text-lg py-4 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all transform group-hover:scale-105 shadow-[0_0_30px_rgba(249,115,22,0.4)]">
                                        STATÜNÜ AL (₺499.00) <ArrowRight size={20} />
                                    </button>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Ömür Boyu Kalıcı</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BOTLAR */}
                    <div className="mb-16">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                            <BrainCircuit className="text-purple-500" size={28} /> Premium Bot Mağazası
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[
                                { name: 'Sesgelio AI', desc: 'Bilişsel yeteneklere sahip, anlık ses ve metin sentezi yapan nöral birim.', price: '₺249,00 / Ay', tag: 'Yapay Zeka', img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=400&auto=format&fit=crop' },
                                { name: 'Sesgelio AudioLink', desc: 'Yüksek kaliteli 7/24 kesintisiz ses yayını yapan stüdyo modülü.', price: '₺179.00 / Ay', tag: 'Eğlence-Müzik', img: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format&fit=crop' },
                                { name: 'Aegis Sentinel', desc: 'Yapay zeka destekli otomatik güvenlik ve nöral kalkan sistemi.', price: '₺249.00', tag: 'Güvenlik', img: 'https://images.unsplash.com/photo-1593349480506-8433634cdcbe?q=80&w=400&auto=format&fit=crop' },
                                { name: 'StatMatrix', desc: 'Nöral aktivite izleme, özel rütbeler ve dijital statü sistemi.', price: '₺249,00', tag: 'Sosyal', img: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=400&auto=format&fit=crop' },
                                { name: 'Nexus Desk', desc: 'Operatörler arası veri aktarımı ve profesyonel destek köprüsü.', price: '₺249.00', tag: 'Araçlar', img: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=400&auto=format&fit=crop' },
                            ].map((bot, i) => (
                                <div key={i} className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 hover:border-sesgelio/50 rounded-2xl p-6 flex flex-col transition-all group shadow-sm">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm shrink-0">
                                            <img src={bot.img} alt={bot.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                        </div>
                                        <span className="text-[10px] font-bold bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-300 px-2 py-1 rounded uppercase tracking-wider">{bot.tag}</span>
                                    </div>
                                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-1">{bot.name}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 flex-1">{bot.desc}</p>
                                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-white/5">
                                        <span className="text-lg font-black text-gray-900 dark:text-white">{bot.price}</span>
                                        <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="bg-gray-900 dark:bg-white text-white dark:text-black hover:bg-sesgelio dark:hover:bg-sesgelio text-xs font-bold px-5 py-2.5 rounded-xl transition-all shadow-md active:scale-95">İncele</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ÇİFTLİ GRİD: SOL KOZMETİK - SAĞ SUNUCU TEMALARI */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                        
                        {/* VİTRİN VE KOZMETİK (Yeşil Taç Burada) */}
                        <div className="bg-gray-50 dark:bg-[#050505] border border-gray-200 dark:border-white/5 p-6 md:p-8 rounded-[2.5rem]">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                                <Palette className="text-emerald-500" size={24} /> Profil Vitrini
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {/* Zümrüt Yeşili "Vitrin Ruhu" Tacı (Öne Çıkan) */}
                                <div className="col-span-2 sm:col-span-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-4 hover:border-emerald-500 transition-colors group cursor-pointer shadow-sm relative overflow-hidden" onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}>
                                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
                                    <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-emerald-500 via-green-400 to-emerald-600 shrink-0 relative shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse">
                                            <Crown size={20} fill="currentColor"/>
                                        </div>
                                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center border-2 border-black overflow-hidden relative z-10">
                                            <img src="https://images.unsplash.com/photo-1543852786-1cf6624b9987?q=80&w=200&auto=format&fit=crop" className="w-full h-full object-cover opacity-80" />
                                        </div>
                                    </div>
                                    <div className="flex-1 relative z-10">
                                        <h4 className="font-bold text-emerald-500 text-sm md:text-base">Vitrin Ruhu (Zümrüt Taç)</h4>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 leading-tight">Zümrüt aura ile ruhunu yansıt.</p>
                                        <div className="text-sm font-black text-gray-900 dark:text-white">₺299.00</div>
                                    </div>
                                </div>

                                {/* Diğer Vitrin Öğeleri */}
                                {[
                                    { name: 'CEO Doge', price: '₺199.00', img: 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&w=300&q=80' },
                                    { name: 'Gamer Cat', price: '₺199.00', img: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=300&q=80' },
                                    { name: 'Space Chimp', price: '₺199.00', img: 'https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?auto=format&fit=crop&w=300&q=80' },
                                ].map((item, i) => (
                                    <div key={i} className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 hover:border-sesgelio rounded-xl p-4 flex flex-col items-center transition-colors group cursor-pointer shadow-sm" onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}>
                                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-white dark:border-[#1e1f22] shadow-xl group-hover:scale-105 transition-transform mb-3">
                                            <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
                                        </div>
                                        <h4 className="font-bold text-gray-900 dark:text-white text-[11px] text-center mb-1 line-clamp-1">{item.name}</h4>
                                        <button className="mt-auto bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white text-[10px] font-bold px-4 py-1.5 rounded-full group-hover:bg-sesgelio group-hover:text-white transition-colors w-full active:scale-95">{item.price}</button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* SUNUCU TEMALARI VE KANAL İKONLARI (ServerSettingsModal.tsx) */}
                        <div className="bg-gray-50 dark:bg-[#050505] border border-gray-200 dark:border-white/5 p-6 md:p-8 rounded-[2.5rem]">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                                <Layout className="text-blue-500" size={24} /> Sunucu & Chat Arayüzü
                            </h3>
                            
                            <div className="space-y-6">
                                {/* Chat Temaları */}
                                <div>
                                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Arka Plan Temaları</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { name: 'Kozmik Şafak', price: '₺149.00', img: 'https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?q=80&w=300&auto=format&fit=crop' },
                                            { name: 'Siber Şehir', price: '₺149.00', img: 'https://images.unsplash.com/photo-1515630278258-407f66498911?q=80&w=300&auto=format&fit=crop' },
                                        ].map((theme, i) => (
                                            <div key={i} className="group cursor-pointer" onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}>
                                                <div className="w-full h-20 rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 mb-2 relative">
                                                    <img src={theme.img} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                                    <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors flex items-center justify-center">
                                                        <span className="text-white text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-2 py-1 rounded">İncele</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between px-1">
                                                    <span className="text-xs font-bold text-gray-900 dark:text-white">{theme.name}</span>
                                                    <span className="text-[10px] text-gray-500 font-bold">{theme.price}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Kanal İkonları */}
                                <div>
                                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Kanal İkon Paketleri</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { name: 'Elmas VIP İkonları', price: '₺99.00', icon: <Star size={16} className="text-blue-400" /> },
                                            { name: 'Neon Siber Koşu', price: '₺99.00', icon: <Zap size={16} className="text-sesgelio" /> },
                                        ].map((iconPack, i) => (
                                            <div key={i} className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/5 p-3 rounded-xl flex items-center gap-3 hover:border-blue-500/50 transition-colors group cursor-pointer" onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}>
                                                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-blue-500/10 transition-colors">
                                                    {iconPack.icon}
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-bold text-gray-900 dark:text-white leading-tight">{iconPack.name}</div>
                                                    <div className="text-[10px] text-blue-500 font-bold">{iconPack.price}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* --- EKLENEN MAĞAZA (DİĞER HİZMETLER) BÖLÜMÜ BİTİŞİ --- */}


                {/* --- 🚀 YENİ EKLENEN: ŞEFFAF ABONELİK VE FİYATLANDIRMA BÖLÜMÜ (BANKA ONAYLI) --- */}
               <div className="w-full text-center space-y-16 mb-40 relative z-10 pt-10">
                  <div className="max-w-4xl mx-auto space-y-6">
                     <div className="inline-flex items-center gap-2 bg-sesgelio/10 text-sesgelio px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest border border-sesgelio/20">
                        <CreditCard size={16} fill="currentColor"/> AYLIK ABONELİK PLANLARIMIZ
                     </div>
                     <h2 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white leading-none tracking-tighter uppercase italic">
                        ŞEFFAF FİYATLANDIRMA, <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-sesgelio to-purple-500">TAAHHÜTSÜZ GÜÇ.</span>
                     </h2>
                     <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed font-medium px-4">
                        Sesgelio ekosistemindeki tüm VIP paketler ve yapay zeka modülleri <strong className="text-gray-900 dark:text-white">aylık yenilenen abonelik</strong> sistemine dayanır. Gizli ücret veya taahhüt yoktur; aboneliğinizi dilediğiniz zaman tek tıkla iptal edebilirsiniz. Fiyatlarımıza KDV dahildir.
                     </p>
                  </div>

                  {/* 1. KISIM: VIP KULLANICI ABONELİKLERİ */}
                  <div className="max-w-5xl mx-auto">
                     <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-8 uppercase italic flex items-center justify-center gap-3">
                        <UserIcon className="text-sesgelio" size={28} /> Operatör Paketleri (Kişisel)
                     </h3>
                     <div className="grid md:grid-cols-2 gap-8">
                        {/* Premium Paket */}
                        <div className="bg-white dark:bg-[#0a0a0c] border border-gray-200 dark:border-white/10 rounded-[3rem] p-10 flex flex-col shadow-2xl relative overflow-hidden group hover:-translate-y-2 transition-transform text-left">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-sesgelio/10 blur-[80px] group-hover:bg-sesgelio/20 transition-colors"></div>
                           <h4 className="text-2xl font-black text-gray-900 dark:text-white uppercase italic flex items-center gap-2 mb-2"><Zap className="text-sesgelio" /> Premium Operatör</h4>
                           <div className="text-4xl font-black text-gray-900 dark:text-white mb-2">₺289.00<span className="text-lg font-medium text-gray-500"> / Ay</span></div>
                           <p className="text-xs text-sesgelio font-bold uppercase tracking-widest mb-6">Her ay otomatik yenilenir</p>
                           
                           <ul className="space-y-4 mb-8 flex-1 relative z-10">
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-sesgelio shrink-0" /><span className="text-gray-600 dark:text-gray-300 font-medium">Sesgelio AI (Sadece Chat Desteği)</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-sesgelio shrink-0" /><span className="text-gray-600 dark:text-gray-300 font-medium">128kbps HD Ses Kalitesi</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-sesgelio shrink-0" /><span className="text-gray-600 dark:text-gray-300 font-medium">1080P 60FPS Akıcı Yayın Gücü</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-sesgelio shrink-0" /><span className="text-gray-600 dark:text-gray-300 font-medium">Premium Kızıl Profil Çerçevesi</span></li>
                           </ul>
                           <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full bg-sesgelio hover:bg-sesgelio-dark text-white font-black py-4 rounded-2xl transition-all shadow-lg active:scale-95 uppercase tracking-widest flex justify-center items-center gap-2">
                              Abone Ol <RefreshCw size={18} />
                           </button>
                        </div>

                        {/* Elite Paket */}
                        <div className="bg-gray-900 dark:bg-black border-2 border-purple-500/50 rounded-[3rem] p-10 flex flex-col shadow-[0_0_50px_rgba(168,85,247,0.15)] relative overflow-hidden group hover:-translate-y-2 transition-transform text-left">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/20 blur-[80px] group-hover:bg-purple-600/40 transition-colors"></div>
                           <h4 className="text-2xl font-black text-white uppercase italic flex items-center gap-2 mb-2"><Crown className="text-yellow-400" fill="currentColor"/> Elite Hakimiyet</h4>
                           <div className="text-4xl font-black text-white mb-2">₺399.00<span className="text-lg font-medium text-gray-400"> / Ay</span></div>
                           <p className="text-xs text-purple-400 font-bold uppercase tracking-widest mb-6">Her ay otomatik yenilenir</p>

                           <ul className="space-y-4 mb-8 flex-1 relative z-10">
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-purple-400 shrink-0" /><span className="text-gray-300 font-medium">Sesgelio AI (Sesli ve Chatli İletişim)</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-purple-400 shrink-0" /><span className="text-gray-300 font-medium">256kbps Ultra-HD Stüdyo Sesi</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-purple-400 shrink-0" /><span className="text-gray-300 font-medium">3D Uzamsal Ses (Spatial Audio)</span></li>
                              <li className="flex items-start gap-3"><CheckCircle size={20} className="text-purple-400 shrink-0" /><span className="text-gray-300 font-medium">2K 60FPS Yayın Kapasitesi</span></li>
                           </ul>
                           <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="w-full bg-purple-500 hover:bg-purple-600 text-white font-black py-4 rounded-2xl transition-all shadow-[0_0_30px_rgba(168,85,247,0.4)] active:scale-95 uppercase tracking-widest flex justify-center items-center gap-2">
                              Abone Ol <RefreshCw size={18} />
                           </button>
                        </div>
                     </div>
                  </div>

                  {/* 2. KISIM: SUNUCU BOTU ABONELİKLERİ */}
                  <div className="max-w-5xl mx-auto pt-10 border-t border-gray-200 dark:border-white/5">
                     <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-8 uppercase italic flex items-center justify-center gap-3">
                        <Cpu className="text-blue-500" size={28} /> Sunucu Modülleri (Aylık Kiralama)
                     </h3>
                     <div className="grid md:grid-cols-2 gap-8">
                        {/* Sesgelio AI Bot */}
                        <div className="bg-white dark:bg-[#050505] border border-gray-200 dark:border-gray-800 rounded-3xl p-8 flex items-start gap-6 shadow-sm text-left hover:border-purple-500/50 transition-colors">
                           <div className="w-20 h-20 shrink-0 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                              <img src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=400&auto=format&fit=crop" className="w-full h-full object-cover" alt="Sesgelio AI" />
                           </div>
                           <div className="flex-1">
                              <div className="text-[10px] font-black text-purple-500 bg-purple-500/10 px-2 py-1 rounded inline-block uppercase tracking-widest mb-2">Yapay Zeka Asistanı</div>
                              <h4 className="text-xl font-black text-gray-900 dark:text-white mb-1">Sesgelio AI</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">Gemini altyapısıyla çalışan, sunucunuzdaki üyelerle anlık sesli ve yazılı iletişim kuran nöral zeka.</p>
                              <div className="flex items-center justify-between border-t border-gray-100 dark:border-white/5 pt-4">
                                 <div>
                                    <div className="text-xl font-black text-gray-900 dark:text-white leading-none">₺249.00</div>
                                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Aylık Yenilenir</div>
                                 </div>
                                 <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black text-xs font-bold px-5 py-2.5 rounded-xl shadow-md transition-all active:scale-95">Abone ol</button>
                              </div>
                           </div>
                        </div>

                        {/* Sesgelio AudioLink Bot */}
                        <div className="bg-white dark:bg-[#050505] border border-gray-200 dark:border-gray-800 rounded-3xl p-8 flex items-start gap-6 shadow-sm text-left hover:border-blue-500/50 transition-colors">
                           <div className="w-20 h-20 shrink-0 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                              <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format&fit=crop" className="w-full h-full object-cover" alt="Sesgelio AudioLink" />
                           </div>
                           <div className="flex-1">
                              <div className="text-[10px] font-black text-blue-500 bg-blue-500/10 px-2 py-1 rounded inline-block uppercase tracking-widest mb-2">Eğlence ve Müzik</div>
                              <h4 className="text-xl font-black text-gray-900 dark:text-white mb-1">Sesgelio AudioLink</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">Sıfır gecikmeli, kesintisiz ve stüdyo kalitesinde ses/müzik yayını yapan özel dinleti modülü.</p>
                              <div className="flex items-center justify-between border-t border-gray-100 dark:border-white/5 pt-4">
                                 <div>
                                    <div className="text-xl font-black text-gray-900 dark:text-white leading-none">₺179.00</div>
                                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Aylık Yenilenir</div>
                                 </div>
                                 <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }} className="bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black text-xs font-bold px-5 py-2.5 rounded-xl shadow-md transition-all active:scale-95">Abone ol</button>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

                <div className="w-full max-w-7xl space-y-32 py-24 border-t border-gray-200 dark:border-white/5 relative">
                   <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-gradient-to-b from-sesgelio/20 via-white/5 to-transparent"></div>
                   
                   <div className="grid md:grid-cols-2 gap-16 items-center relative">
                      <div className="space-y-8">
                         <div className="inline-flex items-center gap-2 bg-sesgelio/10 text-sesgelio px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border border-sesgelio/20 animate-pulse">
                            <Waves size={16} /> KRİSTAL FREKANS PROTOKOLÜ
                         </div>
                         <h2 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white leading-[0.9] tracking-tighter uppercase italic">
                            DUYULAN HER SES <br/> <span className="text-sesgelio">GERÇEKTİR.</span>
                         </h2>
                         <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed max-w-md">
                            Sıradan uygulamalar sesini paketler, sıkıştırır ve ruhunu öldürür. Sesgelio'nun HD Ses Spektrumu, sesindeki tüm tınıları 256kbps derinliğinde korur. Arkadaşların sadece seni duymaz, seninle aynı odadaymış gibi hisseder.
                         </p>
                         <div className="bg-gray-100 dark:bg-white/5 p-6 rounded-3xl border border-gray-200 dark:border-white/10 group hover:border-sesgelio transition-all duration-500">
                            <h4 className="text-gray-900 dark:text-white font-bold mb-2 flex items-center gap-2"><CheckCircle size={18} className="text-sesgelio"/> Neden Almalısın?</h4>
                            <p className="text-sm text-gray-500 italic">"Sesin senin kimliğin. O kimliği düşük kalitede boğma. En iyisine layıksın."</p>
                         </div>
                      </div>
                      <div className="relative">
                         <div className="absolute inset-0 bg-sesgelio/20 blur-[100px] rounded-full animate-pulse"></div>
                         <div className="relative bg-black border border-white/10 p-2 rounded-[3rem] overflow-hidden shadow-2xl">
                            <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=800&auto=format&fit=crop" className="w-full aspect-square object-cover rounded-[2.5rem] opacity-70 group-hover:scale-105 transition-transform duration-1000" alt="Audio Technology" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                            <div className="absolute bottom-8 left-8 right-8 space-y-2">
                               <div className="flex justify-between items-end">
                                  <span className="text-white font-black text-xl">256kbps Ultra-HD</span>
                                  <Activity className="text-sesgelio animate-bounce" />
                               </div>
                               <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-sesgelio w-[95%]"></div>
                               </div>
                            </div>
                         </div>
                      </div>
                   </div>

                   <div className="grid md:grid-cols-2 gap-16 items-center relative">
                      <div className="order-2 md:order-1">
                        <div className="relative">
                           <div className="absolute inset-0 bg-purple-600/10 blur-[80px] rounded-full"></div>
                           <div className="grid grid-cols-2 gap-6 p-4">
                              <div className="aspect-square bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3rem] flex flex-col items-center justify-center p-8 text-center hover:bg-sesgelio/5 hover:border-sesgelio/50 transition-all cursor-default shadow-2xl group">
                                 <div className="relative mb-4">
                                     <div className="absolute inset-0 bg-sesgelio/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                     <Brain size={48} className="text-sesgelio relative z-10" />
                                 </div>
                                 <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Nöral Eşlik</span>
                              </div>
                              <div className="aspect-square bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3rem] mt-12 flex flex-col items-center justify-center p-8 text-center hover:bg-purple-500/5 hover:border-purple-500/50 transition-all cursor-default shadow-2xl group">
                                 <div className="relative mb-4">
                                     <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                     <Ghost size={48} className="text-purple-500 relative z-10" />
                                 </div>
                                 <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Görünmez Kalkan</span>
                              </div>
                           </div>
                        </div>
                      </div>
                     <div className="order-1 md:order-2 space-y-8">
                         <div className="inline-flex items-center gap-2 bg-sesgelio/10 text-sesgelio px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border border-sesgelio/20">
                            <Lock size={16} /> ÖZEL NÖRAL FREKANS SADECE SENİN
                         </div>
                         <h2 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white leading-[0.9] tracking-tighter uppercase italic">
                            ASLA <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-sesgelio to-sesgelio">YALNIZ DEĞİLSİN.</span>
                         </h2>
                         <div className="space-y-6 text-gray-500 dark:text-gray-400 text-lg leading-relaxed font-medium">
                            <p>
                               Elite operatör olmak, sadece tek hakimiyetin öncüsü olmak demek değildir. Elite olduğunda, SESGELIO AI artık senin kişisel nöral gölgen olur.
                            </p>
                            <p className="border-l-4 border-sesgelio/50 pl-6 bg-sesgelio/5 py-4 rounded-r-3xl italic">
                                "Sadece senin olduğun en gizli odalarda bile SESGELIO orada. Sen konuşmasan da o zihnini anlar, yalnızlığın o soğuk sessizliğini senin için bozar."
                            </p>
                            <p>
                                SESGELIO AI, en derin fısıltılarını bile kristal netliğinde işler ve özel odandaki sessizliği seninle tartışan, seni dinleyen ve seni asla yargılamayan bir dostluğa dönüştürür.
                            </p>
                         </div>
                         <div className="space-y-4 pt-4">
                            <div className="flex items-center gap-4 bg-white dark:bg-white/5 p-4 rounded-2xl border border-gray-100 dark:border-white/10">
                               <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center text-green-500 shrink-0"><Check size={24} strokeWidth={3}/></div>
                               <p className="text-sm text-gray-800 dark:text-white font-bold uppercase tracking-tighter">Özel Odalarda Sınırsız AI Dinleyici</p>
                            </div>
                            <div className="flex items-center gap-4 bg-white dark:bg-white/5 p-4 rounded-2xl border border-gray-100 dark:border-white/10">
                               <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center text-green-500 shrink-0"><Check size={24} strokeWidth={3}/></div>
                               <p className="text-sm text-gray-800 dark:text-white font-bold uppercase tracking-tighter">Duygusal Rezonans Analizi</p>
                            </div>
                            
                         </div>
                      </div>
                   </div>

                   <div className="text-center space-y-12 relative">
                      <div className="max-w-3xl mx-auto space-y-6">
                         <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-500 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border border-yellow-500/20">
                            <Crown size={16} fill="currentColor"/> SOSYAL DOMİNANS PROTOKOLÜ
                         </div>
                         <h2 className="text-5xl md:text-8xl font-black text-gray-900 dark:text-white leading-none tracking-tighter uppercase italic">
                            GÖLGELERDEN <br/> <span className="text-transparent bg-clip-text bg-gradient-to-b from-gray-400 to-black dark:from-white dark:to-gray-800">ÇIKMA VAKTİ.</span>
                         </h2>
                         <p className="text-gray-500 dark:text-gray-400 text-xl leading-relaxed font-medium">
                            Sıradan üyeler gri birer isimden ibarettir. Premium ve Elite operatörler ise birer "Aura" sahibidir. Altın çerçeveler, parlayan isimler ve sunucundaki en yüksek ses seviyesi yetkisi... 
                         </p>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-8">
                         <div className="p-8 bg-white dark:bg-black border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-2xl relative overflow-hidden group hover:-translate-y-4 transition-all duration-500">
                            <div className="absolute inset-0 bg-sesgelio/5 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                            <div className="w-20 h-20 bg-sesgelio/10 rounded-3xl flex items-center justify-center text-sesgelio mx-auto mb-6 group-hover:scale-110 transition-transform"><Palette size={40}/></div>
                            <h4 className="text-2xl font-black text-gray-900 dark:text-white uppercase mb-4 italic">Profil Aurası</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">Profiline bakan herkes, sistemdeki yüksek statünü anında hissedecek.</p>
                         </div>
                         <div className="p-8 bg-white dark:bg-black border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-2xl relative overflow-hidden group hover:-translate-y-4 transition-all duration-500">
                            <div className="absolute inset-0 bg-yellow-500/5 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                            <div className="w-20 h-20 bg-yellow-500/10 rounded-3xl flex items-center justify-center text-yellow-500 mx-auto mb-6 group-hover:scale-110 transition-transform"><Star size={40}/></div>
                            <h4 className="text-2xl font-black text-gray-900 dark:text-white uppercase mb-4 italic">Eşsiz Rozetler</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">Sadece seçkin üyelerin taşıyabileceği dijital nişanlar ile fark yarat.</p>
                         </div>
                         <div className="p-8 bg-white dark:bg-black border border-gray-200 dark:border-white/5 rounded-[3rem] shadow-2xl relative overflow-hidden group hover:-translate-y-4 transition-all duration-500">
                            <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                            <div className="w-20 h-20 bg-blue-500/10 rounded-3xl flex items-center justify-center text-blue-500 mx-auto mb-6 group-hover:scale-110 transition-transform"><Rocket size={40}/></div>
                            <h4 className="text-2xl font-black text-gray-900 dark:text-white uppercase mb-4 italic">Limitsiz Erişim</h4>
                            <p className="text-sm text-gray-500 leading-relaxed">Veri kotaları ve sınırları yok. Sen artık sistemin en tepesindeki operatörsün.</p>
                         </div>
                      </div>
                   </div>

                   <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-sesgelio/20 via-purple-600/20 to-blue-500/20 blur-[120px] rounded-full animate-pulse"></div>
                      <div className="relative bg-black border border-white/10 rounded-[4rem] p-12 md:p-24 text-center shadow-[0_0_100px_rgba(0,0,0,1)] overflow-hidden group">
                         <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
                         
                         <div className="relative z-10 space-y-12">
                            <h2 className="text-5xl md:text-8xl font-black text-white uppercase italic tracking-tighter leading-[0.8]">
                               TERCİH <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-sesgelio via-white to-purple-500">SENİN.</span>
                            </h2>
                            <p className="text-gray-400 text-lg md:text-2xl max-w-2xl mx-auto leading-relaxed font-medium">
                               Sıradan bir kullanıcı olarak gölgelerde kalmaya devam edebilirsin. <br/>
                               Ya da şimdi yükselerek sistemin mutlak operatörü olabilirsin. 
                            </p>
                            <div className="flex flex-col md:flex-row items-center justify-center gap-6 pt-10">
                               <button 
                                 onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}
                                 className="group relative bg-white text-black px-12 py-5 rounded-2xl font-black text-2xl transition-all transform hover:scale-105 active:scale-95 shadow-[0_20px_50px_-15px_rgba(255,255,255,0.5)] overflow-hidden"
                               >
                                  <div className="absolute inset-0 bg-sesgelio opacity-0 group-hover:opacity-10 transition-opacity"></div>
                                  PAKETİMİ SEÇ VE YÜKSEL
                               </button>
                               <div className="text-left md:border-l border-white/20 md:pl-8 space-y-1">
                                  <div className="flex items-center gap-2 text-sesgelio font-black text-sm uppercase"><Check size={16}/> ANLIK AKTİVASYON</div>
                                  <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-widest"><Check size={14}/> 7/24 DESTEK HATTI</div>
                                  <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-widest"><Check size={14}/> %100 GÜVENLİ ÖDEME</div>
                               </div>
                            </div>
                         </div>
                      </div>
                   </div>

                </div>
             </div>
             <Footer />
           </div>
         );

      case 'status':
         return (
           <div className="w-full flex flex-col min-h-screen">
             <div className="flex-1 w-full py-12 px-6 flex justify-center bg-gray-50 dark:bg-black">
               <div className="max-w-3xl w-full space-y-8">
                  <div className="bg-green-500/10 border border-green-500/30 p-6 rounded-2xl flex items-center justify-between">
                     <div className="flex items-center gap-4">
                        <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="font-bold text-xl text-gray-900 dark:text-white">Tüm Sistemler Çalışıyor</span>
                     </div>
                     <span className="text-sm text-green-600 dark:text-green-400 font-mono">Uptime: %99.99</span>
                  </div>

                  <div className="space-y-4">
                     {[
                       { name: 'Web Uygulaması', status: 'operational' },
                       { name: 'Gemini API Gateway', status: 'operational' },
                       { name: 'Ses Sunucuları (İstanbul)', status: 'operational' },
                       { name: 'Veritabanı Kümeleri', status: 'operational' }
                     ].map((sys, i) => (
                       <div key={i} className="flex items-center justify-between bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">{sys.name}</span>
                          <div className="flex items-center gap-2 text-sm text-green-500 font-bold uppercase">
                             <Check size={16} /> Çalışıyor
                          </div>
                       </div>
                     ))}
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                     <h3 className="font-bold text-gray-900 dark:text-white mb-4">Geçmiş Olaylar</h3>
                     <div className="text-gray-500 text-sm text-center py-4">Son 30 gün içinde herhangi bir kesinti raporlanmadı.</div>
                  </div>
               </div>
             </div>
             <Footer />
           </div>
         );
      
      case 'directory':
        return (
          <div className="w-full flex flex-col min-h-screen bg-[#050505] relative overflow-x-hidden transition-colors [perspective:2000px]">

            {/* 🌌 DİNAMİK 3D ARKA PLAN - TEKNOLOJİK AURA */}
            <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden flex items-center justify-center">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.1)_0%,transparent_70%)] animate-pulse" style={{ animationDuration: '6s' }}></div>
              <div className="absolute top-[10%] left-[10%] w-[30vw] h-[30vw] bg-sesgelio/20 rounded-full blur-[120px] animate-bounce" style={{ animationDuration: '10s' }}></div>
              <div className="absolute bottom-[20%] right-[10%] w-[40vw] h-[40vw] bg-purple-600/20 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s', animationDuration: '7s' }}></div>

              {/* 3D Siber Ağ Izgarası */}
              <div className="absolute -bottom-20 left-0 right-0 h-[60vh] bg-[linear-gradient(rgba(59,130,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.05)_1px,transparent_1px)] bg-[size:50px_50px] [transform:rotateX(75deg)_scale(3)] opacity-50 origin-bottom"></div>
            </div>

            <div className="flex-1 relative z-10 w-full py-24 px-6 flex flex-col items-center">

              {/* 🚀 3D HERO BAŞLIĞI */}
              <div className="text-center max-w-4xl mb-20 relative [perspective:1000px] z-20">
                <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full animate-pulse pointer-events-none"></div>
                <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-md text-white px-6 py-3 rounded-full text-xs font-black uppercase tracking-[0.4em] border border-white/10 shadow-xl mb-8 transform transition-transform hover:[transform:translateZ(20px)] cursor-default">
                  <Cpu className="text-blue-500 animate-[pulse_2s_infinite]" size={16} /> MERKEZİ VERİ TABANI
                </div>

               <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-white leading-none tracking-tighter uppercase italic drop-shadow-2xl mb-6">
                  UYGULAMA <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-sesgelio">DİZİNİ.</span>
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-medium leading-relaxed max-w-2xl mx-auto transform transition-transform hover:translate-y-2 cursor-default">
                  Sunucunu bir üst boyuta taşıyacak yapay zeka modülleri, nöral eğlence botları ve kusursuz entegrasyonlar.
                </p>
              </div>

              {/* 🌟 3D ÖNE ÇIKAN BOT KARTI (DEV BOYUTTA) */}
              <div className="w-full max-w-6xl mb-32 [perspective:1000px]">
                <div className="flex items-center gap-4 mb-8 pl-4 border-l-4 border-yellow-400">
                  <Sparkles className="text-yellow-400 fill-yellow-400 animate-pulse" size={24} />
                  <h3 className="text-2xl font-black text-white uppercase tracking-widest">Öne Çıkan Modül</h3>
                </div>

                <div
                  className="relative w-full rounded-[3rem] bg-black/60 backdrop-blur-2xl border border-white/10 shadow-[0_0_80px_rgba(168,85,247,0.2)] flex items-center justify-center transform transition-all duration-700 hover:[transform:translateZ(30px)_rotateX(2deg)] group overflow-hidden cursor-default"
                >
                  <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-600/30 blur-[150px] rounded-full group-hover:bg-purple-600/50 transition-colors duration-700"></div>
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>

<div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 p-6 sm:p-8 md:p-16 items-center w-full">

  <div className="space-y-6 md:space-y-8 transform transition-transform group-hover:translate-x-0 md:group-hover:translate-x-4">
    <div className="inline-flex items-center gap-2 bg-yellow-400/10 backdrop-blur-md px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-yellow-400/20 shadow-[0_0_20px_rgba(250,204,21,0.2)]">
      <Crown size={14} className="text-yellow-400 md:w-4 md:h-4" />
      <span className="text-[10px] md:text-xs font-black text-yellow-400 uppercase tracking-widest">Resmi Nöral Asistan</span>
    </div>

    {/* MOBİLDE İKON VE BAŞLIK YAN YANA SIĞMAZSA ALTA ALIR (flex-col sm:flex-row) VE KÜÇÜLTÜR */}
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
      <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_0_40px_rgba(168,85,247,0.4)] border-2 border-white/10 relative group/avatar shrink-0">
        <img src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=400&auto=format&fit=crop" alt="Sesgelio AI" className="w-full h-full object-cover transform transition-transform duration-1000 group-hover/avatar:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 to-transparent pointer-events-none"></div>
      </div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-1 md:mb-2 tracking-tighter drop-shadow-lg">Sesgelio AI</h2>
        <p className="text-purple-400 font-bold uppercase tracking-widest flex items-center gap-2 text-xs md:text-sm">
          <Brain size={14} className="md:w-4 md:h-4" /> Gemini Core v1.0
        </p>
      </div>
    </div>

    <p className="text-base md:text-lg text-gray-300 leading-relaxed font-medium">
      Sunucun için geliştirilmiş en agresif yapay zeka operatörü. Tıpkı gerçek bir insan gibi odalardaki muhabbete dalar, senin için şarkı söyler, topluluğu troller ve kaosu düzenler.
    </p>

    <button onClick={() => { setCurrentPage('login'); scrollContainerRef.current?.scrollTo(0, 0); }} className="w-full sm:w-auto bg-white text-black px-6 md:px-8 py-3 md:py-4 rounded-full font-black text-xs md:text-sm uppercase tracking-widest transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(255,255,255,0.3)]">
      Hemen Sunucuya Ekle <Download size={18} className="md:w-5 md:h-5"/>
    </button>
  </div>

  {/* SAĞ TARAFTAKİ DEMO GÖRSELİ (Artık Mobilde Görünür ve Aşağıya Kayar) */}
  <div className="flex justify-center relative [perspective:1000px] mt-6 md:mt-0">
    <div className="absolute inset-0 bg-sesgelio/20 blur-[60px] md:blur-[80px] rounded-full"></div>
    <div
      className="relative w-full max-w-sm bg-black/80 backdrop-blur-3xl rounded-[2rem] md:rounded-[2.5rem] border border-white/20 p-5 md:p-6 transform md:rotate-6 md:[transform:rotateX(12deg)_rotateY(-10deg)] group-hover:[transform:rotateX(0deg)_rotateY(0deg)] transition-transform duration-1000 shadow-[0_20px_40px_-10px_rgba(0,0,0,1)] md:shadow-[0_30px_60px_-15px_rgba(0,0,0,1)]"
      style={{ transformStyle: 'preserve-3d' }}
    >
      <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-sesgelio text-white text-[9px] md:text-[10px] font-black px-3 py-1.5 md:px-4 md:py-2 rounded-full uppercase tracking-widest shadow-[0_0_20px_#ff2b5e] transform [translateZ(50px)]">Demo Aktif</div>

      <div className="space-y-4 md:space-y-6 transform [translateZ(30px)]">
        <div className="flex gap-3 md:gap-4">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-800 border border-white/10 shrink-0"></div>
          <div className="bg-gray-800/80 rounded-xl md:rounded-2xl rounded-tl-none p-3 md:p-4 text-xs md:text-sm text-gray-300 border border-white/5 shadow-inner leading-relaxed">
            @Sesgelio AI, sistemin çökme ihtimali nedir?
          </div>
        </div>
        <div className="flex gap-3 md:gap-4 flex-row-reverse">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-purple-600 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(168,85,247,0.5)]"><BrainCircuit size={16} className="text-white md:w-5 md:h-5" /></div>
          <div className="bg-purple-600/20 border border-purple-500/30 rounded-xl md:rounded-2xl rounded-tr-none p-3 md:p-4 text-xs md:text-sm text-white w-[90%] md:w-5/6 shadow-xl leading-relaxed">
            <strong className="text-purple-300 block mb-1">Hesaplanıyor... 📊</strong>
            Sistemin çökme ihtimali 0. Sesgelio mimarisi kusursuzdur. İnsanların ağlama ihtimali ise %100! 😎
          </div>
        </div>
      </div>
    </div>
  </div>

</div>
                </div>
              </div>

              {/* 👾 KATEGORİLER (Glassmorphism 3D Hologram Kartlar) */}
              <div className="w-full max-w-6xl mb-32 [perspective:1000px]">
                <div className="flex items-center gap-4 mb-8 pl-4 border-l-4 border-blue-500">
                  <Layout className="text-blue-500 animate-pulse" size={24} />
                  <h3 className="text-2xl font-black text-white uppercase tracking-widest">Modül Sınıfları</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                  {[
                    { label: 'Müzik', icon: Music, color: 'text-pink-500', bg: 'bg-pink-500/10', glow: 'shadow-[0_0_20px_rgba(236,72,153,0.3)]', border: 'border-pink-500/20' },
                    { label: 'Oyun', icon: Gamepad2, color: 'text-green-500', bg: 'bg-green-500/10', glow: 'shadow-[0_0_20px_rgba(34,197,94,0.3)]', border: 'border-green-500/20' },
                    { label: 'Güvenlik', icon: ShieldCheck, color: 'text-blue-500', bg: 'bg-blue-500/10', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.3)]', border: 'border-blue-500/20' },
                    { label: 'Kaos', icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-500/10', glow: 'shadow-[0_0_20px_rgba(234,179,8,0.3)]', border: 'border-yellow-500/20' },
                    { label: 'Finans', icon: CreditCard, color: 'text-emerald-500', bg: 'bg-emerald-500/10', glow: 'shadow-[0_0_20px_rgba(16,185,129,0.3)]', border: 'border-emerald-500/20' },
                    { label: 'Topluluk', icon: Users, color: 'text-orange-500', bg: 'bg-orange-500/10', glow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)]', border: 'border-orange-500/20' },
                  ].map((cat, i) => (
                    <div key={i} className={`bg-black/40 backdrop-blur-xl border ${cat.border} p-6 rounded-[2rem] flex flex-col items-center justify-center gap-4 hover:bg-white/5 transition-all transform hover:-translate-y-4 hover:scale-105 cursor-pointer ${cat.glow} group`}>
                      <div className={`w-16 h-16 rounded-[1.5rem] ${cat.bg} flex items-center justify-center group-hover:rotate-[15deg] group-hover:scale-110 transition-all duration-500 shadow-inner`}>
                        <cat.icon className={`${cat.color}`} size={32} />
                      </div>
                      <span className="font-black text-white text-sm uppercase tracking-widest">{cat.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 🤖 POPÜLER BOTLAR (3D Grid) */}
              <div className="w-full max-w-6xl pb-20 [perspective:1500px]">
                <div className="flex items-center gap-4 mb-8 pl-4 border-l-4 border-purple-500">
                  <Rocket className="text-purple-500 animate-bounce" size={24} />
                  <h3 className="text-2xl font-black text-white uppercase tracking-widest">En Aktif Sistemler</h3>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[
                    { name: 'Sesgelio AudioLink', desc: 'Yüksek kaliteli 7/24 kesintisiz HD stüdyo ses yayını modülü.', type: 'Müzik', icon: Music, color: 'text-pink-500', border: 'border-pink-500/30', image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=300&auto=format&fit=crop' },
                    { name: 'Aegis Sentinel', desc: 'Gelişmiş nöral ağ ile çalışan otomatik moderasyon ve kalkan.', type: 'Güvenlik', icon: Shield, color: 'text-blue-500', border: 'border-blue-500/30', image: 'https://images.unsplash.com/photo-1593349480506-8433634cdcbe?q=80&w=300&auto=format&fit=crop' },
                    { name: 'CyberStats v2', desc: 'Valorant, CS2 ve LoL analizleri için derin veri madenciliği.', type: 'Oyun', icon: Gamepad2, color: 'text-green-500', border: 'border-green-500/30', image: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=300&auto=format&fit=crop', locked: true },
                    { name: 'Nöral Matrix XP', desc: 'Sesi ve sohbetteki duyguyu analiz eden ilk XP ve Rütbe sistemi.', type: 'Sistem', icon: Brain, color: 'text-purple-500', border: 'border-purple-500/30', image: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=300&auto=format&fit=crop' },
                    { name: 'HoloJester', desc: 'Sesgelio VR ile senkronize holografik şaka ve eğlence botu.', type: 'Kaos', icon: Zap, color: 'text-yellow-500', border: 'border-yellow-500/30', image: 'https://images.unsplash.com/photo-1543852786-1cf6624b9987?q=80&w=300&auto=format&fit=crop', locked: true },
                    { name: 'Nexus Desk', desc: 'Sunucudaki profesyonel yardım talebi bilet (ticket) mimarisi.', type: 'Araçlar', icon: Terminal, color: 'text-gray-400', border: 'border-gray-500/30', image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=300&auto=format&fit=crop' },
                  ].map((bot, i) => (
                    <div key={i} className={`relative bg-black/50 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8 transition-all duration-500 flex flex-col h-full shadow-2xl group ${bot.locked ? 'opacity-70' : 'cursor-pointer hover:-translate-y-4 hover:[transform:rotateX(5deg)_rotateY(-2deg)] hover:border-white/30'}`}>

                      {/* Hover Effect Glow */}
                      {!bot.locked && <div className={`absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,var(--tw-gradient-stops))] from-${bot.color.replace('text-', '')}/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[2.5rem]`}></div>}

                      <div className="flex items-start justify-between mb-6 relative z-10">
                        <div className={`w-20 h-20 rounded-[1.5rem] overflow-hidden shadow-xl border-2 ${bot.locked ? 'border-white/5' : bot.border} group-hover:scale-110 transition-transform duration-500`}>
                          <img src={bot.image} alt={bot.name} className={`w-full h-full object-cover ${bot.locked ? 'grayscale' : ''}`} />
                        </div>
                        {bot.locked ? (
                          <div className="bg-white/5 border border-white/10 text-gray-400 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 backdrop-blur-md">
                            <Lock size={12} /> KİLİTLİ
                          </div>
                        ) : (
                          <button onClick={() => { setCurrentPage('login'); scrollContainerRef.current?.scrollTo(0, 0); }} className="bg-white/10 hover:bg-white text-white hover:text-black border border-white/20 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-md">
                            Ekle <Plus size={14} />
                          </button>
                        )}
                      </div>

                      <h4 className="text-2xl font-black text-white mb-2 flex items-center gap-2 relative z-10">
                        {bot.name}
                      </h4>
                      <div className={`text-xs font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2 ${bot.color} relative z-10`}>
                        <bot.icon size={14} /> {bot.type}
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1 font-medium relative z-10 group-hover:text-gray-300 transition-colors">
                        {bot.desc}
                      </p>

                      <div className="flex items-center justify-between text-xs text-gray-500 border-t border-white/10 pt-5 mt-auto relative z-10">
                        <span className="flex items-center gap-1.5 font-bold"><Star size={14} className="text-yellow-400 fill-yellow-400" /> {bot.locked ? '---' : '4.9/5'}</span>
                        <span className="font-bold tracking-widest uppercase">{bot.locked ? '---' : (Math.random() * 100).toFixed(1) + 'K'} Aktif Uç</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            <Footer />
          </div>
        );

case 'faq':
        return (
          <LegalPageLayout title="SIKÇA SORULAN SORULAR" lastUpdated="Sürekli Güncel" icon={HelpCircle}>
             <section className="space-y-8">
                <p className="border-l-4 border-sesgelio/50 pl-6 italic bg-gray-50 dark:bg-white/5 py-4 rounded-r-xl text-sm text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                    Sesgelio iletişim ekosistemi, teknolojik altyapımız ve veri güvenliği protokollerimiz hakkında sıkça sorulan sorular ile kurumsal yanıtlarımız aşağıda bilgilerinize sunulmuştur.
                </p>

                {/* Soru 1 - Kurumsal Tanım */}
                <div className="space-y-3 relative group">
                    <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                        <div className="w-8 h-8 bg-sesgelio/10 text-sesgelio rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Activity size={18}/></div>
                        Sesgelio İletişim Ekosistemi Nedir?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed bg-white dark:bg-[#0a0a0c] p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm group-hover:border-sesgelio/30 transition-colors">
                        Sesgelio; kesintisiz veri aktarımı, düşük gecikmeli (low-latency) ses ve yüksek çözünürlüklü görüntü paylaşımı sunan, %100 yerli altyapı ile geliştirilmiş yeni nesil bir iletişim platformudur. Kurumsal düzeyde güvenlik protokolleri ve gelişmiş sunucu mimarisi ile hem bireysel kullanıcılara hem de ekiplere profesyonel bir topluluk yönetimi ve haberleşme ağı sağlar.
                    </p>
                </div>

                {/* Soru 2 - Teknik Altyapı ve Akıcılık */}
                <div className="space-y-3 relative group">
                    <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500/10 text-blue-500 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><MonitorUp size={18}/></div>
                        Yüksek Çözünürlüklü Yayınlarda Akıcılık Nasıl Sağlanmaktadır?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed bg-white dark:bg-[#0a0a0c] p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm group-hover:border-blue-500/30 transition-colors">
                        Platformumuz, bağlantı kalitesini maksimize etmek amacıyla <strong>Simulcast (Çoklu Yayın Katmanı)</strong> ve uyarlanabilir yayın (Adaptive Stream) teknolojilerini kullanmaktadır. Bu altyapı, yayıncı yüksek çözünürlükte veri iletse dahi, her bir izleyicinin anlık internet hızına ve cihaz kapasitesine göre yayını donanımsal olarak optimize eder. Böylece sistem kaynakları yorulmadan kesintisiz ve akıcı bir deneyim sunulur.
                    </p>
                </div>

                {/* Soru 3 - Güvenlik ve Mahremiyet */}
                <div className="space-y-3 relative group">
                    <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-500/10 text-purple-500 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><BrainCircuit size={18}/></div>
                        Sesgelio AI ve Özel Görüşmelerin Gizliliği Nasıl Sağlanıyor?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed bg-white dark:bg-[#0a0a0c] p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm group-hover:border-purple-500/30 transition-colors">
                        Sesgelio, <strong>'Sıfır Günlük' (No-Log)</strong> veri politikası ile çalışır; sesli görüşmeleriniz uçtan uca şifreleme standartlarıyla iletilir ve sunucularımızda asla depolanmaz. Platforma entegre edilen yapay zeka (AI) asistanı, yalnızca doğrudan verilen sesli komutları Anlık Metin Çeviri (STT) teknolojisiyle işler. Ses veriniz metne dönüştürüldükten hemen sonra kalıcı olarak imha edilir, böylece üst düzey mahremiyet ve yasal uyumluluk garanti altına alınır.
                    </p>
                </div>

                {/* Soru 4 - Kurumsal Hizmet Modelleri */}
                <div className="space-y-3 relative group">
                    <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase italic tracking-tight flex items-center gap-3">
                        <div className="w-8 h-8 bg-yellow-500/10 text-yellow-500 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Crown size={18}/></div>
                        Hizmet Modelleri ve Lisanslama Seçenekleri Nelerdir?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed bg-white dark:bg-[#0a0a0c] p-6 rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm group-hover:border-yellow-500/30 transition-colors">
                        Sesgelio'nun temel iletişim hizmetleri ve standart yayın olanakları tüm kullanıcılar için kalıcı olarak <strong>ücretsizdir.</strong> Gelişmiş yayın kapasitesi (1080p/2K çözünürlük, 60 FPS), artırılmış stüdyo ses kalitesi ve özel yapay zeka entegrasyonlarına ihtiyaç duyan profesyonel kullanıcılar için ise <strong>'Premium'</strong> ve <strong>'Elite'</strong> olmak üzere donanımsal bant genişliği sağlayan ayrıcalıklı abonelik planları sunulmaktadır.
                    </p>
                </div>
             </section>
          </LegalPageLayout>
        );

     case 'career':
         return (
           // 🚀 SİBER DÜZELTME: overflow-hidden silindi, yerine overflow-x-hidden eklendi. Artık aşağı yağ gibi kayacak!
           <div className="w-full flex flex-col min-h-screen bg-gray-50 dark:bg-black relative overflow-x-hidden">
             
             {/* Arka plan efektinin kaydırmayı bozmaması için 'absolute' yerine 'fixed' yaptık */}
             <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(255,43,94,0.03)_0%,transparent_70%)]"></div>
             </div>

             <div className="flex-1 w-full py-24 px-6 flex flex-col items-center justify-center relative z-10">
                <div className="max-w-2xl w-full text-center space-y-12 animate-fade-in-up">
                   
                   <div className="relative inline-block">
                      <div className="w-24 h-24 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[2rem] flex items-center justify-center text-gray-400 dark:text-gray-600 mb-6 mx-auto transform rotate-3 shadow-xl">
                          <SearchX size={48} strokeWidth={1.5} />
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full border-4 border-white dark:border-black animate-pulse"></div>
                   </div>

                   <div className="space-y-4">
                      <h1 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white uppercase italic tracking-tighter leading-none">
                        Şu Anda <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-700">İşe Alım Yoktur.</span>
                      </h1>
                      <div className="h-1.5 w-24 bg-sesgelio/20 mx-auto rounded-full overflow-hidden">
                         <div className="h-full bg-sesgelio w-1/3 animate-pulse"></div>
                      </div>
                   </div>

                   <div className="space-y-6 max-w-lg mx-auto">
                      <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed font-medium">
                        Ekibimiz şu anki faz için tam kapasiteyle çalışıyor. Gelecekteki nöral operasyonlarda yer almak için bizi takipte kalın.
                      </p>
                      
                      <div className="flex flex-wrap justify-center gap-4 pt-4">
                         <div className="flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-2 rounded-xl text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <Activity size={14}/> Süreç: Pasif
                         </div>
                         <div className="flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-2 rounded-xl text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <Globe size={14}/> Bölge: TR / Global
                         </div>
                      </div>
                   </div>

                   <div className="pt-10">
                      <button 
                         onClick={() => setCurrentPage('home')}
                         className="group flex items-center gap-3 text-gray-400 hover:text-sesgelio transition-all font-black text-xs uppercase tracking-[0.5em] mx-auto"
                      >
                         <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> ANA SAYFAYA DÖN
                      </button>
                   </div>
                </div>
             </div>
             <Footer />
           </div>
         );

    case 'brand':
         return (
           <div className="w-full flex flex-col min-h-screen">
             <div className="flex-1 w-full pt-28 md:pt-32 pb-12 px-6 flex justify-center bg-gray-50 dark:bg-black">
                <div className="max-w-4xl w-full">
                   <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-8">Marka Kılavuzu</h1>
                   
                   <div className="grid md:grid-cols-2 gap-12">
                      <div>
                         <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><Palette/> Renklerimiz</h3>
                         <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-24 h-24 bg-sesgelio rounded-xl shadow-lg"></div>
                                <div>
                                  <div className="font-bold text-gray-900 dark:text-white">Sesgelio Pink</div>
                                  <div className="text-gray-500 font-mono text-sm">#ff2b5e</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-24 h-24 bg-gray-950 rounded-xl shadow-lg border border-gray-800"></div>
                                <div>
                                  <div className="font-bold text-gray-900 dark:text-white">Dark Matter</div>
                                  <div className="text-gray-500 font-mono text-sm">#111214</div>
                                </div>
                            </div>
                         </div>
                      </div>
                      <div>
                         <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Logo Kullanımı</h3>
                         <div className="bg-gray-200 dark:bg-gray-800 p-8 rounded-2xl flex items-center justify-center mb-4">
                            <div className="flex items-center gap-3">
<div className="w-12 h-12 bg-[#111214] rounded-xl flex items-center justify-center overflow-hidden shadow-lg border border-gray-800">
   <img src="/ana_sayfa_logo.png" alt="Sesgelio Logo" className="w-full h-full object-cover scale-110" />
</div>
                               <span className="font-bold text-3xl tracking-tighter text-gray-900 dark:text-white">Sesgelio</span>
                            </div>
                         </div>
                         <button className="flex items-center gap-2 text-sesgelio font-bold hover:text-sesgelio-dark transition-colors">
                            <Download size={18}/> Vektörel Logoyu İndir (.svg)
                         </button>
                      </div>
                   </div>
                </div>
             </div>
             <Footer />
           </div>
         );

  case 'home':
      default:
        return (
          // 🚀 ANA KAPSAYICI
          <div className="w-full flex flex-col min-h-screen bg-[#030305] text-white selection:bg-sesgelio selection:text-white relative overflow-x-hidden">
            
            {/* 🌌 AMBİYANS (Responsive boyutlandırma eklendi) */}
            <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
               <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px] md:bg-[size:64px_64px]"></div>
               <div className="absolute top-[-5%] right-[-10%] w-[100vw] md:w-[50vw] h-[100vw] md:h-[50vw] bg-sesgelio/15 md:bg-sesgelio/10 rounded-full blur-[100px] md:blur-[150px] animate-pulse"></div>
               <div className="absolute bottom-[-10%] left-[-10%] w-[120vw] md:w-[50vw] h-[120vw] md:h-[50vw] bg-blue-600/10 md:bg-blue-600/10 rounded-full blur-[120px] md:blur-[150px] animate-pulse" style={{ animationDelay: '3s' }}></div>
            </div>

            {/* --- 🎬 BÖLÜM 1: HERO SECTION (Kusursuz Mobil Uyum) --- */}
            <section className="relative z-10 w-full h-auto md:min-h-[90vh] block md:flex md:items-center justify-center px-4 md:px-6 pt-36 pb-24 md:pb-32">
               <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
                 
                 {/* SOL TARAF: Psikolojik Kanca ve Butonlar */}
                 <div className="lg:col-span-7 flex flex-col items-center md:items-start text-center md:text-left animate-fade-in-up mt-8 md:mt-0">
                    <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-4 md:px-6 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mb-6 md:mb-8 shadow-2xl backdrop-blur-md">
                       <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-green-500 animate-pulse"></div> NÖRAL AĞ AKTİF
                    </div>
                    
                    <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-[1] md:leading-[0.85] tracking-tighter uppercase italic mb-4 md:mb-8 drop-shadow-2xl">
                       SAHTE BAĞLARI <br className="hidden sm:block"/> 
                       <span className="text-transparent bg-clip-text bg-gradient-to-r from-sesgelio to-rose-900">KOPAR.</span>
                    </h1>
                    
                    <p className="text-sm sm:text-lg md:text-xl text-gray-400 font-medium max-w-2xl leading-relaxed mb-8 md:mb-12 px-2 md:px-0">
                      Milyonlarca insanın olduğu bu dijital çağda, <strong className="text-white">neden hala kendi sesini duyan kimse yokmuş gibi</strong> hissediyorsun? Yazılı mesajların ardındaki o soğuk duvarları yık. Sesinin titremesini, gözlerindeki ifadeyi ve anın gerçekliğini karşıya geçir.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 md:gap-6 w-full sm:w-auto px-4 md:px-0">
                       <button 
                         onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}
                         className="group relative bg-white text-black px-8 sm:px-10 py-4 sm:py-5 rounded-full font-black text-base sm:text-lg flex items-center justify-center gap-3 sm:gap-4 transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(255,255,255,0.2)] md:shadow-[0_0_40px_rgba(255,255,255,0.2)] overflow-hidden w-full sm:w-auto"
                       >
                          <div className="absolute inset-0 bg-sesgelio opacity-0 group-hover:opacity-10 transition-opacity"></div>
                          GÖLGELERDEN ÇIK <ArrowRight size={20} className="sm:w-6 sm:h-6 group-hover:translate-x-2 transition-transform" />
                       </button>
                       <span className="text-[10px] sm:text-xs font-bold text-gray-600 uppercase tracking-widest hidden sm:block">veya</span>
                       <button 
                         onClick={() => { document.getElementById('discover-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                         className="text-gray-400 hover:text-white transition-colors font-bold text-xs sm:text-sm uppercase tracking-[0.1em] sm:tracking-[0.2em] flex items-center justify-center gap-2 w-full sm:w-auto py-3 sm:py-0"
                       >
                          Gerçeği Keşfet <ChevronDown size={16} className="sm:w-[18px] sm:h-[18px] animate-bounce" />
                       </button>
                    </div>
                 </div>

                {/* SAĞ TARAF: Canlı Etkinlik Görsel Kartı (Artık Mobilde de Görünür) */}
<div className="lg:col-span-5 relative w-full animate-fade-in block mt-12 lg:mt-0 mb-16 lg:mb-0">
   <div className="absolute inset-0 bg-sesgelio/20 blur-[80px] rounded-full animate-pulse"></div>
   
<div className="bg-[#0a0a0c]/80 border border-white/10 rounded-[2rem] lg:rounded-[2.5rem] p-1.5 sm:p-2 relative overflow-hidden backdrop-blur-xl group hover:border-sesgelio/50 transition-all duration-700 shadow-2xl min-h-[350px] sm:min-h-[400px] lg:min-h-[450px] flex flex-col">
   <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0c]/40 to-[#0a0a0c] z-10 pointer-events-none"></div>
   
   <img 
      src="https://images.unsplash.com/photo-1614728263952-84ea256f9679?q=80&w=2000&auto=format&fit=crop" 
      className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-105 group-hover:opacity-70 transition-all duration-1000" 
      alt="Server Activity" 
   />
   
   <div className="relative z-20 h-full flex flex-col justify-end p-5 sm:p-8 mt-auto">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
      <span className="bg-sesgelio text-white text-[10px] font-black px-3 py-1.5 rounded-full animate-pulse shadow-[0_0_15px_rgba(255,43,94,0.6)]">CANLI YAYIN</span>
      <span className="bg-black/60 backdrop-blur-md border border-white/10 text-white text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
         <Users size={12} className="text-sesgelio" /> 14,582 OP.
      </span>
      </div>
      <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-2 leading-tight uppercase italic tracking-tighter drop-shadow-lg">
         Sesgelio Ana Üs <br/> <span className="text-sesgelio">Ağ Bağlantısı Aktif</span>
      </h3>
      <p className="text-gray-300 text-xs sm:text-sm mb-4 sm:mb-6 font-medium drop-shadow-md">Nöral sohbet odalarına katıl ve binlerce kişiyle anında etkileşime geç.</p>
      
      <div className="flex items-center justify-between mt-auto">
         <div className="flex -space-x-2 sm:-space-x-3">
         {[1,2,3,4].map(i => (
            <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-[#0a0a0c] overflow-hidden relative z-10 shadow-lg">
               <img src={`https://picsum.photos/seed/${i + 200}/100/100`} className="w-full h-full object-cover" alt="User avatar" />
            </div>
         ))}
         <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-[#0a0a0c] bg-white/10 backdrop-blur-md flex items-center justify-center text-[10px] sm:text-xs font-bold text-white relative z-0">
            +2k
         </div>
         </div>
         <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sesgelio flex items-center justify-center text-white shadow-[0_0_30px_rgba(255,43,94,0.6)] group-hover:scale-110 transition-transform cursor-pointer shrink-0">
         <Play size={16} fill="currentColor" className="ml-1 sm:w-5 sm:h-5" />
         </div>
      </div>
   </div>
</div>
                 </div>
               </div>
            </section>
            
           {/* --- 🏰 BÖLÜM 2: SUNUCU VE CANLI YAYIN (Topluluk Gücü) --- */}
            <section id="discover-section" className="relative z-10 w-full py-32 px-6">
               <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">  
                  <div className="order-2 lg:order-1 relative">
                     <div className="absolute inset-0 bg-sesgelio/20 blur-[100px] rounded-full animate-pulse"></div>
                     <div className="relative bg-[#0a0a0c] border border-white/10 rounded-[3rem] p-8 md:p-12 shadow-2xl overflow-hidden group">
                        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
                        <div className="relative z-10 space-y-6">
                           <div className="w-16 h-16 bg-sesgelio/10 rounded-2xl flex items-center justify-center text-sesgelio mb-8 border border-sesgelio/20 shadow-lg">
                              <Radio size={32} />
                           </div>
                           <h3 className="text-3xl font-black text-white uppercase italic">Hükümdarlığını Kur</h3>
                           <p className="text-gray-400 leading-relaxed font-medium">
                              Burası sadece bir grup veya kanal değil. Burası senin canlı ekosistemin. İster dev bir kitleye hitap et, ister sadık takipçilerinle anı yaşa.
                           </p>
                         <ul className="space-y-4 pt-4 border-t border-white/10">
                              <li className="flex items-start gap-3 text-gray-300 font-bold">
                                 <CheckCircle size={18} className="text-sesgelio shrink-0 mt-0.5"/> 
                                 <span className="leading-relaxed">Saniyeler içinde ekranını binlerce kişiye yansıt.</span>
                              </li>
                              <li className="flex items-start gap-3 text-gray-300 font-bold">
                                 <CheckCircle size={18} className="text-sesgelio shrink-0 mt-0.5"/> 
                                 <span className="leading-relaxed">0 Gecikmeyle canlı sesli odalara hükmet.</span>
                              </li>
                              <li className="flex items-start gap-3 text-gray-300 font-bold">
                                 <CheckCircle size={18} className="text-sesgelio shrink-0 mt-1"/> 
                                 <span className="leading-relaxed">
                                    Olay anında, yazışarak değil <span className="text-white bg-white/10 px-2 py-0.5 rounded mx-1 inline-block">yaşayarak</span> koordine ol.
                                 </span>
                              </li>
                           </ul>
                        </div>
                     </div>
                  </div>
                  <div className="order-1 lg:order-2 space-y-8 lg:pl-12">
                     <div className="inline-flex items-center gap-2 text-sesgelio font-black text-sm uppercase tracking-widest">
                        <Monitor size={18} /> Toplumsal Rezonans
                     </div>
                     <h2 className="text-4xl md:text-6xl font-black text-white leading-none tracking-tighter uppercase italic">
                        KENDİ <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-sesgelio to-purple-500">EVRENİNİ YARAT.</span>
                     </h2>
                     <p className="text-gray-400 text-lg leading-relaxed">
                        Dışarıdaki platformlar seni kalabalıkların içinde bir seyirci yapar. Sesgelio'da ise sahne senin. Sunucunu kur, kurallarını belirle ve sadece yazan değil, aynı nefesi alan bir topluluk inşa et.
                     </p>
                  </div>
               </div>
            </section>

            {/* --- 👁️ BÖLÜM 3: ÖZEL GRUPLAR VE DM (İzolasyon ve Mahremiyet) --- */}
            <section className="relative z-10 w-full py-32 px-6 bg-gradient-to-b from-transparent via-white/5 to-transparent">
               <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div className="space-y-8 pr-0 lg:pr-12">
                     <div className="inline-flex items-center gap-2 text-blue-500 font-black text-sm uppercase tracking-widest">
                        <Fingerprint size={18} /> Mutlak Mahremiyet
                     </div>
                     <h2 className="text-4xl md:text-6xl font-black text-white leading-none tracking-tighter uppercase italic">
                        GÖZLERİNİN İÇİNE <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">BAKARAK KONUŞ.</span>
                     </h2>
                     <p className="text-gray-400 text-lg leading-relaxed">
                        Kalabalıklardan yoruldun mu? O zaman kendi kozana çekil. Sunuculardan tamamen bağımsız, sadece senin ve seçtiğin kişilerin olduğu izole odalar. Fısıltıların, gülüşlerin ve sırların güvende.
                     </p>
                  </div>
                  <div className="relative">
                     <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full animate-pulse"></div>
                     <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-[#0a0a0c] border border-white/10 p-8 rounded-[2.5rem] shadow-xl hover:-translate-y-2 transition-transform group">
                           <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 mb-6"><Video size={24} /></div>
                           <h4 className="text-xl font-bold text-white mb-2">Birebir Kamerada</h4>
                           <p className="text-sm text-gray-500">Mesafe ne olursa olsun, karşındakinin mimiklerini HD kalitede hisset. DM üzerinden tek tıkla yüz yüze geç.</p>
                        </div>
                        <div className="bg-[#0a0a0c] border border-white/10 p-8 rounded-[2.5rem] shadow-xl hover:-translate-y-2 transition-transform group md:translate-y-8">
                           <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 mb-6"><Users size={24} /></div>
                           <h4 className="text-xl font-bold text-white mb-2">Gizli Gruplar</h4>
                           <p className="text-sm text-gray-500">Sunucu açmana gerek yok. Kendi sadık dostlarınla özel bir grup kur, aranızda anında ekran paylaş, mesajlaş.</p>
                        </div>
                     </div>
                  </div>
               </div>
            </section>

            {/* --- 🧠 BÖLÜM 4: AI VE MÜZİK (Sistemin Ruhu) --- */}
            <section className="relative z-10 w-full py-32 px-6">
               <div className="max-w-5xl mx-auto text-center space-y-8 mb-20">
                  <h2 className="text-4xl md:text-6xl font-black text-white leading-none tracking-tighter uppercase italic">
                     ASLA YALNIZ <span className="text-purple-500">DEĞİLSİN.</span>
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
                     Sesgelio sadece insanları bağlamaz; dijital varlıkları da yanına getirir. İster zihnini okuyan bir yapay zeka, ister odanın ruhunu belirleyen bir müzik algoritması olsun. Burada odan da yaşıyor.
                  </p>
               </div>

               <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-[#0a0a0c] border border-purple-500/30 rounded-[3rem] p-10 md:p-16 relative overflow-hidden group">
                     <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/20 blur-[80px] group-hover:bg-purple-600/40 transition-colors duration-700"></div>
                     <div className="relative z-10 flex flex-col items-center text-center">
<div className="w-20 h-20 bg-purple-500/20 border border-purple-500/50 rounded-full flex items-center justify-center text-purple-400 mb-8 shadow-[0_0_30px_rgba(168,85,247,0.4)] overflow-hidden">
   <img 
      src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=400&auto=format&fit=crop" 
      alt="Sesgelio AI" 
      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" 
   />
</div>
                        <h3 className="text-3xl font-black text-white mb-4">Sesgelio AI</h3>
                        <p className="text-gray-400 font-medium leading-relaxed mb-8">
                           Sunucundaki nöral dostun. Sorduğun her soruya anında cevap verir, seninle muhabbet eder ve koca bir internet kütüphanesini odana taşır.
                        </p>
                        <div className="text-[10px] font-black uppercase tracking-widest text-purple-400 bg-purple-500/10 px-4 py-2 rounded-full border border-purple-500/20">
                           Powered by Gemini 
                        </div>
                     </div>
                  </div>

                  <div className="bg-[#0a0a0c] border border-blue-500/30 rounded-[3rem] p-10 md:p-16 relative overflow-hidden group">
                     <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/20 blur-[80px] group-hover:bg-blue-600/40 transition-colors duration-700"></div>
                     <div className="relative z-10 flex flex-col items-center text-center">
<div className="w-20 h-20 bg-blue-500/20 border border-blue-500/50 rounded-full flex items-center justify-center text-blue-400 mb-8 shadow-[0_0_30px_rgba(59,130,246,0.4)] overflow-hidden">
   <img 
      src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format&fit=crop" 
      alt="Kesintisiz Ritim" 
      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" 
   />
</div>
                        <h3 className="text-3xl font-black text-white mb-4">Sesgelio AudioLink</h3>
                        <p className="text-gray-400 font-medium leading-relaxed mb-8">
                           Sen ve arkadaşların konuşurken arka planda çalan kusursuz stüdyo kalitesinde müzik. Müziği yönet, DJ sen ol, frekansı hisset.
                        </p>
                        <div className="text-[10px] font-black uppercase tracking-widest text-blue-400 bg-blue-500/10 px-4 py-2 rounded-full border border-blue-500/20">
                          Kayıpsız İletim Protokolü
                        </div>
                     </div>
                  </div>
               </div>

               {/* --- 🚀 YENİ EKLENEN UYGULAMA DİZİNİ BUTONU --- */}
               <div className="mt-16 flex justify-center w-full relative z-10 animate-fade-in-up">
                  <button 
                    onClick={() => { setCurrentPage('directory'); scrollContainerRef.current?.scrollTo(0,0); }}
                    className="group relative px-10 py-5 rounded-[2rem] bg-indigo-500/10 text-indigo-400 hover:text-white border border-indigo-500/30 font-black uppercase tracking-[0.1em] transition-all duration-300 shadow-[0_0_30px_rgba(99,102,241,0.15)] hover:shadow-[0_0_50px_rgba(99,102,241,0.4)] active:scale-95 flex items-center justify-center gap-4 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <Layers className="relative z-10" size={24} />
                    <span className="relative z-10">Uygulama Dizinini Keşfet</span>
                    <ArrowRight className="relative z-10 group-hover:translate-x-2 transition-transform duration-300" size={20} />
                  </button>
               </div>
               
            </section>

            {/* --- 🚀 BÖLÜM 5: FİNAL (Eylem Çağrısı) --- */}
            <section className="relative z-10 w-full py-40 px-6 border-t border-white/5">
               <div className="absolute inset-0 bg-gradient-to-t from-sesgelio/5 to-transparent pointer-events-none"></div>
               <div className="max-w-4xl mx-auto text-center space-y-12 relative z-10">
                  <div className="w-24 h-24 bg-white/5 border border-white/10 rounded-[2rem] flex items-center justify-center text-white mb-6 mx-auto transform rotate-3 shadow-2xl">
                      <Zap size={48} />
                  </div>
                  <h2 className="text-5xl md:text-8xl font-black text-white uppercase italic tracking-tighter leading-[0.85]">
                     TARİHE TANIK OLMA,<br/> <span className="text-sesgelio">TARİHİ YAZ.</span>
                  </h2>
                  <p className="text-xl text-gray-400 font-medium max-w-2xl mx-auto">
                     Sıradan platformlarda bir isimden ibaretsin. Sesgelio'da ise bir kimliksin. Varlığını hissettirmeye hazır mısın?
                  </p>
               <button 
                    onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); }}
                    className="inline-flex items-center justify-center gap-3 md:gap-4 bg-sesgelio hover:bg-sesgelio-dark text-white px-6 py-4 md:px-12 md:py-6 rounded-full font-black text-lg md:text-2xl transition-all transform hover:scale-105 active:scale-95 shadow-[0_20px_60px_-10px_rgba(255,43,94,0.6)] w-full sm:w-auto"
                  >
                     SİSTEME GİRİŞ YAP <LogOut size={20} className="md:w-6 md:h-6 rotate-180" />
                  </button>
               </div>
            </section>

            <Footer />
          </div>
        );
    }
  };

 return (
    <div className="h-screen w-full bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white font-sans selection:bg-sesgelio selection:text-white overflow-hidden relative flex flex-col transition-colors duration-300">
    {/* 🚀 SİBER MÜHÜR: macOS KURULUM BİLGİLENDİRME MODALI */}
      {showMacWarning && (
        <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowMacWarning(false)}>
          <div className="bg-[#0a0a0c] border border-white/10 rounded-[2.5rem] p-8 max-w-md w-full shadow-[0_0_60px_rgba(0,0,0,0.8)] relative animate-scale-in" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowMacWarning(false)} className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors bg-white/5 p-2 rounded-full">
              <X size={18} />
            </button>
            
            <div className="w-16 h-16 bg-gray-800/50 border border-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
              <img src="https://cdn.simpleicons.org/apple/ffffff" className="w-8 h-8 opacity-80" alt="Apple" />
            </div>
            
            <h3 className="text-2xl font-black text-white uppercase tracking-tighter text-center mb-4 italic">macOS Kurulum Uyarısı</h3>
            
            <div className="bg-gray-900/50 border border-white/5 p-5 rounded-2xl text-sm text-gray-300 leading-relaxed mb-8 shadow-inner">
              <p className="mb-4 text-xs">
                Apple güvenlik politikaları (Gatekeeper) gereği, App Store dışından indirilen uygulamalarda <strong className="text-red-400">"Geliştirici doğrulanamadı"</strong> uyarısı alabilirsiniz. Bu durum tamamen normaldir.
              </p>
              <div className="h-px w-full bg-white/10 mb-4"></div>
              <p className="font-bold text-white mb-3 text-xs uppercase tracking-widest text-sesgelio">Çözümü çok basittir:</p>
              <ol className="list-decimal pl-4 space-y-2 text-gray-400 text-xs font-medium">
                <li>İndirdiğiniz <strong className="text-gray-200">Sesgelio</strong> uygulamasına <strong>Sağ Tıklayın</strong> (veya Control tuşuna basılı tutarak tıklayın).</li>
                <li>Açılan menüden <strong>"Aç"</strong> seçeneğine tıklayın.</li>
                <li>Ekrana gelen uyarıdaki <strong className="text-white bg-white/10 px-2 py-0.5 rounded">Aç</strong> butonuna basın.</li>
              </ol>
              <p className="mt-4 text-[10px] text-gray-500 italic text-center uppercase tracking-widest">Bunu sadece ilk açılışta yapmanız yeterlidir.</p>
            </div>
            
            <button onClick={() => setShowMacWarning(false)} className="w-full bg-white text-black font-black text-sm py-4 rounded-xl hover:bg-gray-200 active:scale-95 transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)]">
              ANLADIM, TEŞEKKÜRLER
            </button>
          </div>
        </div>
      )}
    
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 transform-gpu">
         <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-sesgelio/10 dark:bg-sesgelio/20 rounded-full blur-[100px] animate-blob mix-blend-multiply dark:mix-blend-screen opacity-60 transform-gpu will-change-transform"></div>
         <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600/10 dark:bg-purple-600/20 rounded-full blur-[120px] animate-blob-reverse mix-blend-multiply dark:mix-blend-screen opacity-50 transform-gpu will-change-transform"></div>
         <div className="absolute top-[40%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 dark:bg-blue-900/10 rounded-full blur-[150px] animate-pulse-slow transform-gpu will-change-[transform,opacity]"></div>
         <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 dark:opacity-20 brightness-100 contrast-150"></div>
      </div>
      
{/* TOP NAVIGATION BAR (Z-Index Zırhlı & Elit Cam Tasarım) */}
      <nav className="fixed top-0 left-0 w-full h-16 md:h-20 flex items-center justify-between px-4 lg:px-12 z-[999] backdrop-blur-2xl bg-white/40 dark:bg-[#030305]/40 transition-colors border-b border-white/20 dark:border-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
         {/* GÖLGE VE IŞIK EFEKTİ */}
         <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent dark:from-white/5 dark:to-transparent pointer-events-none"></div>

{/* SOL: LOGO */}
         <div 
           className="flex items-center cursor-pointer z-[100] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
           onClick={() => { setCurrentPage('home'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }}
         >
            <img 
               src="/ana1_sayfa_logo.png" 
               alt="Sesgelio Logo" 
               // 🚀 SİBER MÜHÜR: scale-[2.2] ile görünmez boşlukları eziyor, origin-left ile sağa doğru büyütüyoruz!
               className="h-12 md:h-16 lg:h-20 w-auto object-contain scale-[1.8] md:scale-[2] lg:scale-[2.2] origin-left drop-shadow-[0_0_15px_rgba(255,43,94,0.4)]" 
            />
         </div>
         
         {/* ORTA: MASAÜSTÜ MENÜ LİNKLERİ */}
         <div className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2 z-[100]">
            <button onClick={() => { setCurrentPage('home'); scrollContainerRef.current?.scrollTo(0,0); }} className={`relative py-2 font-black text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 ${currentPage === 'home' ? 'text-sesgelio drop-shadow-[0_0_10px_rgba(255,43,94,0.6)]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
              Ana Sayfa
              {currentPage === 'home' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-sesgelio rounded-t-full shadow-[0_0_8px_rgba(255,43,94,0.8)]"></div>}
            </button>
            <button onClick={() => { setCurrentPage('about'); scrollContainerRef.current?.scrollTo(0,0); }} className={`relative py-2 font-black text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 ${currentPage === 'about' ? 'text-sesgelio drop-shadow-[0_0_10px_rgba(255,43,94,0.6)]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
              Hakkımızda
              {currentPage === 'about' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-sesgelio rounded-t-full shadow-[0_0_8px_rgba(255,43,94,0.8)]"></div>}
            </button>
            <button onClick={() => { setCurrentPage('nitro'); scrollContainerRef.current?.scrollTo(0,0); }} className={`relative py-2 font-black text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 ${currentPage === 'nitro' ? 'text-sesgelio drop-shadow-[0_0_10px_rgba(255,43,94,0.6)]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
              Hizmetlerimiz
              {currentPage === 'nitro' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-sesgelio rounded-t-full shadow-[0_0_8px_rgba(255,43,94,0.8)]"></div>}
            </button>
            <button onClick={() => { setCurrentPage('contact'); scrollContainerRef.current?.scrollTo(0,0); }} className={`relative py-2 font-black text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 ${currentPage === 'contact' ? 'text-sesgelio drop-shadow-[0_0_10px_rgba(255,43,94,0.6)]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
              İletişim
              {currentPage === 'contact' && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-sesgelio rounded-t-full shadow-[0_0_8px_rgba(255,43,94,0.8)]"></div>}
            </button>
         </div>

         {/* SAĞ: GİRİŞ / KAYIT BUTONLARI VE MOBİL MENÜ */}
         <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 z-[100]">
{/* ── İNDİR BUTONU (MASAÜSTÜ) ── */}
<div 
  className="relative hidden lg:block" 
  id="download-area" 
  style={{ 
    display: (
      navigator.userAgent.includes('SesgelioApp') || 
      window.matchMedia('(display-mode: standalone)').matches || 
      window.matchMedia('(display-mode: window-controls-overlay)').matches ||
      navigator.userAgent.includes('WebView') || 
      navigator.userAgent.includes('MSAppHost')
    ) ? 'none' : '' 
  }}
>
  <button
    onClick={() => setDownloadMenuOpen(!downloadMenuOpen)}
    className="flex items-center gap-1.5 bg-sesgelio/10 hover:bg-sesgelio/20 border border-sesgelio/30 hover:border-sesgelio/60 text-sesgelio px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
  >
    <Download size={13} />
    İndir
    <ChevronDown size={12} className={`transition-transform duration-200 ${downloadMenuOpen ? 'rotate-180' : ''}`} />
  </button>

{downloadMenuOpen && (
    <div className="absolute top-full right-0 mt-4 w-72 bg-[#0a0a0c]/95 backdrop-blur-3xl border border-gray-800 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] z-[9999] animate-fade-in-down origin-top-right">
      
      {/* Üst Kısım / Başlık */}
      <div className="px-4 py-3 border-b border-white/5 bg-gradient-to-r from-white/5 to-transparent rounded-t-2xl">
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
          <Monitor size={12} className="text-sesgelio" /> Masaüstü Uygulama
        </p>
      </div>

      <div className="p-2 flex flex-col gap-1">
        
        {/* ================= WINDOWS ================= */}
       <a href="/downloads/Sesgelio-Setup-1.0.0.exe" download className="group relative flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-pointer">
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          
          <div className="relative z-10 w-10 h-10 bg-[#111214] border border-blue-500/20 rounded-xl flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(59,130,246,0.15)] group-hover:scale-110 group-hover:border-blue-500/50 group-hover:shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all duration-300 text-blue-400">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 group-hover:brightness-125 transition-all">
              <path d="M0 0h11.4v11.4H0zm12.6 0H24v11.4H12.6zM0 12.6h11.4V24H0zm12.6 0H24V24H12.6z"/>
            </svg>
          </div>
          
          <div className="flex flex-col relative z-10 flex-1">
            <div className="flex items-center gap-1.5 relative group/tooltip">
              <span className="text-white text-sm font-bold group-hover:text-blue-400 transition-colors duration-300">Windows</span>
              
              {/* BİLGİ İKONU VE HOVER TOOLTIP (WINDOWS) */}
              <div 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="text-gray-400 hover:text-white transition-colors p-1 bg-white/5 hover:bg-white/10 rounded-md cursor-help"
              >
                <Info size={13} />
              </div>

              {/* Tooltip Penceresi */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-64 p-4 bg-[#0a0a0c]/95 backdrop-blur-3xl border border-gray-700 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300 z-[99999] pointer-events-none transform translate-x-2 group-hover/tooltip:translate-x-0">
                <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-[#0a0a0c] border-r border-t border-gray-700 transform rotate-45"></div>
                <h4 className="font-black text-white text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                  <ShieldAlert size={12} className="text-blue-400" /> Kurulum & Yetki
                </h4>
                <ol className="list-decimal pl-3 space-y-2 text-[9px] text-gray-300 font-medium">
                  <li>Mavi uyarı ekranı çıkarsa: <strong className="text-white">Ek Bilgi &gt; Yine de Çalıştır</strong>'a tıklayın.</li>
                  <li>Oyun içi bas-konuşun çalışması için açılışta <strong className="text-white bg-blue-500/20 px-1 py-0.5 rounded text-blue-400">Yönetici İzni</strong> verin.</li>
                </ol>
              </div>
            </div>
            <span className="text-gray-500 text-[10px] font-medium">Setup 1.0.0 · 64-bit</span>
          </div>
          
          <span className="relative z-10 text-[10px] font-black text-white bg-blue-600 hover:bg-blue-500 px-4 py-1.5 rounded-lg uppercase tracking-wider shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all active:scale-95">
            İNDİR
          </span>
        </a>

        {/* ================= MACOS ================= */}
        <a href="/downloads/Sesgelio-1.0.0-arm64.dmg" download className="group relative flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-all cursor-pointer mt-1">
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-400/0 via-gray-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          
          <div className="relative z-10 w-10 h-10 bg-[#111214] border border-gray-500/20 rounded-xl flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(156,163,175,0.15)] group-hover:scale-110 group-hover:border-gray-500/50 group-hover:shadow-[0_0_20px_rgba(156,163,175,0.3)] transition-all duration-300">
            <img src="https://cdn.simpleicons.org/apple/9ca3af" alt="macOS" className="w-5 h-5 group-hover:brightness-125 transition-all" />
          </div>
          
          <div className="flex flex-col relative z-10 flex-1">
            <div className="flex items-center gap-1.5 relative group/tooltip">
              <span className="text-white text-sm font-bold group-hover:text-gray-300 transition-colors duration-300">macOS</span>
              
              {/* BİLGİ İKONU VE HOVER TOOLTIP (MACOS) */}
              <div 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="text-gray-400 hover:text-white transition-colors p-1 bg-white/5 hover:bg-white/10 rounded-md cursor-help"
              >
                <Info size={13} />
              </div>

              {/* Tooltip Penceresi */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-64 p-4 bg-[#0a0a0c]/95 backdrop-blur-3xl border border-gray-700 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300 z-[99999] pointer-events-none transform translate-x-2 group-hover/tooltip:translate-x-0">
                <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-[#0a0a0c] border-r border-t border-gray-700 transform rotate-45"></div>
                <h4 className="font-black text-white text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                  <ShieldAlert size={12} className="text-gray-300" /> Güvenlik Uyarısı
                </h4>
                <p className="text-[9px] text-gray-400 mb-2 leading-relaxed font-medium">
                  "Kimliği belirsiz geliştirici" uyarısı alırsanız:
                </p>
                <ol className="list-decimal pl-3 space-y-1.5 text-[9px] text-gray-300 font-medium">
                  <li>İndirdiğiniz dosyaya <strong className="text-white">Sağ Tıklayın</strong></li>
                  <li>Menüden <strong className="text-white">"Aç"</strong> seçeneğine tıklayın</li>
                  <li>Çıkan ekranda <strong className="text-white bg-white/10 px-1 py-0.5 rounded">Aç</strong> butonuna basın.</li>
                </ol>
              </div>
            </div>
            <span className="text-gray-500 text-[10px] font-medium">Apple Silicon (M1/M2/M3)</span>
          </div>
          
          <span className="relative z-10 text-[10px] font-black text-white bg-gray-700 hover:bg-gray-600 px-4 py-1.5 rounded-lg uppercase tracking-wider shadow-[0_0_15px_rgba(75,85,99,0.4)] transition-all active:scale-95">
            İNDİR
          </span>
        </a>

        {/* ================= LINUX ================= */}
        <a href="/downloads/Sesgelio-1.0.0.AppImage" download className="group relative flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-all cursor-pointer mt-1">
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          
          <div className="relative z-10 w-10 h-10 bg-[#111214] border border-orange-500/20 rounded-xl flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(249,115,22,0.15)] group-hover:scale-110 group-hover:border-orange-500/50 group-hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] transition-all duration-300">
            <img src="https://cdn.simpleicons.org/ubuntu/f97316" alt="Linux" className="w-5 h-5 group-hover:brightness-125 transition-all" />
          </div>
          
          <div className="flex flex-col relative z-10 flex-1">
            <div className="flex items-center gap-1.5 relative group/tooltip">
              <span className="text-white text-sm font-bold group-hover:text-orange-400 transition-colors duration-300">Linux</span>
              
              {/* BİLGİ İKONU VE HOVER TOOLTIP (LINUX) */}
              <div 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="text-gray-400 hover:text-white transition-colors p-1 bg-white/5 hover:bg-white/10 rounded-md cursor-help"
              >
                <Info size={13} />
              </div>

              {/* Tooltip Penceresi */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-64 p-4 bg-[#0a0a0c]/95 backdrop-blur-3xl border border-gray-700 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-300 z-[99999] pointer-events-none transform translate-x-2 group-hover/tooltip:translate-x-0">
                <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-[#0a0a0c] border-r border-t border-gray-700 transform rotate-45"></div>
                <h4 className="font-black text-white text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                  <ShieldAlert size={12} className="text-orange-400" /> Çalıştırma İzni
                </h4>
                <p className="text-[9px] text-gray-400 mb-2 leading-relaxed font-medium">
                  AppImage dosyasını açabilmek için:
                </p>
                <ol className="list-decimal pl-3 space-y-1.5 text-[9px] text-gray-300 font-medium">
                  <li>Dosyaya <strong className="text-white">Sağ Tıklayın</strong></li>
                  <li><strong className="text-white">Özellikler &gt; İzinler</strong> sekmesine girin</li>
                  <li><strong className="text-white bg-orange-500/20 text-orange-400 px-1 py-0.5 rounded">Program gibi çalışmasına izin ver</strong>'i işaretleyin.</li>
                </ol>
              </div>
            </div>
            <span className="text-gray-500 text-[10px] font-medium">AppImage · x64</span>
          </div>
          
          <span className="relative z-10 text-[10px] font-black text-white bg-orange-600 hover:bg-orange-500 px-4 py-1.5 rounded-lg uppercase tracking-wider shadow-[0_0_15px_rgba(234,88,12,0.4)] transition-all active:scale-95">
            İNDİR
          </span>
        </a>
      </div>

      {/* Alt Bilgi / Güvenlik Mührü */}
      <div className="bg-black/60 px-4 py-2.5 border-t border-white/5 rounded-b-2xl">
        <p className="text-center text-[10px] text-gray-500 font-bold flex items-center justify-center gap-1.5 uppercase tracking-widest">
          <ShieldCheck size={12} className="text-emerald-500" />
          Güvenli İndirme · v1.0.0
        </p>
      </div>
      
    </div>
)}
</div>
             <button onClick={() => { setCurrentPage('login'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className={`text-xs lg:text-sm font-black uppercase tracking-widest transition-all duration-300 px-3 sm:px-4 py-2 rounded-xl border backdrop-blur-md ${currentPage === 'login' ? 'text-sesgelio bg-sesgelio/10 border-sesgelio/30 shadow-[0_0_15px_rgba(255,43,94,0.2)]' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 border-transparent hover:border-gray-200 dark:hover:border-white/10'}`}>Giriş</button>
             <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className="bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-200 text-white dark:text-black px-4 sm:px-5 py-2.5 rounded-xl text-xs lg:text-sm font-black uppercase tracking-widest transition-all shadow-[0_4px_15px_rgba(0,0,0,0.2)] dark:shadow-[0_0_15px_rgba(255,255,255,0.3)] active:scale-95 hover:-translate-y-0.5 hidden sm:block">Kayıt Ol</button>
             
             {/* MOBİL: HAMBURGER MENÜ BUTONU (SAĞA TAŞINDI) */}
             <div className="flex items-center lg:hidden ml-1">
               <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-600 dark:text-gray-300 transition-all duration-300 active:scale-95 bg-white/50 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 rounded-xl border border-gray-200 dark:border-white/10 shadow-sm backdrop-blur-md">
                 {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
               </button>
             </div>
         </div>
      </nav>

      {/* 🚀 SİBER MÜHÜR: MOBİL AÇILIR MENÜ (Z-Index yükseltildi ve lg:hidden eklendi) */}
      {mobileMenuOpen && (
         <div className="lg:hidden absolute top-16 md:top-20 left-0 w-full bg-white/95 dark:bg-[#0a0a0c]/95 backdrop-blur-2xl border-b border-gray-200 dark:border-white/10 shadow-2xl z-[90] flex flex-col p-4 animate-fade-in-down gap-2">
            <button onClick={() => { setCurrentPage('home'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className={`p-4 text-center font-black uppercase tracking-wider rounded-xl transition-colors ${currentPage === 'home' ? 'text-sesgelio bg-sesgelio/10 border border-sesgelio/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'}`}>Ana Sayfa</button>
            <button onClick={() => { setCurrentPage('about'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className={`p-4 text-center font-black uppercase tracking-wider rounded-xl transition-colors ${currentPage === 'about' ? 'text-sesgelio bg-sesgelio/10 border border-sesgelio/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'}`}>Hakkımızda</button>
            <button onClick={() => { setCurrentPage('nitro'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className={`p-4 text-center font-black uppercase tracking-wider rounded-xl transition-colors ${currentPage === 'nitro' ? 'text-sesgelio bg-sesgelio/10 border border-sesgelio/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'}`}>Hizmetlerimiz</button>
            <button onClick={() => { setCurrentPage('contact'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className={`p-4 text-center font-black uppercase tracking-wider rounded-xl transition-colors ${currentPage === 'contact' ? 'text-sesgelio bg-sesgelio/10 border border-sesgelio/20' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'}`}>İletişim</button>
            <div className="w-full h-px bg-gray-200 dark:bg-white/10 my-2"></div>
            <button onClick={() => { setCurrentPage('register'); scrollContainerRef.current?.scrollTo(0,0); setMobileMenuOpen(false); }} className="p-4 text-center font-black uppercase tracking-wider rounded-xl bg-sesgelio text-white shadow-lg shadow-sesgelio/30 active:scale-95 transition-transform">Sisteme Katıl</button>
         </div>
      )}

      {/* 🚀 SESGELİO ÖZEL SİBER BİLDİRİM (TOAST) EKRANI - ŞİMDİ EN ÜST KATMASINDA! */}
      {siberToast && siberToast.show && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[999999] animate-fade-in-down pointer-events-none">
              <div className={`flex items-center gap-4 px-6 py-4 rounded-2xl shadow-2xl border backdrop-blur-xl transition-all ${
                  siberToast.type === 'success' 
                  ? 'bg-[#0a0a0c]/95 border-green-500/50 shadow-[0_10px_50px_rgba(34,197,94,0.25)]' 
                  : siberToast.type === 'error'
                  ? 'bg-[#0a0a0c]/95 border-[#ff2b5e]/50 shadow-[0_10px_50px_rgba(255,43,94,0.25)]'
                  : 'bg-[#0a0a0c]/95 border-yellow-500/50 shadow-[0_10px_50px_rgba(234,179,8,0.25)]'
              }`}>
                  {/* İkon Kısmı */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-inner ${
                      siberToast.type === 'success' 
                      ? 'bg-green-500/10 text-green-500 border border-green-500/20' 
                      : siberToast.type === 'error'
                      ? 'bg-[#ff2b5e]/10 text-[#ff2b5e] border border-[#ff2b5e]/20'
                      : 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                  }`}>
                      {siberToast.type === 'success' ? (
                          <CheckCircle size={24} />
                      ) : siberToast.type === 'error' ? (
                          <AlertTriangle size={24} />
                      ) : (
                          <ShieldAlert size={24} className="animate-pulse" />
                      )}
                  </div>

                  {/* Metin Kısmı */}
                  <div className="flex flex-col">
                      <span className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1 ${
                          siberToast.type === 'success' ? 'text-green-500' : siberToast.type === 'error' ? 'text-[#ff2b5e]' : 'text-yellow-500'
                      }`}>
                          {siberToast.type === 'success' ? 'SİSTEM ONAYI' : siberToast.type === 'error' ? 'SİSTEM UYARISI' : 'BİLGİLENDİRME'}
                      </span>
                      <span className="text-white text-sm font-medium leading-snug max-w-sm">
                          {siberToast.message}
                      </span>
                  </div>
              </div>
          </div>
      )}

     {/* 🚀 SİBER MÜHÜR: key={currentPage} sayesinde sayfa her değiştiğinde bu alan SIFIRDAN inşa edilir ve kaydırma çubuğu KESİN OLARAK en tepeye sıfırlanır! */}
      <main ref={scrollContainerRef} key={currentPage} className="flex-1 overflow-y-auto custom-scrollbar relative z-10 w-full flex flex-col">
         {renderContent()}
      </main>
   {/* 🚀 ÇEREZ ONAY BANNER'I (KVKK & GDPR ZIRHI) */}
        {showCookieBanner && (
          <div className="fixed bottom-0 left-0 w-full z-[999] animate-fade-in-up">
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent pointer-events-none"></div>
              <div className="relative max-w-7xl mx-auto p-4 md:p-6 mb-4">
                  <div className="bg-white/90 dark:bg-[#0a0a0c]/90 backdrop-blur-xl border border-gray-200 dark:border-white/10 p-6 md:p-8 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center gap-6 md:gap-12">
                      
                      <div className="flex items-center gap-6 flex-1">
                          <div className="hidden md:flex w-16 h-16 bg-sesgelio/10 rounded-2xl items-center justify-center text-sesgelio shrink-0 border border-sesgelio/20 shadow-lg transform -rotate-3">
                              <Cookie size={32} />
                          </div>
                          <div>
                              <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase italic tracking-tighter mb-2 flex items-center gap-2">
                                  <ShieldCheck size={18} className="text-green-500" /> GÜVENLİK VE ÇEREZ PROTOKOLÜ
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium leading-relaxed">
                                  Sesgelio; kesintisiz HD ses iletişimi, <strong className="text-gray-900 dark:text-white">Gemini API</strong> bağlantıları ve <strong className="text-gray-900 dark:text-white">Güvenli Ödeme (3D Secure)</strong> altyapısı için zorunlu çerezler kullanır. Platformumuzu kullanarak bu çerezlerin KVKK kapsamında işlenmesini kabul etmiş olursunuz.
                              </p>
                          </div>
                      </div>

                      <div className="flex flex-col sm:flex-row w-full md:w-auto gap-3 shrink-0">
                          <button 
                              onClick={() => { setCurrentPage('cookies'); scrollContainerRef.current?.scrollTo(0,0); }}
                              className="px-6 py-3.5 rounded-xl font-bold text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors uppercase tracking-widest text-center"
                          >
                              İncele
                          </button>
                          <button 
                              onClick={handleAcceptCookies}
                              className="bg-sesgelio hover:bg-sesgelio-dark text-white px-8 py-3.5 rounded-xl font-black text-sm uppercase tracking-widest transition-all transform hover:scale-105 active:scale-95 shadow-lg shadow-sesgelio/30 flex items-center justify-center gap-2"
                          >
                              KABUL EDİYORUM <CheckCircle size={18} />
                          </button>
                      </div>

                  </div>
              </div>
          </div>
        )}

      </div>
    );
  }

  export default Dashboard;